import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ExpandDiv from "../common/ExpandDiv";
import ShareModal from "../common/ShareModal";
import {
  FormInput,
  FormSelect,
  FormCheckbox,
  FormRadioButton,
  FormTextArea,
  FieldTable,
  ToggleSwitch,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleAddFieldTableRow,
  handleEditFieldTableRow,
  handleSaveFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function ALPSD(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(false);
  const [showNarrative, setShowNarrative] = useState("Hide");

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  const preSignForm = () => {
    const obj = { ...data };

    if (obj.RecipientSupportsDetails["@value"]) {
      const recipientSupportsDetails = JSON.parse(
        obj.RecipientSupportsDetails["@value"]
      );

      if (recipientSupportsDetails)
        obj.RecipientSupportsDetails["@value"] = JSON.stringify(
          recipientSupportsDetails.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.FamilyHistoryDetails["@value"]) {
      const familyHistoryDetails = JSON.parse(
        obj.FamilyHistoryDetails["@value"]
      );

      if (familyHistoryDetails)
        obj.FamilyHistoryDetails["@value"] = JSON.stringify(
          familyHistoryDetails.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.CurrentMedicationDetails["@value"]) {
      const currentMedicationDetails = JSON.parse(
        obj.CurrentMedicationDetails["@value"]
      );

      if (currentMedicationDetails)
        obj.CurrentMedicationDetails["@value"] = JSON.stringify(
          currentMedicationDetails.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.MentalHealthHistory["@value"]) {
      const mentalHealthHistory = JSON.parse(obj.MentalHealthHistory["@value"]);

      if (mentalHealthHistory)
        obj.MentalHealthHistory["@value"] = JSON.stringify(
          mentalHealthHistory.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.SubstanceUseHistory["@value"]) {
      const substanceUseHistory = JSON.parse(obj.SubstanceUseHistory["@value"]);

      if (substanceUseHistory)
        obj.SubstanceUseHistory["@value"] = JSON.stringify(
          substanceUseHistory.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.SubstanceUseTreatmentHistory["@value"]) {
      const substanceUseTreatmentHistory = JSON.parse(
        obj.SubstanceUseTreatmentHistory["@value"]
      );

      if (substanceUseTreatmentHistory)
        obj.SubstanceUseTreatmentHistory["@value"] = JSON.stringify(
          substanceUseTreatmentHistory.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.TraumaHistory["@value"]) {
      const traumaHistory = JSON.parse(obj.TraumaHistory["@value"]);

      if (traumaHistory)
        obj.TraumaHistory["@value"] = JSON.stringify(
          traumaHistory.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.DiagnosisDetails["@value"]) {
      const diagnosisDetails = JSON.parse(obj.DiagnosisDetails["@value"]);

      if (diagnosisDetails)
        obj.DiagnosisDetails["@value"] = JSON.stringify(
          diagnosisDetails.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    if (obj.TreatmentRecommendations["@value"]) {
      const treatmentRecommendations = JSON.parse(
        obj.TreatmentRecommendations["@value"]
      );

      if (treatmentRecommendations)
        obj.TreatmentRecommendations["@value"] = JSON.stringify(
          treatmentRecommendations.map((item) => {
            return { ...item, IsEditable: false };
          })
        );
    }

    setData(obj);
  };

  const handleEducationStatusChange = (e) => {
    handleChange(e);
    if (e.currentTarget.value === "0") {
      const employedInput = {
        currentTarget: {
          type: "radio",
          name: "CurrentlyEmployed",
          value: "0",
        },
      };
      handleChange(employedInput);

      const militaryInput = {
        currentTarget: {
          type: "radio",
          name: "MilitaryHistory",
          value: "0",
        },
      };
      handleChange(militaryInput);
    }
  };

  const handleTraumaSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const TraumaticExposureInput = {
        currentTarget: {
          type: "checkbox",
          name: "TraumaticExposure",
          checked: false,
        },
      };
      handleChange(TraumaticExposureInput);

      const AvoidantTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "AvoidantTrauma",
          checked: false,
        },
      };
      handleChange(AvoidantTraumaInput);

      const HypervigilanceTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "HypervigilanceTrauma",
          checked: false,
        },
      };
      handleChange(HypervigilanceTraumaInput);

      const IntrusiveDreamsInput = {
        currentTarget: {
          type: "checkbox",
          name: "IntrusiveDreams",
          checked: false,
        },
      };
      handleChange(IntrusiveDreamsInput);

      const NegativeThoughtsInput = {
        currentTarget: {
          type: "checkbox",
          name: "NegativeThoughts",
          checked: false,
        },
      };
      handleChange(NegativeThoughtsInput);

      const CocentrationProblemsInput = {
        currentTarget: {
          type: "checkbox",
          name: "CocentrationProblems",
          checked: false,
        },
      };
      handleChange(CocentrationProblemsInput);

      const RecklessBehaviorTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "RecklessBehaviorTrauma",
          checked: false,
        },
      };
      handleChange(RecklessBehaviorTraumaInput);

      const SleepDisturbancesTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepDisturbancesTrauma",
          checked: false,
        },
      };
      handleChange(SleepDisturbancesTraumaInput);

      const OtherTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherTrauma",
          checked: false,
        },
      };
      handleChange(OtherTraumaInput);

      const OtherTraumaDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherTraumaDetails",
          value: "",
        },
      };
      handleChange(OtherTraumaDetailsInput);

      const TraumaSymptomsFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsFrequency",
          value: "",
        },
      };
      handleChange(TraumaSymptomsFrequencyInput);

      const TraumaSymptomsIntensityInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsIntensity",
          value: "",
        },
      };
      handleChange(TraumaSymptomsIntensityInput);

      const TraumaSymptomsDurationInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsDuration",
          value: "",
        },
      };
      handleChange(TraumaSymptomsDurationInput);

      const TraumaSymptomsOnsetInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsOnset",
          value: "",
        },
      };
      handleChange(TraumaSymptomsOnsetInput);
    }
  };

  const handleAnxietyDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AnxietyDisorderExcessWorryInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderExcessWorry",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderExcessWorryInput);

      const AnxietyDisorderEasilyFatiguedInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderEasilyFatigued",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderEasilyFatiguedInput);

      const MuscleTensionInput = {
        currentTarget: {
          type: "checkbox",
          name: "MuscleTension",
          checked: false,
        },
      };
      handleChange(MuscleTensionInput);

      const OverworriedInput = {
        currentTarget: {
          type: "checkbox",
          name: "Overworried",
          checked: false,
        },
      };
      handleChange(OverworriedInput);

      const AnxietyDisorderDifficultyConcentratingInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderDifficultyConcentrating",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderDifficultyConcentratingInput);

      const SleepDisturbancesAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepDisturbancesAnxiety",
          checked: false,
        },
      };
      handleChange(SleepDisturbancesAnxietyInput);

      const RestlessAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "RestlessAnxiety",
          checked: false,
        },
      };
      handleChange(RestlessAnxietyInput);

      const IrritabilityInput = {
        currentTarget: {
          type: "checkbox",
          name: "Irritability",
          checked: false,
        },
      };
      handleChange(IrritabilityInput);

      const OtherAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherAnxiety",
          checked: false,
        },
      };
      handleChange(OtherAnxietyInput);

      const OtherAnxietyDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherAnxietyDetails",
          value: "",
        },
      };
      handleChange(OtherAnxietyDetailsInput);

      const AnxietyFrequencyInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyFrequency",
          checked: false,
        },
      };
      handleChange(AnxietyFrequencyInput);

      const AnxietyIntensityInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyIntensity",
          checked: false,
        },
      };
      handleChange(AnxietyIntensityInput);

      const AnxietyDurationInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDuration",
          checked: false,
        },
      };
      handleChange(AnxietyDurationInput);
    }
  };

  const handleDepressionChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const DepressedMoodInput = {
        currentTarget: {
          type: "checkbox",
          name: "DepressedMood",
          checked: false,
        },
      };
      handleChange(DepressedMoodInput);

      const SleepChangesInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepChanges",
          checked: false,
        },
      };
      handleChange(SleepChangesInput);

      const GuiltInput = {
        currentTarget: {
          type: "checkbox",
          name: "Guilt",
          checked: false,
        },
      };
      handleChange(GuiltInput);

      const DiminishedInterestInput = {
        currentTarget: {
          type: "checkbox",
          name: "DiminishedInterest",
          checked: false,
        },
      };
      handleChange(DiminishedInterestInput);

      const PurposelessMovementInput = {
        currentTarget: {
          type: "checkbox",
          name: "PurposelessMovement",
          checked: false,
        },
      };
      handleChange(PurposelessMovementInput);

      const WeightChangesInput = {
        currentTarget: {
          type: "checkbox",
          name: "WeightChanges",
          checked: false,
        },
      };
      handleChange(WeightChangesInput);

      const FatigueDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "FatigueDepression",
          checked: false,
        },
      };
      handleChange(FatigueDepressionInput);

      const ThoughtsOfDeathInput = {
        currentTarget: {
          type: "checkbox",
          name: "ThoughtsOfDeath",
          checked: false,
        },
      };
      handleChange(ThoughtsOfDeathInput);

      const OtherDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherDepression",
          checked: false,
        },
      };
      handleChange(OtherDepressionInput);

      const OtherDepressionDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherDepressionDetails",
          value: "",
        },
      };
      handleChange(OtherDepressionDetailsInput);

      const DepressionFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "DepressionFrequency",
          value: "",
        },
      };
      handleChange(DepressionFrequencyInput);

      const DepressionIntensityInput = {
        currentTarget: {
          type: "input",
          name: "DepressionIntensity",
          value: "",
        },
      };
      handleChange(DepressionIntensityInput);

      const DepressionDurationInput = {
        currentTarget: {
          type: "input",
          name: "DepressionDuration",
          value: "",
        },
      };
      handleChange(DepressionDurationInput);
    }
  };

  const handleAttentionHyperactivityChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AttentionHyperactivityEasilyDistractedInput = {
        currentTarget: {
          type: "checkbox",
          name: "AttentionHyperactivityEasilyDistracted",
          checked: false,
        },
      };
      handleChange(AttentionHyperactivityEasilyDistractedInput);

      const DifficultyFollowThroughInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyFollowThrough",
          checked: false,
        },
      };
      handleChange(DifficultyFollowThroughInput);

      const ForgetfulInput = {
        currentTarget: {
          type: "checkbox",
          name: "Forgetful",
          checked: false,
        },
      };
      handleChange(ForgetfulInput);

      const DifficultyListeningInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyListening",
          checked: false,
        },
      };
      handleChange(DifficultyListeningInput);

      const DisorganizedAttentionInput = {
        currentTarget: {
          type: "checkbox",
          name: "DisorganizedAttention",
          checked: false,
        },
      };
      handleChange(DisorganizedAttentionInput);

      const FidgetyInput = {
        currentTarget: {
          type: "checkbox",
          name: "Fidgety",
          checked: false,
        },
      };
      handleChange(FidgetyInput);

      const LackOfDetailsInput = {
        currentTarget: {
          type: "checkbox",
          name: "LackOfDetails",
          checked: false,
        },
      };
      handleChange(LackOfDetailsInput);

      const LosesThingsInput = {
        currentTarget: {
          type: "checkbox",
          name: "LosesThings",
          checked: false,
        },
      };
      handleChange(LosesThingsInput);

      const InterruptsOthersInput = {
        currentTarget: {
          type: "checkbox",
          name: "InterruptsOthers",
          checked: false,
        },
      };
      handleChange(InterruptsOthersInput);

      const UnableToSitStillInput = {
        currentTarget: {
          type: "checkbox",
          name: "UnableToSitStill",
          checked: false,
        },
      };
      handleChange(UnableToSitStillInput);

      const ExcessiveTalkingInput = {
        currentTarget: {
          type: "checkbox",
          name: "ExcessiveTalking",
          checked: false,
        },
      };
      handleChange(ExcessiveTalkingInput);

      const DifficultyWaitingInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyWaiting",
          checked: false,
        },
      };
      handleChange(DifficultyWaitingInput);

      const OtherAttentionInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherAttention",
          checked: false,
        },
      };
      handleChange(OtherAttentionInput);

      const OtherAttentionDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherAttentionDetails",
          value: "",
        },
      };
      handleChange(OtherAttentionDetailsInput);

      const AttentionFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "AttentionFrequency",
          value: "",
        },
      };
      handleChange(AttentionFrequencyInput);

      const AttentionIntensityInput = {
        currentTarget: {
          type: "input",
          name: "AttentionIntensity",
          value: "",
        },
      };
      handleChange(AttentionIntensityInput);

      const AttentionDurationInput = {
        currentTarget: {
          type: "input",
          name: "AttentionDuration",
          value: "",
        },
      };
      handleChange(AttentionDurationInput);
    }
  };

  const handleSubstanceUseSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PatternOfSubstanceUseInput = {
        currentTarget: {
          type: "checkbox",
          name: "PatternOfSubstanceUse",
          checked: false,
        },
      };
      handleChange(PatternOfSubstanceUseInput);

      const DesiresCutDownUseInput = {
        currentTarget: {
          type: "checkbox",
          name: "DesiresCutDownUse",
          checked: false,
        },
      };
      handleChange(DesiresCutDownUseInput);

      const TimeSpentObtainingInput = {
        currentTarget: {
          type: "checkbox",
          name: "TimeSpentObtaining",
          checked: false,
        },
      };
      handleChange(TimeSpentObtainingInput);

      const CravingsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Cravings",
          checked: false,
        },
      };
      handleChange(CravingsInput);

      const FailureOfResponsibilitiesInput = {
        currentTarget: {
          type: "checkbox",
          name: "FailureOfResponsibilities",
          checked: false,
        },
      };
      handleChange(FailureOfResponsibilitiesInput);

      const NegativeImpactInput = {
        currentTarget: {
          type: "checkbox",
          name: "NegativeImpact",
          checked: false,
        },
      };
      handleChange(NegativeImpactInput);

      const AbandonsActivitiesInput = {
        currentTarget: {
          type: "checkbox",
          name: "AbandonsActivities",
          checked: false,
        },
      };
      handleChange(AbandonsActivitiesInput);

      const ToleranceInput = {
        currentTarget: {
          type: "checkbox",
          name: "Tolerance",
          checked: false,
        },
      };
      handleChange(ToleranceInput);

      const SUWithdrawalInput = {
        currentTarget: {
          type: "checkbox",
          name: "SUWithdrawal",
          checked: false,
        },
      };
      handleChange(SUWithdrawalInput);

      const SUOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "SUOther",
          checked: false,
        },
      };
      handleChange(SUOtherInput);

      const SUOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "SUOtherDetails",
          value: "",
        },
      };
      handleChange(SUOtherDetailsInput);

      const SubstanceUseFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseFrequency",
          value: "",
        },
      };
      handleChange(SubstanceUseFrequencyInput);

      const SubstanceUseIntensityInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseIntensity",
          value: "",
        },
      };
      handleChange(SubstanceUseIntensityInput);

      const SubstanceUseDurationInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseDuration",
          value: "",
        },
      };
      handleChange(SubstanceUseDurationInput);
    }
  };

  const handleDMDDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const TemperInput = {
        currentTarget: {
          type: "checkbox",
          name: "Temper",
          checked: false,
        },
      };
      handleChange(TemperInput);

      const OutsideOutburstsInput = {
        currentTarget: {
          type: "checkbox",
          name: "OutsideOutbursts",
          checked: false,
        },
      };
      handleChange(OutsideOutburstsInput);

      const FrequentOutburstsInput = {
        currentTarget: {
          type: "checkbox",
          name: "FrequentOutbursts",
          checked: false,
        },
      };
      handleChange(FrequentOutburstsInput);

      const DMDDIrritableInput = {
        currentTarget: {
          type: "checkbox",
          name: "DMDDIrritable",
          checked: false,
        },
      };
      handleChange(DMDDIrritableInput);

      const OutburstsAroundOthersInput = {
        currentTarget: {
          type: "checkbox",
          name: "OutburstsAroundOthers",
          checked: false,
        },
      };
      handleChange(OutburstsAroundOthersInput);

      const DMDDOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "DMDDOther",
          checked: false,
        },
      };
      handleChange(DMDDOtherInput);

      const DMDDOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "DMDDOtherDetails",
          value: "",
        },
      };
      handleChange(DMDDOtherDetailsInput);

      const DMDDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "DMDDFrequency",
          value: "",
        },
      };
      handleChange(DMDDFrequencyInput);

      const DMDDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "DMDDIntensity",
          value: "",
        },
      };
      handleChange(DMDDIntensityInput);

      const DMDDDurationInput = {
        currentTarget: {
          type: "input",
          name: "DMDDDuration",
          value: "",
        },
      };
      handleChange(DMDDDurationInput);
    }
  };

  const handleConductDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AggressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "Aggression",
          checked: false,
        },
      };
      handleChange(AggressionInput);

      const DestructionOfPropertyInput = {
        currentTarget: {
          type: "checkbox",
          name: "DestructionOfProperty",
          checked: false,
        },
      };
      handleChange(DestructionOfPropertyInput);

      const TheftInput = {
        currentTarget: {
          type: "checkbox",
          name: "Theft",
          checked: false,
        },
      };
      handleChange(TheftInput);

      const SeriousViolationsInput = {
        currentTarget: {
          type: "checkbox",
          name: "SeriousViolations",
          checked: false,
        },
      };
      handleChange(SeriousViolationsInput);

      const OtherConductDisorderInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherConductDisorder",
          checked: false,
        },
      };
      handleChange(OtherConductDisorderInput);

      const OtherConductDisorderDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherConductDisorderDetails",
          value: "",
        },
      };
      handleChange(OtherConductDisorderDetailsInput);

      const ConductDisorderFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderFrequency",
          value: "",
        },
      };
      handleChange(ConductDisorderFrequencyInput);

      const ConductDisorderIntensityInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderIntensity",
          value: "",
        },
      };
      handleChange(ConductDisorderIntensityInput);

      const ConductDisorderDurationInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderDuration",
          value: "",
        },
      };
      handleChange(ConductDisorderDurationInput);
    }
  };

  const handleODDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const IrritableODDInput = {
        currentTarget: {
          type: "checkbox",
          name: "IrritableODD",
          checked: false,
        },
      };
      handleChange(IrritableODDInput);

      const ArgumentativeInput = {
        currentTarget: {
          type: "checkbox",
          name: "Argumentative",
          checked: false,
        },
      };
      handleChange(ArgumentativeInput);

      const VindictiveInput = {
        currentTarget: {
          type: "checkbox",
          name: "Vindictive",
          checked: false,
        },
      };
      handleChange(VindictiveInput);

      const OtherODDInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherODD",
          checked: false,
        },
      };
      handleChange(OtherODDInput);

      const OtherODDDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherODDDetails",
          value: "",
        },
      };
      handleChange(OtherODDDetailsInput);

      const ODDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "ODDFrequency",
          value: "",
        },
      };
      handleChange(ODDFrequencyInput);

      const ODDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "ODDIntensity",
          value: "",
        },
      };
      handleChange(ODDIntensityInput);

      const ODDDurationInput = {
        currentTarget: {
          type: "input",
          name: "ODDDuration",
          value: "",
        },
      };
      handleChange(ODDDurationInput);
    }
  };

  const handleOCDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const OCDPresenceInput = {
        currentTarget: {
          type: "checkbox",
          name: "OCDPresence",
          checked: false,
        },
      };
      handleChange(OCDPresenceInput);

      const ObsessionsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Obsessions",
          checked: false,
        },
      };
      handleChange(ObsessionsInput);

      const TimeConsumingInput = {
        currentTarget: {
          type: "checkbox",
          name: "TimeConsuming",
          checked: false,
        },
      };
      handleChange(TimeConsumingInput);

      const CompulsionsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Compulsions",
          checked: false,
        },
      };
      handleChange(CompulsionsInput);

      const ReduceAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "ReduceAnxiety",
          checked: false,
        },
      };
      handleChange(ReduceAnxietyInput);

      const SuppressUrgesInput = {
        currentTarget: {
          type: "checkbox",
          name: "SuppressUrges",
          checked: false,
        },
      };
      handleChange(SuppressUrgesInput);

      const OCDOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "OCDOther",
          checked: false,
        },
      };
      handleChange(OCDOtherInput);

      const OCDOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OCDOtherDetails",
          value: "",
        },
      };
      handleChange(OCDOtherDetailsInput);

      const OCDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "OCDFrequency",
          value: "",
        },
      };
      handleChange(OCDFrequencyInput);

      const OCDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "OCDIntensity",
          value: "",
        },
      };
      handleChange(OCDIntensityInput);

      const OCDDurationInput = {
        currentTarget: {
          type: "input",
          name: "OCDDuration",
          value: "",
        },
      };
      handleChange(OCDDurationInput);
    }
  };

  const handlePanicDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PoundingHeartInput = {
        currentTarget: {
          type: "checkbox",
          name: "PoundingHeart",
          checked: false,
        },
      };
      handleChange(PoundingHeartInput);

      const PanicDisorderSweatingInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderSweating",
          checked: false,
        },
      };
      handleChange(PanicDisorderSweatingInput);

      const TremblingInput = {
        currentTarget: {
          type: "checkbox",
          name: "Trembling",
          checked: false,
        },
      };
      handleChange(TremblingInput);

      const ShortnessOfBreathInput = {
        currentTarget: {
          type: "checkbox",
          name: "ShortnessOfBreath",
          checked: false,
        },
      };
      handleChange(ShortnessOfBreathInput);

      const ChokingInput = {
        currentTarget: {
          type: "checkbox",
          name: "Choking",
          checked: false,
        },
      };
      handleChange(ChokingInput);

      const ChestPainInput = {
        currentTarget: {
          type: "checkbox",
          name: "ChestPain",
          checked: false,
        },
      };
      handleChange(ChestPainInput);

      const PanicDisorderNauseaInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderNausea",
          checked: false,
        },
      };
      handleChange(PanicDisorderNauseaInput);

      const DizzyInput = {
        currentTarget: {
          type: "checkbox",
          name: "Dizzy",
          checked: false,
        },
      };
      handleChange(DizzyInput);

      const ChillsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Chills",
          checked: false,
        },
      };
      handleChange(ChillsInput);

      const DerealizationInput = {
        currentTarget: {
          type: "checkbox",
          name: "Derealization",
          checked: false,
        },
      };
      handleChange(DerealizationInput);

      const FearOfCrazyInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearOfCrazy",
          checked: false,
        },
      };
      handleChange(FearOfCrazyInput);

      const FearOfDyingInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearOfDying",
          checked: false,
        },
      };
      handleChange(FearOfDyingInput);

      const PanicDisorderOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderOther",
          checked: false,
        },
      };
      handleChange(PanicDisorderOtherInput);

      const PanicDisorderOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderOtherDetails",
          value: "",
        },
      };
      handleChange(PanicDisorderOtherDetailsInput);

      const PanicDisorderFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderFrequency",
          value: "",
        },
      };
      handleChange(PanicDisorderFrequencyInput);

      const PanicDisorderIntensityInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderIntensity",
          value: "",
        },
      };
      handleChange(PanicDisorderIntensityInput);

      const PanicDisorderDurationInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderDuration",
          value: "",
        },
      };
      handleChange(PanicDisorderDurationInput);
    }
  };

  const handlePhobiasChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PhobiasSpecificFearInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasSpecificFear",
          checked: false,
        },
      };
      handleChange(PhobiasSpecificFearInput);

      const ImmediateFearInput = {
        currentTarget: {
          type: "checkbox",
          name: "ImmediateFear",
          checked: false,
        },
      };
      handleChange(ImmediateFearInput);

      const PhobiasAvoidanceInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasAvoidance",
          checked: false,
        },
      };
      handleChange(PhobiasAvoidanceInput);

      const FearExaggeratedInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearExaggerated",
          checked: false,
        },
      };
      handleChange(FearExaggeratedInput);

      const PhobiasOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasOther",
          checked: false,
        },
      };
      handleChange(PhobiasOtherInput);

      const PhobiasOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasOtherDetails",
          value: "",
        },
      };
      handleChange(PhobiasOtherDetailsInput);

      const PhobiasFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasFrequency",
          value: "",
        },
      };
      handleChange(PhobiasFrequencyInput);

      const PhobiasIntensityInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasIntensity",
          value: "",
        },
      };
      handleChange(PhobiasIntensityInput);

      const PhobiasDurationInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasDuration",
          value: "",
        },
      };
      handleChange(PhobiasDurationInput);
    }
  };

  const handleDIChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const DILowIQInput = {
        currentTarget: {
          type: "checkbox",
          name: "DILowIQ",
          checked: false,
        },
      };
      handleChange(DILowIQInput);

      const DISensoryImpairmentsInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISensoryImpairments",
          checked: false,
        },
      };
      handleChange(DISensoryImpairmentsInput);

      const DISpeechDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISpeechDelays",
          checked: false,
        },
      };
      handleChange(DISpeechDelaysInput);

      const DIMotorCoordinationDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DIMotorCoordinationDelays",
          checked: false,
        },
      };
      handleChange(DIMotorCoordinationDelaysInput);

      const DILearningDisorderInput = {
        currentTarget: {
          type: "checkbox",
          name: "DILearningDisorder",
          checked: false,
        },
      };
      handleChange(DILearningDisorderInput);

      const DISpatialDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISpatialDelays",
          checked: false,
        },
      };
      handleChange(DISpatialDelaysInput);

      const DevelopmentalImpairmentsOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "DevelopmentalImpairmentsOther",
          checked: false,
        },
      };
      handleChange(DevelopmentalImpairmentsOtherInput);

      const DevelopmentalImpairmentsOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "DevelopmentalImpairmentsOtherDetails",
          value: "",
        },
      };
      handleChange(DevelopmentalImpairmentsOtherDetailsInput);
    }
  };

  const handleOtherSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const OtherSymptomsDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsDetails",
          value: "",
        },
      };
      handleChange(OtherSymptomsDetailsInput);

      const OtherSymptomsFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsFrequency",
          value: "",
        },
      };
      handleChange(OtherSymptomsFrequencyInput);

      const OtherSymptomsIntensityInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsIntensity",
          value: "",
        },
      };
      handleChange(OtherSymptomsIntensityInput);

      const OtherSymptomsDurationInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsDuration",
          value: "",
        },
      };
      handleChange(OtherSymptomsDurationInput);
    }
  };

  const getFamilyMemberConcerns = (item) => {
    const concerns = [];

    if (item.MentalHealth === "true") concerns.push("Mental Health");
    if (item.Medical === "true") concerns.push("Medical");
    if (item.Disability === "true") concerns.push("Disability");
    if (item.SubstanceAbuse === "true") concerns.push("Substance Abuse");
    if (item.Suicidal === "true") concerns.push("Suicidal");
    if (item.Legal === "true") concerns.push("Legal");
    if (item.Trauma === "true") concerns.push("Trauma");
    if (item.Perpetration === "true") concerns.push("Perpetration");
    if (item.Other === "true") concerns.push("Other");

    if (concerns.length > 0) return concerns.join(", ");
    else return "None reported";
  };

  const getTreatmentRecommendationStatus = (item) => {
    let statuses = [];

    if (
      item.Deferred === "true" ||
      (item.Deferred !== "false" && item.StatusDeferred === "1")
    )
      statuses.push("SAFY will be providing");

    if (
      item.Referred === "true" ||
      (item.Referred !== "false" && item.StatusReferred === "1")
    )
      statuses.push("SAFY will be referring to another agency/individual");

    if (item.Declined === "true") statuses.push("Declined by Recipient");

    return statuses.join(", ");
  };

  const handleSubstanceUseNAChange = (e) => {
    handleChange(e);
    const noPastTreatmentInput = {
      currentTarget: {
        type: "checkbox",
        name: "NoPastTreatment",
        checked: true,
      },
    };
    handleChange(noPastTreatmentInput);
  };

  const sexAtBirthOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Intersex", name: "Intersex" },
    { _id: "Sex not listed", name: "Sex not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const genderIdentityOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Non-binary", name: "Non-binary" },
    { _id: "Agender", name: "Agender" },
    { _id: "Transgender (FtM)", name: "Transgender (FtM)" },
    { _id: "Transgender (MtF)", name: "Transgender (MtF)" },
    { _id: "Identity not listed", name: "Identity not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const pronounOptions = [
    { _id: "She/Her/Hers", name: "She/Her/Hers" },
    { _id: "He/Him/His", name: "He/Him/His" },
    { _id: "They/Them/Their", name: "They/Them/Their" },
    { _id: "Prefer to self-describe", name: "Prefer to self-describe" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const divisionOptions = [
    { _id: "Birmingham", name: "Birmingham" },
    { _id: "Huntsville", name: "Huntsville" },
    { _id: "Mobile", name: "Mobile" },
    { _id: "Montgomery", name: "Montgomery" },
  ];

  const psdTypeOptions = [
    { _id: "Initial", name: "Initial" },
    { _id: "Review/Update", name: "Review/Update" },
  ];

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const yesNoNAOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "N/A", name: "N/A" },
  ];

  const yesNoNAAgeOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    {
      _id: "N/A - Recipient Unable to Agree due to age or ability",
      name: "N/A - Recipient Unable to Agree due to age or ability",
    },
  ];

  const yesNoUnknownOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "Unknown", name: "Unknown" },
  ];

  const yesNoNoneOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "None reported", name: "None reported" },
  ];

  const yesNoNoneNAOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "None reported", name: "None reported" },
    { _id: "N/A", name: "N/A" },
  ];

  const livingSituationOptions = [
    { _id: "Parent's Home", name: "Parent's Home" },
    {
      _id: "Kindship/Other Caregiver Home",
      name: "Kindship/Other Caregiver Home",
    },
    { _id: "Foster Home", name: "Foster Home" },
    { _id: "Other", name: "Other" },
  ];

  const relationshipOptions = [
    { _id: "Father", name: "Father" },
    { _id: "Mother", name: "Mother" },
    { _id: "Grandfather", name: "Grandfather" },
    { _id: "Grandmother", name: "Grandmother" },
    { _id: "Brother", name: "Brother" },
    { _id: "Sister", name: "Sister" },
    { _id: "Step-father", name: "Step-father" },
    { _id: "Step-mother", name: "Step-mother" },
    { _id: "Foster Parent", name: "Foster Parent" },
    { _id: "Uncle", name: "Uncle" },
    { _id: "Aunt", name: "Aunt" },
    { _id: "Cousin", name: "Cousin" },
    { _id: "Family Friend", name: "Family Friend" },
    { _id: "Coach", name: "Coach" },
    { _id: "Mentor", name: "Mentor" },
    { _id: "Friend/Peer", name: "Friend/Peer" },
    { _id: "Foster Sibling", name: "Foster Sibling" },
    { _id: "Paramour", name: "Paramour" },
    { _id: "Other Family Member", name: "Other Family Member" },
    { _id: "Other", name: "Other" },
    { _id: "Child", name: "Child" },
  ];

  const familyMemberOptions = [
    { _id: "Father", name: "Father" },
    { _id: "Mother", name: "Mother" },
    { _id: "Grandfather", name: "Grandfather" },
    { _id: "Grandmother", name: "Grandmother" },
    { _id: "Uncle", name: "Uncle" },
    { _id: "Aunt", name: "Aunt" },
    { _id: "Brother", name: "Brother" },
    { _id: "Sister", name: "Sister" },
    { _id: "Step-father", name: "Step-father" },
    { _id: "Step-mother", name: "Step-mother" },
    { _id: "Cousin", name: "Cousin" },
  ];

  const disciplinePracticesOptions = [
    { _id: "Effective", name: "Effective" },
    { _id: "Inconsistent", name: "Inconsistent" },
    { _id: "Ineffective", name: "Ineffective" },
  ];

  const educationOptions = [
    {
      _id: "Recipient has not started school",
      name: "Recipient has not started school",
    },
    {
      _id: "Currently enrolled in school",
      name: "Currently enrolled in school",
    },
    { _id: "Graduated", name: "Graduated" },
    { _id: "GED", name: "GED" },
    { _id: "Dropout/Did not complete", name: "Dropout/Did not complete" },
  ];

  const functioningLevelOptions = [
    { _id: "Appropriate", name: "Appropriate" },
    { _id: "Problematic", name: "Problematic" },
    { _id: "N/A", name: "N/A" },
  ];

  const outcomeOptions = [
    { _id: "Successful", name: "Successful" },
    { _id: "Unsuccessful", name: "Unsuccessful" },
    { _id: "Did not complete", name: "Did not complete" },
    { _id: "Other", name: "Other" },
  ];

  const useOfProtectionOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "Not with Consistency", name: "Not with Consistency" },
    { _id: "N/A", name: "N/A" },
  ];

  const pregnancyOptions = [
    { _id: "Current", name: "Current" },
    { _id: "Past", name: "Past" },
    { _id: "None Reported", name: "None Reported" },
    { _id: "N/A", name: "N/A" },
  ];

  const traumaOptions = [
    { _id: "Physical Abuse", name: "Physical Abuse" },
    { _id: "Sexual Abuse", name: "Sexual Abuse" },
    { _id: "Neglect", name: "Neglect" },
    { _id: "Emotional Abuse", name: "Emotional Abuse" },
    { _id: "Domestic Violence", name: "Domestic Violence" },
    { _id: "Accident", name: "Accident" },
    { _id: "Acts of Violence", name: "Acts of Violence" },
    { _id: "Bullying", name: "Bullying" },
    { _id: "Death/Tragedy", name: "Death/Tragedy" },
    { _id: "Natural Disaster", name: "Natural Disaster" },
    { _id: "Community Violence", name: "Community Violence" },
    { _id: "Medical/Serious Illness", name: "Medical/Serious Illness" },
    { _id: "Abandonment", name: "Abandonment" },
    { _id: "War/Terrorism", name: "War/Terrorism" },
    { _id: "Exploitation/Trafficking", name: "Exploitation/Trafficking" },
    { _id: "Criminal Activity", name: "Criminal Activity" },
    { _id: "Caregiver Disruptions", name: "Caregiver Disruptions" },
    { _id: "Kidnapping", name: "Kidnapping" },
    { _id: "Other", name: "Other" },
  ];

  const statusOptions = [
    { _id: "Victim", name: "Victim" },
    { _id: "Witnessed", name: "Witnessed" },
    { _id: "Suspected", name: "Suspected" },
    { _id: "Threatened", name: "Threatened" },
    { _id: "Perceived", name: "Perceived" },
    { _id: "Repeated Exposure", name: "Repeated Exposure" },
    { _id: "Indirect Exposure", name: "Indirect Exposure" },
  ];

  const traumaAwarenessOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    {
      _id: "Aware but No Recall/Memories",
      name: "Aware but No Recall/Memories",
    },
  ];

  const timeframeOptions = [
    { _id: "None/Never", name: "None/Never" },
    { _id: "Current", name: "Current" },
    { _id: "Last 30 Days", name: "Last 30 Days" },
    { _id: "Last 6 Months", name: "Last 6 Months" },
    { _id: "More Than 6 Months Ago", name: "More Than 6 Months Ago" },
  ];

  const safetyPlanStatusOptions = [
    { _id: "None", name: "None" },
    { _id: "Active/Effective", name: "Active/Effective" },
    { _id: "Ineffective", name: "Ineffective" },
    { _id: "Updates Completed", name: "Updates Completed" },
    { _id: "Not Needed", name: "Not Needed" },
  ];

  const riskOptions = [
    { _id: "None Noted", name: "None Noted" },
    { _id: "Low", name: "Low" },
    { _id: "Moderate", name: "Moderate" },
    { _id: "High", name: "High" },
  ];

  const treatmentRecommendationOptions = [
    { _id: "Counseling", name: "Counseling" },
    { _id: "Basic Living Skills", name: "Basic Living Skills" },
    { _id: "Psychoeducational Services", name: "Psychoeducation Services" },
    { _id: "In-Home Interventions", name: "In-Home Interventions" },
    {
      _id: "Mental Health Care Coordination",
      name: "Mental Health Care Coordination",
    },
    { _id: "Medication Monitoring", name: "Medication Monitoring" },
    { _id: "Case Management Services", name: "Case Management Services" },
    {
      _id: "Psychiatric Evaluation/Services",
      name: "Psychiatric Evaluation/Services",
    },
    {
      _id: "Psychosocial Rehabilitation Services (PSR)",
      name: "Psychosocial Rehabilitation Services (PSR)",
    },
    {
      _id: "Early Childhood Intervention Services",
      name: "Early Childhood Intervention Services",
    },
    {
      _id: "Therapeutic Mentoring",
      name: "Therapeutic Mentoring",
    },
    {
      _id: "Substance Abuse Specific Treatment",
      name: "Substance Abuse Specific Treatment",
    },
  ];

  const buildClinicalNarrativeSummary = () => {
    let summary = "";

    // Presenting Problem & Strengths
    let presentingProblemSummary = "";

    if (data.ReferralReason["@value"] && data.ReferralReason["@value"] !== "")
      presentingProblemSummary += `Reason for Referral: ${data.ReferralReason["@value"]}; `;

    if (data.ReferralSource["@value"] && data.ReferralSource["@value"] !== "")
      presentingProblemSummary += `Referral Source: ${data.ReferralSource["@value"]}; `;

    if (
      data.PresentingProblem["@value"] &&
      data.PresentingProblem["@value"] !== ""
    )
      presentingProblemSummary += `Presenting Problem: ${data.PresentingProblem["@value"]}; `;

    if (
      data.RecipientStrengths["@value"] &&
      data.RecipientStrengths["@value"] !== ""
    )
      presentingProblemSummary += `Recipient Strengths: ${data.RecipientStrengths["@value"]}; `;

    if (
      data.CaregiverStrengths["@value"] &&
      data.CaregiverStrengths["@value"] !== ""
    )
      presentingProblemSummary += `Caregiver Description of Strengths: ${data.CaregiverStrengths["@value"]}; `;

    if (
      data.RecipientAbilities["@value"] &&
      data.RecipientAbilities["@value"] !== ""
    )
      presentingProblemSummary += `Recipient abilities, skills, and interests: ${data.RecipientAbilities["@value"]}; `;

    if (presentingProblemSummary !== "")
      summary += `Presenting Problem:\r\n${presentingProblemSummary.trimEnd()}\r\n\r\n`;

    //Family Environment and History
    let familyHistorySummary = "";
    if (
      data.RecipientSupportsDetails["@value"] &&
      data.RecipientSupportsDetails["@value"] !== "" &&
      data.RecipientSupportsDetails["@value"] !== '[{"IsEditable": true}]'
    )
      familyHistorySummary += `All Family Members/Supports for Recipient: ${JSON.parse(
        data.RecipientSupportsDetails["@value"]
      )
        .filter((item) => !item.IsEditable)
        .map((item) => `${item.Name} - ${item.RelationshipQuality}`)
        .join(", ")
        .trimEnd()}; `;

    if (
      data.FamilyHistoryDetails["@value"] &&
      data.FamilyHistoryDetails["@value"] !== "" &&
      data.FamilyHistoryDetails["@value"] !== '[{"IsEditable": true}]'
    )
      familyHistorySummary += `Family Members: ${JSON.parse(
        data.FamilyHistoryDetails["@value"]
      )
        .filter((item) => !item.IsEditable)
        .map((item) => `${item.Name} - ${item.Comments}`)
        .join(", ")
        .trimEnd()}; `;

    if (familyHistorySummary !== "")
      summary += `Family History:\r\n${familyHistorySummary.trimEnd()}\r\n\r\n`;

    // // Culture/Ethnicity/Religion/Spirituality
    // let cultureSummary = "";

    // if (
    //   data.SocioeconomicStatus["@value"] &&
    //   data.SocioeconomicStatus["@value"] !== ""
    // )
    //   cultureSummary += `Socioeconomic Status: ${data.SocioeconomicStatus["@value"]}; `;

    // if (data.Religion["@value"] && data.Religion["@value"] !== "")
    //   cultureSummary += `Identified religion/belief system and role of faith in recipient's life: ${data.Religion["@value"]}; `;

    // if (
    //   data.CulturalDifferencesComments["@value"] &&
    //   data.CulturalDifferencesComments["@value"] !== ""
    // )
    //   cultureSummary += `Comments: ${data.CulturalDifferencesComments["@value"]}; `;

    // if (cultureSummary !== "")
    //   summary += `Culture/Ethnicity/Religion/Spirituality:\r\n${cultureSummary.trimEnd()}\r\n\r\n`;

    // // Developmental History
    // let developmentalSummary = "";
    // if (data.PHComments["@value"] && data.PHComments["@value"] !== "")
    //   developmentalSummary += `Mother's Pregnancy History Comments: ${data.PHComments["@value"]}; `;

    // if (
    //   (data.MilestonesMetDetails["@value"] &&
    //     data.MilestonesMetDetails["@value"] !== "") ||
    //   (data.SpecialNeedsDetails["@value"] &&
    //     data.SpecialNeedsDetails["@value"]) !== ""
    // )
    //   developmentalSummary += `${`Developmental delays or special needs: ${
    //     data.MilestonesMetDetails["@value"] !== undefined &&
    //     data.MilestonesMetDetails["@value"] !== ""
    //       ? `${data.MilestonesMetDetails["@value"]}, `
    //       : ""
    //   }${
    //     data.SpecialNeedsDetails["@value"] !== undefined &&
    //     data.SpecialNeedsDetails["@value"] !== ""
    //       ? `${data.SpecialNeedsDetails["@value"]}, `
    //       : ""
    //   }`.slice(0, -2)}; `;

    // if (
    //   data.SpecializedServicesDetails["@value"] &&
    //   data.SpecializedServicesDetails["@value"] !== ""
    // )
    //   developmentalSummary += `Specialized Services: ${data.SpecializedServicesDetails["@value"]}; `;

    // if (developmentalSummary !== "")
    //   summary += `Developmental History:\r\n${developmentalSummary.trimEnd()}\r\n\r\n`;

    // //Education/Employment
    // let educationSummary = "";
    // if (data.SchoolName["@value"] && data.SchoolName["@value"] !== "")
    //   educationSummary += `School Name: ${data.SchoolName["@value"]}; `;

    // if (data.CurrentGrade["@value"] && data.CurrentGrade["@value"] !== "")
    //   educationSummary += `Current Grade: ${data.CurrentGrade["@value"]}; `;

    // if (
    //   data.IEPClassification["@value"] &&
    //   data.IEPClassification["@value"] !== ""
    // )
    //   educationSummary += `IEP Classification: ${data.IEPClassification["@value"]}; `;

    // if (
    //   data.SchoolExperience["@value"] &&
    //   data.SchoolExperience["@value"] !== ""
    // )
    //   educationSummary += `Summary of Recipient's School Experience: ${data.SchoolExperience["@value"]}; `;

    // if (
    //   data.EmployerAndTitle["@value"] &&
    //   data.EmployerAndTitle["@value"] !== ""
    // )
    //   educationSummary += `Name of employer and title: ${data.EmployerAndTitle["@value"]}; `;

    // if (
    //   data.EmploymentInterests["@value"] &&
    //   data.EmploymentInterests["@value"] !== ""
    // )
    //   educationSummary += `Employment Interests/Skills/Concerns: ${data.EmploymentInterests["@value"]}; `;

    // if (educationSummary !== "")
    //   summary += `Education/Employment:\r\n${educationSummary.trimEnd()}\r\n\r\n`;

    // // Activites of Daily Living
    // let dailyLivingSummary = "";
    // if (
    //   data.MoneyManagementDetails["@value"] &&
    //   data.MoneyManagementDetails["@value"] !== ""
    // )
    //   dailyLivingSummary += `Money management: ${data.MoneyManagementDetails["@value"]}; `;

    // if (
    //   data.FoodManagementDetails["@value"] &&
    //   data.FoodManagementDetails["@value"] !== ""
    // )
    //   dailyLivingSummary += `Food management: ${data.FoodManagementDetails["@value"]}; `;

    // if (data.HygieneDetails["@value"] && data.HygieneDetails["@value"] !== "")
    //   dailyLivingSummary += `Personal appearance and hygiene: ${data.HygieneDetails["@value"]}; `;

    // if (data.HealthDetails["@value"] && data.HealthDetails["@value"] !== "")
    //   dailyLivingSummary += `Health awareness/Personal Health: ${data.HealthDetails["@value"]}; `;

    // if (
    //   data.HousekeepingDetails["@value"] &&
    //   data.HousekeepingDetails["@value"] !== ""
    // )
    //   dailyLivingSummary += `Housekeeping: ${data.HousekeepingDetails["@value"]}; `;

    // if (
    //   data.TransportationDetails["@value"] &&
    //   data.TransportationDetails["@value"] !== ""
    // )
    //   dailyLivingSummary += `Transportation: ${data.TransportationDetails["@value"]}; `;

    // if (
    //   data.SafetySkillsDetails["@value"] &&
    //   data.SafetySkillsDetails["@value"] !== ""
    // )
    //   dailyLivingSummary += `Emergency and safety skills: ${data.SafetySkillsDetails["@value"]}; `;

    // if (
    //   data.ResourceKnowledgeDetails["@value"] &&
    //   data.ResourceKnowledgeDetails["@value"] !== ""
    // )
    //   dailyLivingSummary += `Knowledge of community resources: ${data.ResourceKnowledgeDetails["@value"]}; `;

    // if (
    //   data.TimeManagementDetails["@value"] &&
    //   data.TimeManagementDetails["@value"] !== ""
    // )
    //   dailyLivingSummary += `Time management: ${data.TimeManagementDetails["@value"]}; `;

    // if (
    //   data.InteractionDetails["@value"] &&
    //   data.InteractionDetails["@value"] !== ""
    // )
    //   dailyLivingSummary += `Interactions with Others: ${data.InteractionDetails["@value"]}; `;

    // if (dailyLivingSummary !== "")
    //   summary += `Activities of Daily Living:\r\n${dailyLivingSummary.trimEnd()}\r\n\r\n`;

    // // Friendship/Social Peer Support/Relationships
    // let relationshipsSummary = "";
    // if (
    //   data.RelationshipsSummary["@value"] &&
    //   data.RelationshipsSummary["@value"] !== ""
    // )
    //   relationshipsSummary += `Friendship/Social Support/Relationships Summary Narrative: ${data.RelationshipsSummary["@value"]};`;

    // if (relationshipsSummary !== "")
    //   summary += `${relationshipsSummary}\r\n\r\n`;

    // // Legal History
    // let legalSummary = "";
    // if (
    //   data.LegalInvolvementDetails["@value"] &&
    //   data.LegalInvolvementDetails["@value"] !== ""
    // )
    //   legalSummary += `Current involvement with the Legal System: ${data.LegalInvolvementDetails["@value"]}; `;

    // if (
    //   data.CPSInvolvementDetails["@value"] &&
    //   data.CPSInvolvementDetails["@value"] !== ""
    // )
    //   legalSummary += `Current Children's Protective Services (CPS) Involvement with Family: ${data.CPSInvolvementDetails["@value"]}; `;

    // if (
    //   data.CPSCaseworkerName["@value"] &&
    //   data.CPSCaseworkerName["@value"] !== ""
    // )
    //   legalSummary += `CPS Caseworker: ${data.CPSCaseworkerName["@value"]}; `;

    // if (
    //   data.ProbationOfficerName["@value"] &&
    //   data.ProbationOfficerName["@value"] !== ""
    // )
    //   legalSummary += `Probation/Parole Officer: ${data.ProbationOfficerName["@value"]}; `;

    // if (
    //   data.OtherLegalDetails["@value"] &&
    //   data.OtherLegalDetails["@value"] !== ""
    // )
    //   legalSummary += `Other Court/Custody Related Issues: ${data.OtherLegalDetails["@value"]}; `;

    // if (legalSummary !== "")
    //   summary += `Legal History:\r\n${legalSummary.trimEnd()}\r\n\r\n`;

    // // Medical History/Medications
    // let medicalSummary = "";
    // if (data.PCP["@value"] && data.PCP["@value"] !== "")
    //   if (data.PCPPhone["@value"] && data.PCPPhone["@value"] !== "")
    //     medicalSummary += `Primary Care Physician: ${data.PCP["@value"]}, Phone: ${data.PCPPhone["@value"]}; `;
    //   else medicalSummary += `Primary Care Physician: ${data.PCP["@value"]}; `;

    // if (
    //   data.LastMedicalExamDate["@value"] &&
    //   data.LastMedicalExamDate["@value"] !== ""
    // )
    //   medicalSummary += `Date of last medical exam: ${moment(
    //     data.LastMedicalExamDate["@value"]
    //   ).format("L")}; `;

    // if (
    //   data.LastDentalExamDate["@value"] &&
    //   data.LastDentalExamDate["@value"] !== ""
    // )
    //   medicalSummary += `Date of last dental exam: ${moment(
    //     data.LastDentalExamDate["@value"]
    //   ).format("L")}; `;

    // if (
    //   data.AllergiesDetails["@value"] &&
    //   data.AllergiesDetails["@value"] !== ""
    // )
    //   medicalSummary += `Allergies: ${data.AllergiesDetails["@value"]}; `;

    // if (
    //   data.MedicalConditionsDetails["@value"] &&
    //   data.MedicalConditionsDetails["@value"] !== ""
    // )
    //   medicalSummary += `Medical conditions/concerns: ${data.MedicalConditionsDetails["@value"]}; `;

    // if (
    //   data.NutritionalConcernsDetails["@value"] &&
    //   data.NutritionalConcernsDetails["@value"] !== ""
    // )
    //   medicalSummary += `Nutritional concerns: ${data.NutritionalConcernsDetails["@value"]}; `;

    // if (
    //   data.DentalConcernsDetails["@value"] &&
    //   data.DentalConcernsDetails["@value"] !== ""
    // )
    //   medicalSummary += `Dental concerns: ${data.DentalConcernsDetails["@value"]}; `;

    // if (data.MedicalComments["@value"] && data.MedicalComments["@value"] !== "")
    //   medicalSummary += `Comments: ${data.MedicalComments["@value"]}; `;

    // if (
    //   data.CurrentMedicationDetails["@value"] &&
    //   data.CurrentMedicationDetails["@value"] !== ""
    // )
    //   medicalSummary += `Current Medications: ${JSON.parse(
    //     data.CurrentMedicationDetails["@value"]
    //   )
    //     .map((medication) => medication.Name)
    //     .join(", ")}; `;

    // if (
    //   data.SideEffectsDetails["@value"] &&
    //   data.SideEffectsDetails["@value"] !== ""
    // )
    //   medicalSummary += `Side effects from medications: ${data.SideEffectsDetails["@value"]}; `;

    // if (
    //   data.PsychotropicMedsDetails["@value"] &&
    //   data.PsychotropicMedsDetails["@value"] !== ""
    // )
    //   medicalSummary += `Past psychotropic nedications: ${data.PsychotropicMedsDetails["@value"]}; `;

    // if (
    //   data.MedicationComments["@value"] &&
    //   data.MedicationComments["@value"] !== ""
    // )
    //   medicalSummary += `Comments: ${data.MedicationComments["@value"]}; `;

    // if (medicalSummary !== "")
    //   summary += `Medical History/Medications:\r\n${medicalSummary.trimEnd()}\r\n\r\n`;

    // // Sexual History to Include Pertinent Sexual Issues/Concerns
    // let sexualSummary = "";
    // if (data.GenderIdentity["@value"] && data.GenderIdentity["@value"] !== "")
    //   sexualSummary += `Gender identity: ${data.GenderIdentity["@value"]}; `;

    // if (
    //   data.SexualOrientation["@value"] &&
    //   data.SexualOrientation["@value"] !== ""
    // )
    //   sexualSummary += `Sexual orientation: ${data.SexualOrientation["@value"]}; `;

    // if (data.SexualBehavior["@value"] && data.SexualBehavior["@value"] !== "")
    //   sexualSummary += `Current or historical sexual behavior: ${data.SexualBehavior["@value"]}; `;

    // if (data.SexualAbuse["@value"] && data.SexualAbuse["@value"] !== "")
    //   sexualSummary += `Current or history of sexual abuse or perpetration: ${data.SexualAbuse["@value"]}; `;

    // if (
    //   data.SexualHistoryComments["@value"] &&
    //   data.SexualHistoryComments["@value"] !== ""
    // )
    //   sexualSummary += `Comments: ${data.SexualHistoryComments["@value"]}; `;

    // if (sexualSummary !== "")
    //   summary += `Sexual History to Include Pertinent Sexual Issues/Concerns:\r\n${sexualSummary.trimEnd()}\r\n\r\n`;

    // Mental Health Treatment History
    let mentalHealthSummary = "";
    if (
      data.PastMentalHealthDiagnosis["@value"] &&
      data.PastMentalHealthDiagnosis["@value"] !== ""
    )
      mentalHealthSummary += `Previous mental health diagnosis: ${data.PastMentalHealthDiagnosis["@value"]}; `;

    if (
      data.MentalHealthHistory["@value"] &&
      data.MentalHealthHistory["@value"] !== "" &&
      data.MentalHealthHistory["@value"] !== '[{"IsEditable": true}]'
    )
      mentalHealthSummary += `Mental Health Treatment History: ${JSON.parse(
        data.MentalHealthHistory["@value"]
      )
        .filter((item) => !item.IsEditable)
        .map((item) => `${item.Agency} - ${item.Dates}`)
        .join("; ")}; `;

    if (mentalHealthSummary !== "")
      summary += `Mental Health Treatment History:\r\n${mentalHealthSummary.trimEnd()}\r\n\r\n`;

    // Alcohol/Drug Use History
    let substanceUseSummary = "";
    if (
      data.SubstanceUseHistory["@value"] &&
      data.SubstanceUseHistory["@value"] !== "" &&
      data.SubstanceUseHistory["@value"] !== '[{"IsEditable": true}]'
    )
      substanceUseSummary += `Current drug or alcohol use: ${JSON.parse(
        data.SubstanceUseHistory["@value"]
      )
        .filter((item) => !item.IsEditable)
        .map(
          (item) =>
            `Substance: ${item.Substance}, First Use: ${item.FirstUse}, Last Use: ${item.LastUse}, Frequency: ${item.Frequency}, Amount: ${item.Amount}, Method: ${item.Method}`
        )
        .join("; ")}; `;

    if (
      data.RecipientPerception["@value"] &&
      data.RecipientPerception["@value"] !== ""
    )
      substanceUseSummary += `Recipient's perception of substance use: ${data.RecipientPerception["@value"]}; `;

    if (
      data.CaregiverPerception["@value"] &&
      data.CaregiverPerception["@value"] !== ""
    )
      substanceUseSummary += `Caregiver's perception of substance use: ${data.CaregiverPerception["@value"]}; `;

    if (
      data.SubstanceUseTreatmentHistory["@value"] &&
      data.SubstanceUseTreatmentHistory["@value"] !== "" &&
      data.SubstanceUseTreatmentHistory["@value"] !== '[{"IsEditable": true}]'
    )
      substanceUseSummary += `Alcohol/Drug Treatment History: ${JSON.parse(
        data.SubstanceUseTreatmentHistory["@value"]
      )
        .filter((item) => !item.IsEditable)
        .map((item) => `Agency: ${item.Agency}, Dates: ${item.Dates}`)
        .join("; ")}; `;

    if (
      data.SubstanceUseTreatmentComments["@value"] &&
      data.SubstanceUseTreatmentComments["@value"] !== ""
    )
      substanceUseSummary += `Comments: ${data.SubstanceUseTreatmentComments["@value"]}; `;

    if (substanceUseSummary !== "")
      summary += `Alcohol/Drug Use History:\r\n${substanceUseSummary.trimEnd()}\r\n\r\n`;

    // Trauma History
    let traumaSummary = "";
    if (
      data.TraumaHistory["@value"] &&
      data.TraumaHistory["@value"] !== "" &&
      data.TraumaHistory["@value"] !== '[{"IsEditable": true}]'
    )
      traumaSummary += `${JSON.parse(data.TraumaHistory["@value"])
        .filter((item) => !item.IsEditable)
        .map((item) => `${item.Trauma}, ${item.Status}, ${item.Comments}`)
        .join(". ")}; `;

    if (
      data.CopingStrategies["@value"] &&
      data.CopingStrategies["@value"] !== ""
    )
      traumaSummary += `Coping strategies: ${data.CopingStrategies["@value"]}; `;

    if (
      data.RecipientTraumaPerspective["@value"] &&
      data.RecipientTraumaPerspective["@value"] !== ""
    )
      traumaSummary += `Recipient's perspective of trauma: ${data.RecipientTraumaPerspective["@value"]}; `;

    if (
      data.CaregiverTraumaPerspective["@value"] &&
      data.CaregiverTraumaPerspective["@value"] !== ""
    )
      traumaSummary += `Caregiver's perspective of trauma: ${data.CaregiverTraumaPerspective["@value"]}; `;

    if (traumaSummary !== "")
      summary += `Trauma History:\r\n${traumaSummary.trimEnd()}\r\n\r\n`;

    // // Mental Status
    // let mentalStatusSummary = "";
    // let appearanceSummary = "";
    // let behaviorSummary = "";
    // let speechSummary = "";
    // let moodSummary = "";
    // let affectSummary = "";
    // let perceptionSummary = "";
    // let cognitionSummary = "";
    // let insightSummary = "";

    // if (
    //   data.AppropriateAppearance["@value"] &&
    //   data.AppropriateAppearance["@value"] === "true"
    // )
    //   appearanceSummary += "Appropriate, ";

    // if (data.Neat["@value"] && data.Neat["@value"] === "true")
    //   appearanceSummary += "Neat, ";

    // if (data.Disheveled["@value"] && data.Disheveled["@value"] === "true")
    //   appearanceSummary += "Disheveled, ";

    // if (
    //   data.BizarreAppearance["@value"] &&
    //   data.BizarreAppearance["@value"] === "true"
    // )
    //   appearanceSummary += "Bizarre, ";

    // if (
    //   data.OtherAppearance["@value"] &&
    //   data.OtherAppearance["@value"] === "true" &&
    //   data.OtherAppearanceDetails["@value"] &&
    //   data.OtherAppearanceDetails["@value"] !== ""
    // )
    //   appearanceSummary += `Other: ${data.OtherAppearanceDetails["@value"]}, `;

    // if (appearanceSummary !== "")
    //   mentalStatusSummary += `\r\nAppearance: ${appearanceSummary.slice(
    //     0,
    //     -2
    //   )}; `;

    // if (data.Cooperative["@value"] && data.Cooperative["@value"] === "true")
    //   behaviorSummary += "Cooperative, ";

    // if (data.Guarded["@value"] && data.Guarded["@value"] === "true")
    //   behaviorSummary += "Guarded, ";

    // if (data.Hyperactive["@value"] && data.Hyperactive["@value"] === "true")
    //   behaviorSummary += "Hyperactive, ";

    // if (data.Agitated["@value"] && data.Agitated["@value"] === "true")
    //   behaviorSummary += "Agitated, ";

    // if (data.Paranoid["@value"] && data.Paranoid["@value"] === "true")
    //   behaviorSummary += "Paranoid, ";

    // if (data.Tics["@value"] && data.Tics["@value"] === "true")
    //   behaviorSummary += "Tics, ";

    // if (data.Impulsive["@value"] && data.Impulsive["@value"] === "true")
    //   behaviorSummary += "Impulsive, ";

    // if (data.Aggressive["@value"] && data.Aggressive["@value"] === "true")
    //   behaviorSummary += "Aggressive, ";

    // if (data.Withdrawn["@value"] && data.Withdrawn["@value"] === "true")
    //   behaviorSummary += "Withdrawn, ";

    // if (
    //   data.BizarreBehavior["@value"] &&
    //   data.BizarreBehavior["@value"] === "true"
    // )
    //   behaviorSummary += "Bizarre, ";

    // if (data.Rigidity["@value"] && data.Rigidity["@value"] === "true")
    //   behaviorSummary += "Rigidity, ";

    // if (
    //   data.OtherBehavior["@value"] &&
    //   data.OtherBehavior["@value"] === "true" &&
    //   data.OtherBehaviorDetails["@value"] &&
    //   data.OtherBehaviorDetails["@value"] !== ""
    // )
    //   behaviorSummary += `Other: ${data.OtherBehaviorDetails["@value"]}, `;

    // if (behaviorSummary !== "")
    //   mentalStatusSummary += `\r\nBehavior: ${behaviorSummary.slice(0, -2)}; `;

    // if (data.Normal["@value"] && data.Normal["@value"] === "true")
    //   speechSummary += "Normal, ";

    // if (data.Slurred["@value"] && data.Slurred["@value"] === "true")
    //   speechSummary += "Slurred, ";

    // if (data.Pressured["@value"] && data.Pressured["@value"] === "true")
    //   speechSummary += "Pressured, ";

    // if (data.Tangential["@value"] && data.Tangential["@value"] === "true")
    //   speechSummary += "Tangential, ";

    // if (data.Incoherent["@value"] && data.Incoherent["@value"] === "true")
    //   speechSummary += "Incoherent, ";

    // if (data.Stutters["@value"] && data.Stutters["@value"] === "true")
    //   speechSummary += "Stutters, ";

    // if (
    //   data.OtherSpeech["@value"] &&
    //   data.OtherSpeech["@value"] === "true" &&
    //   data.OtherSpeechDetails["@value"] &&
    //   data.OtherSpeechDetails["@value"] !== ""
    // )
    //   speechSummary += `Other: ${data.OtherSpeechDetails["@value"]}, `;

    // if (speechSummary !== "")
    //   mentalStatusSummary += `\r\nSpeech: ${speechSummary.slice(0, -2)}; `;

    // if (data.Euthymic["@value"] && data.Euthymic["@value"] === "true")
    //   moodSummary += "Euthymic, ";

    // if (data.Anxious["@value"] && data.Anxious["@value"] === "true")
    //   moodSummary += "Anxious, ";

    // if (data.Angry["@value"] && data.Angry["@value"] === "true")
    //   moodSummary += "Angry, ";

    // if (data.Depressed["@value"] && data.Depressed["@value"] === "true")
    //   moodSummary += "Depressed, ";

    // if (data.Euphoric["@value"] && data.Euphoric["@value"] === "true")
    //   moodSummary += "Euphoric, ";

    // if (data.Irritable["@value"] && data.Irritable["@value"] === "true")
    //   moodSummary += "Irritable, ";

    // if (data.Fearful["@value"] && data.Fearful["@value"] === "true")
    //   moodSummary += "Fearful, ";

    // if (
    //   data.OtherMood["@value"] &&
    //   data.OtherMood["@value"] === "true" &&
    //   data.OtherMoodDetails["@value"] &&
    //   data.OtherMoodDetails["@value"] !== ""
    // )
    //   moodSummary += `Other: ${data.OtherMoodDetails["@value"]}, `;

    // if (moodSummary !== "")
    //   mentalStatusSummary += `\r\nMood: ${moodSummary.slice(0, -2)}; `;

    // if (data.FullRange["@value"] && data.FullRange["@value"] === "true")
    //   affectSummary += "Full range, ";

    // if (data.Restricted["@value"] && data.Restricted["@value"] === "true")
    //   affectSummary += "Restricted, ";

    // if (data.Blunted["@value"] && data.Blunted["@value"] === "true")
    //   affectSummary += "Blunted, ";

    // if (data.Flat["@value"] && data.Flat["@value"] === "true")
    //   affectSummary += "Flat, ";

    // if (data.Inappropriate["@value"] && data.Inappropriate["@value"] === "true")
    //   affectSummary += "Inappropriate, ";

    // if (
    //   data.OtherAffect["@value"] &&
    //   data.OtherAffect["@value"] === "true" &&
    //   data.OtherAffectDetails["@value"] &&
    //   data.OtherAffectDetails["@value"] !== ""
    // )
    //   affectSummary += `Other: ${data.OtherAffectDetails["@value"]}, `;

    // if (affectSummary !== "")
    //   mentalStatusSummary += `\r\nAffect: ${affectSummary.slice(0, -2)}; `;

    // if (data.Logical["@value"] && data.Logical["@value"] === "true")
    //   perceptionSummary += "Logical, ";

    // if (
    //   data.Preoccupations["@value"] &&
    //   data.Preoccupations["@value"] === "true"
    // )
    //   perceptionSummary += "Preoccupations, ";

    // if (data.Delusions["@value"] && data.Delusions["@value"] === "true")
    //   perceptionSummary += "Delusions, ";

    // if (
    //   data.Hallucinations["@value"] &&
    //   data.Hallucinations["@value"] === "true"
    // )
    //   perceptionSummary += "Hallucinations, ";

    // if (data.Racing["@value"] && data.Racing["@value"] === "true")
    //   perceptionSummary += "Racing, ";

    // if (data.Disorganized["@value"] && data.Disorganized["@value"] === "true")
    //   perceptionSummary += "Disorganized, ";

    // if (data.Concrete["@value"] && data.Concrete["@value"] === "true")
    //   perceptionSummary += "Concrete, ";

    // if (
    //   data.SuicidalIdeation["@value"] &&
    //   data.SuicidalIdeation["@value"] === "true"
    // )
    //   perceptionSummary += "Suicidal Ideation, ";

    // if (
    //   data.HomicidalIdeation["@value"] &&
    //   data.HomicidalIdeation["@value"] === "true"
    // )
    //   perceptionSummary += "Homicidal Ideation, ";

    // if (
    //   data.OtherPerception["@value"] &&
    //   data.OtherPerception["@value"] === "true" &&
    //   data.OtherPerceptionDetails["@value"] &&
    //   data.OtherPerceptionDetails["@value"] !== ""
    // )
    //   perceptionSummary += `Other: ${data.OtherPerceptionDetails["@value"]}, `;

    // if (perceptionSummary !== "")
    //   mentalStatusSummary += `\r\nThought Process/Perception: ${perceptionSummary.slice(
    //     0,
    //     -2
    //   )}; `;

    // if (data.NoImpairment["@value"] && data.NoImpairment["@value"] === "true")
    //   cognitionSummary += "No Impairment, ";

    // if (
    //   data.ShortAttentionSpan["@value"] &&
    //   data.ShortAttentionSpan["@value"] === "true"
    // )
    //   cognitionSummary += "Short Attention Span, ";

    // if (
    //   data.DifficultyConcentrating["@value"] &&
    //   data.DifficultyConcentrating["@value"] === "true"
    // )
    //   cognitionSummary += "Difficulty Concentrating, ";

    // if (
    //   data.Distractibility["@value"] &&
    //   data.Distractibility["@value"] === "true"
    // )
    //   cognitionSummary += "Distractibility, ";

    // if (
    //   data.MemoryImpairment["@value"] &&
    //   data.MemoryImpairment["@value"] === "true"
    // )
    //   cognitionSummary += "Memory Impairment, ";

    // if (
    //   data.LowIntelligence["@value"] &&
    //   data.LowIntelligence["@value"] === "true"
    // )
    //   cognitionSummary += "Low Intelligence, ";

    // if (
    //   data.OtherCognition["@value"] &&
    //   data.OtherCognition["@value"] === "true" &&
    //   data.OtherCognitionDetails["@value"] &&
    //   data.OtherCognitionDetails["@value"] !== ""
    // )
    //   cognitionSummary += `Other: ${data.OtherCognitionDetails["@value"]}, `;

    // if (cognitionSummary !== "")
    //   mentalStatusSummary += `\r\nCognition/Intellectual Functioning: ${cognitionSummary.slice(
    //     0,
    //     -2
    //   )}; `;

    // if (
    //   data.AppropriateInsight["@value"] &&
    //   data.AppropriateInsight["@value"] === "true"
    // )
    //   insightSummary += "Appropriate, ";

    // if (data.Fair["@value"] && data.Fair["@value"] === "true")
    //   insightSummary += "Fair, ";

    // if (data.Impaired["@value"] && data.Impaired["@value"] === "true")
    //   insightSummary += "Impaired, ";

    // if (
    //   data.OtherInsight["@value"] &&
    //   data.OtherInsight["@value"] === "true" &&
    //   data.OtherInsightDetails["@value"] &&
    //   data.OtherInsightDetails["@value"] !== ""
    // )
    //   insightSummary += `Other: ${data.OtherInsightDetails["@value"]}, `;

    // if (insightSummary !== "")
    //   mentalStatusSummary += `\r\nInsight/Judgment: ${insightSummary.slice(
    //     0,
    //     -2
    //   )}; `;

    // if (
    //   data.MentalStatusComments["@value"] &&
    //   data.MentalStatusComments["@value"] !== ""
    // )
    //   mentalStatusSummary += `\r\nComments/Observations: ${data.MentalStatusComments["@value"]}; `;

    // if (mentalStatusSummary !== "")
    //   summary += `Mental Status:${mentalStatusSummary.trimEnd()}\r\n\r\n`;

    // Lethality
    let lethalitySummary = "";
    if (
      data.SelfAbusiveBehaviorDetails["@value"] &&
      data.SelfAbusiveBehaviorDetails["@value"] !== ""
    )
      lethalitySummary += `Self-injurious or self-abusive behavior: ${data.SelfAbusiveBehaviorDetails["@value"]}; `;

    if (
      data.SuicidalThreatsDetails["@value"] &&
      data.SuicidalThreatsDetails["@value"] !== ""
    )
      lethalitySummary += `Suicidal threats/gestures: ${data.SuicidalThreatsDetails["@value"]}; `;

    if (
      data.SuicideAttemptDetails["@value"] &&
      data.SuicideAttemptDetails["@value"] !== ""
    )
      lethalitySummary += `Suicide attempt: ${data.SuicideAttemptDetails["@value"]}; `;

    const riskFactors = getSelfHarmRiskFactors();

    if (riskFactors !== "")
      lethalitySummary += `Risk Factors: ${riskFactors}; `;

    const selfHarmProtectiveFactors = getSelfHarmProtectiveFactors();

    if (selfHarmProtectiveFactors !== "")
      lethalitySummary += `Protective Factors: ${selfHarmProtectiveFactors}; `;

    const warningSigns = getSelfHarmWarningSigns();

    if (warningSigns !== "")
      lethalitySummary += `Current Warning Signs: ${warningSigns}; `;

    if (
      data.CurrentSelfHarmRisk["@value"] &&
      data.CurrentSelfHarmRisk["@value"] !== ""
    ) {
      const selfHarmRisk = riskOptions.filter(
        (option) => option._id === data.CurrentSelfHarmRisk["@value"]
      )[0];
      lethalitySummary += `Current Risk of Harm to Self: ${selfHarmRisk.name}; `;

      if (
        data.CurrentSelfHarmRiskComments["@value"] &&
        data.CurrentSelfHarmRiskComments["@value"] !== ""
      )
        lethalitySummary += `Comments/Plan: ${data.CurrentSelfHarmRiskComments["@value"]}; `;
    }

    if (
      data.ThreatsToHarmOthersDetails["@value"] &&
      data.ThreatsToHarmOthersDetails["@value"] !== ""
    )
      lethalitySummary += `Threats to harm/endanger others: ${data.ThreatsToHarmOthersDetails["@value"]}; `;

    if (
      data.ThoughtsOfHarmDetails["@value"] &&
      data.ThoughtsOfHarmDetails["@value"] !== ""
    )
      lethalitySummary += `Thoughts of harming/endangering others: ${data.ThoughtsOfHarmDetails["@value"]}; `;

    if (
      data.ActsOfHarmDetails["@value"] &&
      data.ActsOfHarmDetails["@value"] !== ""
    )
      lethalitySummary += `Acts of harm/endangering others: ${data.ActsOfHarmDetails["@value"]}; `;

    if (
      data.ActsOfHarmAnimalsDetails["@value"] &&
      data.ActsOfHarmAnimalsDetails["@value"] !== ""
    )
      lethalitySummary += `Acts of harm/endangering animals: ${data.ActsOfHarmAnimalsDetails["@value"]}; `;

    if (
      data.ActsInvolvingWeaponsDetails["@value"] &&
      data.ActsInvolvingWeaponsDetails["@value"] !== ""
    )
      lethalitySummary += `Acts involving weapons: ${data.ActsInvolvingWeaponsDetails["@value"]}; `;

    if (
      data.CurrentOtherHarmRisk["@value"] &&
      data.CurrentOtherHarmRisk["@value"] !== ""
    ) {
      const othersHarmRisk = riskOptions.filter(
        (option) => option._id === data.CurrentOtherHarmRisk["@value"]
      )[0];
      lethalitySummary += `Current Risk of Harm to Others: ${othersHarmRisk.name}; `;

      if (
        data.CurrentOtherHarmRiskDetails["@value"] &&
        data.CurrentOtherHarmRiskDetails["@value"] !== ""
      )
        lethalitySummary += `Comments/Plan: ${data.CurrentOtherHarmRiskDetails["@value"]}; `;
    }

    if (lethalitySummary !== "")
      summary += `Lethality:\r\n${lethalitySummary.trimEnd()}\r\n\r\n`;

    // Symptom Checklist
    let symptomSummary = "";

    if (
      (data.TraumaSymptomsFrequency["@value"] &&
        data.TraumaSymptomsFrequency["@value"] !== "") ||
      (data.TraumaSymptomsIntensity["@value"] &&
        data.TraumaSymptomsIntensity["@value"] !== "") ||
      (data.TraumaSymptomsDuration["@value"] &&
        data.TraumaSymptomsDuration["@value"] !== "") ||
      (data.TraumaSymptomsOnset["@value"] &&
        data.TraumaSymptomsOnset["@value"] !== "")
    )
      symptomSummary += `Trauma Symptoms: ${data.TraumaSymptomsFrequency["@value"]}, ${data.TraumaSymptomsIntensity["@value"]}, ${data.TraumaSymptomsDuration["@value"]}, ${data.TraumaSymptomsOnset["@value"]}; `;
    if (
      (data.AnxietyDisorderFrequency["@value"] &&
        data.AnxietyDisorderFrequency["@value"] !== "") ||
      (data.AnxietyDisorderIntensity["@value"] &&
        data.AnxietyDisorderIntensity["@value"] !== "") ||
      (data.AnxietyDisorderDuration["@value"] &&
        data.AnxietyDisorderDuration["@value"] !== "")
    )
      symptomSummary += `Anxiety Disorder: ${data.AnxietyDisorderFrequency["@value"]}, ${data.AnxietyDisorderIntensity["@value"]}, ${data.AnxietyDisorderDuration["@value"]};`;
    if (
      (data.DepressionFrequency["@value"] &&
        data.DepressionFrequency["@value"] !== "") ||
      (data.DepressionIntensity["@value"] &&
        data.DepressionIntensity["@value"] !== "") ||
      (data.DepressionDuration["@value"] &&
        data.DepressionDuration["@value"] !== "")
    )
      symptomSummary += `Depression: ${data.DepressionFrequency["@value"]}, ${data.DepressionIntensity["@value"]}, ${data.DepressionDuration["@value"]};`;
    if (
      (data.AttentionHyperactivityFrequency["@value"] &&
        data.AttentionHyperactivityFrequency["@value"] !== "") ||
      (data.AttentionHyperactivityIntensity["@value"] &&
        data.AttentionHyperactivityIntensity["@value"] !== "") ||
      (data.AttentionHyperactivityDuration["@value"] &&
        data.AttentionHyperactivityDuration["@value"] !== "")
    )
      symptomSummary += `Attention Hyperactivity: ${data.AttentionHyperactivityFrequency["@value"]}, ${data.AttentionHyperactivityIntensity["@value"]}, ${data.AttentionHyperactivityDuration["@value"]};`;
    if (
      (data.SubstanceUseFrequency["@value"] &&
        data.SubstanceUseFrequency["@value"] !== "") ||
      (data.SubstanceUseIntensity["@value"] &&
        data.SubstanceUseIntensity["@value"] !== "") ||
      (data.SubstanceUseDuration["@value"] &&
        data.SubstanceUseDuration["@value"] !== "")
    )
      symptomSummary += `Substance Use: ${data.SubstanceUseFrequency["@value"]}, ${data.SubstanceUseIntensity["@value"]}, ${data.SubstanceUseDuration["@value"]};`;
    if (
      (data.DMDDFrequency["@value"] && data.DMDDFrequency["@value"] !== "") ||
      (data.DMDDIntensity["@value"] && data.DMDDIntensity["@value"] !== "") ||
      (data.DMDDDuration["@value"] && data.DMDDDuration["@value"] !== "")
    )
      symptomSummary += `DMDD: ${data.DMDDFrequency["@value"]}, ${data.DMDDIntensity["@value"]}, ${data.DMDDDuration["@value"]};`;
    if (
      (data.ConductDisorderFrequency["@value"] &&
        data.ConductDisorderFrequency["@value"] !== "") ||
      (data.ConductDisorderIntensity["@value"] &&
        data.ConductDisorderIntensity["@value"] !== "") ||
      (data.ConductDisorderDuration["@value"] &&
        data.ConductDisorderDuration["@value"] !== "")
    )
      symptomSummary += `Conduct Disorder: ${data.ConductDisorderFrequency["@value"]}, ${data.ConductDisorderIntensity["@value"]}, ${data.ConductDisorderDuration["@value"]};`;
    if (
      (data.ODDFrequency["@value"] && data.ODDFrequency["@value"] !== "") ||
      (data.ODDIntensity["@value"] && data.ODDIntensity["@value"] !== "") ||
      (data.ODDDuration["@value"] && data.ODDDuration["@value"] !== "")
    )
      symptomSummary += `ODD: ${data.ODDFrequency["@value"]}, ${data.ODDIntensity["@value"]}, ${data.ODDDuration["@value"]};`;
    if (
      (data.OCDFrequency["@value"] && data.OCDFrequency["@value"] !== "") ||
      (data.OCDIntensity["@value"] && data.OCDIntensity["@value"] !== "") ||
      (data.OCDDuration["@value"] && data.OCDDuration["@value"] !== "")
    )
      symptomSummary += `OCD: ${data.OCDFrequency["@value"]}, ${data.OCDIntensity["@value"]}, ${data.OCDDuration["@value"]};`;
    if (
      (data.PanicDisorderFrequency["@value"] &&
        data.PanicDisorderFrequency["@value"] !== "") ||
      (data.PanicDisorderIntensity["@value"] &&
        data.PanicDisorderIntensity["@value"] !== "") ||
      (data.PanicDisorderDuration["@value"] &&
        data.PanicDisorderDuration["@value"] !== "")
    )
      symptomSummary += `Panic Disorder: ${data.PanicDisorderFrequency["@value"]}, ${data.PanicDisorderIntensity["@value"]}, ${data.PanicDisorderDuration["@value"]};`;
    if (
      (data.PhobiasFrequency["@value"] &&
        data.PhobiasFrequency["@value"] !== "") ||
      (data.PhobiasIntensity["@value"] &&
        data.PhobiasIntensity["@value"] !== "") ||
      (data.PhobiasDuration["@value"] && data.PhobiasDuration["@value"] !== "")
    )
      symptomSummary += `Phobias: ${data.PhobiasFrequency["@value"]}, ${data.PhobiasIntensity["@value"]}, ${data.PhobiasDuration["@value"]};`;
    if (
      (data.OtherSymptomsFrequency["@value"] &&
        data.OtherSymptomsFrequency["@value"] !== "") ||
      (data.OtherSymptomsIntensity["@value"] &&
        data.OtherSymptomsIntensity["@value"] !== "") ||
      (data.OtherSymptomsDuration["@value"] &&
        data.OtherSymptomsDuration["@value"] !== "")
    )
      symptomSummary += `Other Symptoms: ${data.OtherSymptomsFrequency["@value"]}, ${data.OtherSymptomsIntensity["@value"]}, ${data.OtherSymptomsDuration["@value"]};`;

    if (symptomSummary !== "")
      summary += `Symptom Checklist:\r\n${symptomSummary.trimEnd()}\r\n\r\n`;

    // Diagnosis
    // if (
    //   data.DiagnosisDetails["@value"] &&
    //   data.DiagnosisDetails["@value"] !== ""
    // )
    //   summary += `Diagnosis:\r\n${JSON.parse(data.DiagnosisDetails["@value"])
    //     .map(
    //       (item) => `ICD-10 Code: ${item.Code}, Diagnosis: ${item.Diagnosis}`
    //     )
    //     .join("; ")};\r\n\r\n`;

    // //Treatment Recommendations/Needs
    // if (
    //   data.TreatmentRecommendations["@value"] &&
    //   data.TreatmentRecommendations["@value"] !== ""
    // )
    //   summary += `Treatment Recommendations:\r\n${JSON.parse(
    //     data.TreatmentRecommendations["@value"]
    //   )
    //     .map(
    //       (item) =>
    //         `Recommendation: ${item.Recommendation}, ${
    //           item.Deferred === "true"
    //             ? "Status: SAFY will be providing, "
    //             : item.Referred === "true"
    //             ? "Status: SAFY will be referring to another agency/individual, "
    //             : item.Declined === "true"
    //             ? "Status: Declined by Recipient, "
    //             : ""
    //         }Reason: ${item.Reason}`
    //     )
    //     .join("; ")}; `;

    // Clinical Narrative Summary Comments
    if (
      data.ClinicalNarrativeSummaryComments["@value"] &&
      data.ClinicalNarrativeSummaryComments["@value"] !== ""
    )
      summary += `Additional Comments:\r\n${data.ClinicalNarrativeSummaryComments["@value"]}`;

    summary = summary.trimEnd();

    const obj = { ...data };
    obj.ClinicalNarrativeSummary["@value"] = summary;
    setData(obj);
  };

  const getSelfHarmRiskFactors = () => {
    let riskFactors = "";

    if (data.PriorAttempt["@value"] === "true")
      riskFactors += "Prior suicide attempt(s), ";

    if (data.FamilyHistory["@value"] === "true")
      riskFactors += "Family history of suicide, ";

    if (data.AccessToMeans["@value"] === "true")
      riskFactors += "Access to lethal means, ";

    if (data.Exposure["@value"] === "true")
      riskFactors += "Exposure to another person's suicide, ";

    if (data.HealthCondition["@value"] === "true")
      riskFactors += "Serious or chronic health condition and/or pain, ";

    if (data.ElevatingBeliefs["@value"] === "true")
      riskFactors += "Cultural or religious beliefs elevating suicide, ";

    if (data.MentalHealthDisorder["@value"] === "true")
      riskFactors += "Mental health disorder, ";

    if (
      data.OtherRiskFactors !== undefined &&
      data.OtherRiskFactors["@value"] === "true"
    )
      riskFactors += `${
        data.OtherRiskFactorsDetails &&
        data.OtherRiskFactorsDetails["@value"] !== ""
          ? `${data.OtherRiskFactorsDetails["@value"]}, `
          : ""
      }`;

    if (
      data.NoRiskFactors !== undefined &&
      data.NoRiskFactors["@value"] === "true"
    )
      riskFactors += "None reported, ";

    return riskFactors.slice(0, -2);
  };

  const getSelfHarmProtectiveFactors = () => {
    let protectiveFactors = "";

    if (data.ClinicalCare["@value"] === "true")
      protectiveFactors +=
        "Effective clinical care for mental, physical, and substance abuse disorders, ";

    if (data.Connected["@value"] === "true")
      protectiveFactors +=
        "Connectedness to individuals, family, community, and social institutions, ";

    if (data.Skills["@value"] === "true")
      protectiveFactors +=
        "Skills in problem solving, conflict resolution, and nonviolent ways of handling disputes, ";

    if (data.CaregiverContacts["@value"] === "true")
      protectiveFactors += "Contacts with caregivers, ";

    if (data.DiscouragingBeliefs["@value"] === "true")
      protectiveFactors +=
        "Cultural and religious beliefs that discourage suicide, ";

    if (
      data.NoSelfHarmProtectiveFactors !== undefined &&
      data.NoSelfHarmProtectiveFactors["@value"] === "true"
    )
      protectiveFactors += "None reported, ";

    return protectiveFactors.slice(0, -2);
  };

  const getSelfHarmWarningSigns = () => {
    let warningSigns = "";

    if (data.Threatening["@value"] === "true")
      warningSigns += "Threatening to hurt or kill oneself, ";

    if (data.SayingGoodbye["@value"] === "true")
      warningSigns += "Visiting or calling people to say goodbye, ";

    if (data.SeekingMeans["@value"] === "true")
      warningSigns += "Seeking a means to kill oneself, ";

    if (data.SocialWithdrawal["@value"] === "true")
      warningSigns += "Social withdrawal/isolation, ";

    if (data.Hopelessness["@value"] === "true")
      warningSigns += "Hopelessness, ";

    if (data.RecklessBehavior["@value"] === "true")
      warningSigns += "Reckless behavior/increase in risky activities, ";

    if (data.IncreasedSubstanceUse["@value"] === "true")
      warningSigns += "Increasing alcohol or drug use, ";

    if (data.Feelings["@value"] === "true")
      warningSigns +=
        "Feelings of alienation, loneliness, guilt, shame, or inadequacy, ";

    if (data.DramaticMoodChanges["@value"] === "true")
      warningSigns += "Dramatic mood changes, ";

    if (data.GivingAwayPossessions["@value"] === "true")
      warningSigns += "Giving away prized possessions, ";

    if (
      data.NoWarningSigns !== undefined &&
      data.NoWarningSigns["@value"] === "true"
    )
      warningSigns += "None reported, ";

    return warningSigns.slice(0, -2);
  };

  const customValidate = (errs) => {
    if (
      (!data.ISRecipient["@value"] || data.ISRecipient["@value"] === "false") &&
      (!data.ISParent["@value"] || data.ISParent["@value"] === "false") &&
      (!data.ISFamily["@value"] || data.ISFamily["@value"] === "false") &&
      (!data.ISGuardian["@value"] || data.ISGuardian["@value"] === "false") &&
      (!data.ISCaregiver["@value"] || data.ISCaregiver["@value"] === "false") &&
      (!data.ISPhysician["@value"] || data.ISPhysician["@value"] === "false") &&
      (!data.ISRecords["@value"] || data.ISRecords["@value"] === "false") &&
      (!data.ISServiceProvider["@value"] ||
        data.ISServiceProvider["@value"] === "false") &&
      (!data.ISSchool["@value"] || data.ISSchool["@value"] === "false") &&
      (!data.ISCourt["@value"] || data.ISCourt["@value"] === "false") &&
      (!data.ISDHR["@value"] || data.ISDHR["@value"] === "false") &&
      (!data.ISOther["@value"] || data.ISOther["@value"] === "false")
    ) {
      if (errs)
        errs.InformationSources =
          "At least one Information Source is required.";
      else
        errs = {
          InformationSources: "At least one Information Source is required.",
        };
    }

    if (data.TraumaNA["@value"] === "false") {
      if (
        (!data.DistressingMemories["@value"] ||
          data.DistressingMemories["@value"] === "false") &&
        (!data.LossOfMemories["@value"] ||
          data.LossOfMemories["@value"] === "false") &&
        (!data.Nightmares["@value"] || data.Nightmares["@value"] === "false") &&
        (!data.Flashbacks["@value"] || data.Flashbacks["@value"] === "false") &&
        (!data.RepetitivePlayOfEvents["@value"] ||
          data.RepetitivePlayOfEvents["@value"] === "false") &&
        (!data.Dissocation["@value"] ||
          data.Dissocation["@value"] === "false") &&
        (!data.BlockOut["@value"] || data.BlockOut["@value"] === "false") &&
        (!data.Hyperarousal["@value"] ||
          data.Hyperarousal["@value"] === "false") &&
        (!data.Avoidant["@value"] || data.Avoidant["@value"] === "false") &&
        (!data.Detachment["@value"] || data.Detachment["@value"] === "false") &&
        (!data.PsychologicalDistress["@value"] ||
          data.PsychologicalDistress["@value"] === "false") &&
        (!data.NegativeCognitions["@value"] ||
          data.NegativeCognitions["@value"] === "false") &&
        (!data.Hypervigilance["@value"] ||
          data.Hypervigilance["@value"] === "false") &&
        (!data.SleepDisturbances["@value"] ||
          data.SleepDisturbances["@value"] === "false") &&
        (!data.DifficultyConcentratingSymptom["@value"] ||
          data.DifficultyConcentratingSymptom["@value"] === "false") &&
        (!data.EmotionalDysregulation["@value"] ||
          data.EmotionalDysregulation["@value"] === "false") &&
        (!data.DiminishedInterests["@value"] ||
          data.DiminishedInterests["@value"] === "false") &&
        (!data.SocialWithdrawalSymptom["@value"] ||
          data.SocialWithdrawalSymptom["@value"] === "false") &&
        (!data.PhysiologicalResponses["@value"] ||
          data.PhysiologicalResponses["@value"] === "false") &&
        (!data.DevelopmentalRegression["@value"] ||
          data.DevelopmentalRegression["@value"] === "false") &&
        (!data.UnhealthyBoundaries["@value"] ||
          data.UnhealthyBoundaries["@value"] === "false") &&
        (!data.NoSymptoms["@value"] || data.NoSymptoms["@value"] === "false")
      ) {
        if (errs)
          errs.TraumaSymptomsResponse =
            "At least one Trauma Symptom/Response is required.";
        else
          errs = {
            TraumaSymptomsResponse:
              "At least one Trauma Symptom/Response is required.",
          };
      }

      if (
        (!data.SocialSupports["@value"] ||
          data.SocialSupports["@value"] === "false") &&
        (!data.InterpersonalSkills["@value"] ||
          data.InterpersonalSkills["@value"] === "false") &&
        (!data.ValidatingExperiences["@value"] ||
          data.ValidatingExperiences["@value"] === "false") &&
        (!data.Adaptability["@value"] ||
          data.Adaptability["@value"] === "false") &&
        (!data.SenseOfMastery["@value"] ||
          data.SenseOfMastery["@value"] === "false") &&
        (!data.PositiveOutlook["@value"] ||
          data.PositiveOutlook["@value"] === "false") &&
        (!data.Resources["@value"] || data.Resources["@value"] === "false") &&
        (!data.SecureAttachments["@value"] ||
          data.SecureAttachments["@value"] === "false") &&
        (!data.CaregiverStability["@value"] ||
          data.CaregiverStability["@value"] === "false") &&
        (!data.PlacementStability["@value"] ||
          data.PlacementStability["@value"] === "false") &&
        (!data.TiesToExtendedFamily["@value"] ||
          data.TiesToExtendedFamily["@value"] === "false") &&
        (!data.StrongCulturalIdentity["@value"] ||
          data.StrongCulturalIdentity["@value"] === "false") &&
        (!data.StrongSpiritualBeliefs["@value"] ||
          data.StrongSpiritualBeliefs["@value"] === "false") &&
        (!data.Pets["@value"] || data.Pets["@value"] === "false") &&
        (!data.NoTraumaProtectiveFactors["@value"] ||
          data.NoTraumaProtectiveFactors["@value"] === "false")
      ) {
        if (errs)
          errs.TraumaProtectiveFactors =
            "At least one Protective Factor is required.";
        else
          errs = {
            TraumaProtectiveFactors:
              "At least one Protective Factor is required.",
          };
      }
    }

    if (
      (!data.AppropriateAppearance["@value"] ||
        data.AppropriateAppearance["@value"] === "false") &&
      (!data.Neat["@value"] || data.Neat["@value"] === "false") &&
      (!data.Disheveled["@value"] || data.Disheveled["@value"] === "false") &&
      (!data.BizarreAppearance["@value"] ||
        data.BizarreAppearance["@value"] === "false") &&
      (!data.OtherAppearance["@value"] ||
        data.OtherAppearance["@value"] === "false")
    ) {
      if (errs) errs.MSAppearance = "At least one value is required.";
      else
        errs = {
          MSAppearance: "At least one value is required.",
        };
    }

    if (
      (!data.Cooperative["@value"] || data.Cooperative["@value"] === "false") &&
      (!data.Guarded["@value"] || data.Guarded["@value"] === "false") &&
      (!data.Hyperactive["@value"] || data.Hyperactive["@value"] === "false") &&
      (!data.Agitated["@value"] || data.Agitated["@value"] === "false") &&
      (!data.Paranoid["@value"] || data.Paranoid["@value"] === "false") &&
      (!data.Tics["@value"] || data.Tics["@value"] === "false") &&
      (!data.Impulsive["@value"] || data.Impulsive["@value"] === "false") &&
      (!data.Aggressive["@value"] || data.Aggressive["@value"] === "false") &&
      (!data.Withdrawn["@value"] || data.Withdrawn["@value"] === "false") &&
      (!data.BizarreBehavior["@value"] ||
        data.BizarreBehavior["@value"] === "false") &&
      (!data.Rigidity["@value"] || data.Rigidity["@value"] === "false") &&
      (!data.OtherBehavior["@value"] ||
        data.OtherBehavior["@value"] === "false")
    ) {
      if (errs) errs.MSBehavior = "At least one value is required.";
      else
        errs = {
          MSBehavior: "At least one value is required.",
        };
    }

    if (
      (!data.Normal["@value"] || data.Normal["@value"] === "false") &&
      (!data.Slurred["@value"] || data.Slurred["@value"] === "false") &&
      (!data.Pressured["@value"] || data.Pressured["@value"] === "false") &&
      (!data.Tangential["@value"] || data.Tangential["@value"] === "false") &&
      (!data.Incoherent["@value"] || data.Incoherent["@value"] === "false") &&
      (!data.Stutters["@value"] || data.Stutters["@value"] === "false") &&
      (!data.OtherSpeech["@value"] || data.OtherSpeech["@value"] === "false")
    ) {
      if (errs) errs.MSSpeech = "At least one value is required.";
      else
        errs = {
          MSSpeech: "At least one value is required.",
        };
    }

    if (
      (!data.Euthymic["@value"] || data.Euthymic["@value"] === "false") &&
      (!data.Anxious["@value"] || data.Anxious["@value"] === "false") &&
      (!data.Angry["@value"] || data.Angry["@value"] === "false") &&
      (!data.Depressed["@value"] || data.Depressed["@value"] === "false") &&
      (!data.Euphoric["@value"] || data.Euphoric["@value"] === "false") &&
      (!data.Irritable["@value"] || data.Irritable["@value"] === "false") &&
      (!data.Fearful["@value"] || data.Fearful["@value"] === "false") &&
      (!data.OtherMood["@value"] || data.OtherMood["@value"] === "false")
    ) {
      if (errs) errs.MSMood = "At least one value is required.";
      else
        errs = {
          MSMood: "At least one value is required.",
        };
    }

    if (
      (!data.FullRange["@value"] || data.FullRange["@value"] === "false") &&
      (!data.Restricted["@value"] || data.Restricted["@value"] === "false") &&
      (!data.Blunted["@value"] || data.Blunted["@value"] === "false") &&
      (!data.Flat["@value"] || data.Flat["@value"] === "false") &&
      (!data.Inappropriate["@value"] ||
        data.Inappropriate["@value"] === "false") &&
      (!data.OtherAffect["@value"] || data.OtherAffect["@value"] === "false")
    ) {
      if (errs) errs.MSAffect = "At least one value is required.";
      else
        errs = {
          MSAffect: "At least one value is required.",
        };
    }

    if (
      (!data.Logical["@value"] || data.Logical["@value"] === "false") &&
      (!data.Preoccupations["@value"] ||
        data.Preoccupations["@value"] === "false") &&
      (!data.Delusions["@value"] || data.Delusions["@value"] === "false") &&
      (!data.Hallucinations["@value"] ||
        data.Hallucinations["@value"] === "false") &&
      (!data.Racing["@value"] || data.Racing["@value"] === "false") &&
      (!data.Disorganized["@value"] ||
        data.Disorganized["@value"] === "false") &&
      (!data.Concrete["@value"] || data.Concrete["@value"] === "false") &&
      (!data.SuicidalIdeation["@value"] ||
        data.SuicidalIdeation["@value"] === "false") &&
      (!data.HomicidalIdeation["@value"] ||
        data.HomicidalIdeation["@value"] === "false") &&
      (!data.OtherPerception["@value"] ||
        data.OtherPerception["@value"] === "false")
    ) {
      if (errs) errs.MSPerception = "At least one value is required.";
      else
        errs = {
          MSPerception: "At least one value is required.",
        };
    }

    if (
      (!data.NoImpairment["@value"] ||
        data.NoImpairment["@value"] === "false") &&
      (!data.ShortAttentionSpan["@value"] ||
        data.ShortAttentionSpan["@value"] === "false") &&
      (!data.DifficultyConcentrating["@value"] ||
        data.DifficultyConcentrating["@value"] === "false") &&
      (!data.Distractibility["@value"] ||
        data.Distractibility["@value"] === "false") &&
      (!data.MemoryImpairment["@value"] ||
        data.MemoryImpairment["@value"] === "false") &&
      (!data.LowIntelligence["@value"] ||
        data.LowIntelligence["@value"] === "false") &&
      (!data.OtherCognition["@value"] ||
        data.OtherCognition["@value"] === "false")
    ) {
      if (errs) errs.MSCognition = "At least one value is required.";
      else
        errs = {
          MSCognition: "At least one value is required.",
        };
    }

    if (
      (!data.AppropriateInsight["@value"] ||
        data.AppropriateInsight["@value"] === "false") &&
      (!data.Fair["@value"] || data.Fair["@value"] === "false") &&
      (!data.Impaired["@value"] || data.Impaired["@value"] === "false") &&
      (!data.OtherInsight["@value"] || data.OtherInsight["@value"] === "false")
    ) {
      if (errs) errs.MSInsight = "At least one value is required.";
      else
        errs = {
          MSInsight: "At least one value is required.",
        };
    }

    if (data.LethalityNA["@value"] !== "true")
      if (
        data.SelfAbusiveBehavior["@value"] !== "None/Never" ||
        data.SuicidalThreats["@value"] !== "None/Never" ||
        data.SuicideAttempt["@value"] !== "None/Never"
      ) {
        if (
          (!data.PriorAttempt["@value"] ||
            data.PriorAttempt["@value"] === "false") &&
          (!data.FamilyHistory["@value"] ||
            data.FamilyHistory["@value"] === "false") &&
          (!data.AccessToMeans["@value"] ||
            data.AccessToMeans["@value"] === "false") &&
          (!data.Exposure["@value"] || data.Exposure["@value"] === "false") &&
          (!data.HealthCondition["@value"] ||
            data.HealthCondition["@value"] === "false") &&
          (!data.ElevatingBeliefs["@value"] ||
            data.ElevatingBeliefs["@value"] === "false") &&
          (!data.MentalHealthDisorder["@value"] ||
            data.MentalHealthDisorder["@value"] === "false") &&
          (!data.OtherRiskFactors ||
            !data.OtherRiskFactors["@value"] ||
            data.OtherRiskFactors["@value"] === "false") &&
          (!data.NoRiskFactors["@value"] ||
            data.NoRiskFactors["@value"] === "false")
        ) {
          if (errs) errs.RiskFactors = "At least one Risk Factor is required.";
          else
            errs = {
              RiskFactors: "At least one Risk Factor is required.",
            };
        }

        if (
          (!data.ClinicalCare["@value"] ||
            data.ClinicalCare["@value"] === "false") &&
          (!data.Connected["@value"] || data.Connected["@value"] === "false") &&
          (!data.Skills["@value"] || data.Skills["@value"] === "false") &&
          (!data.CaregiverContacts["@value"] ||
            data.CaregiverContacts["@value"] === "false") &&
          (!data.DiscouragingBeliefs["@value"] ||
            data.DiscouragingBeliefs["@value"] === "false") &&
          (!data.NoSelfHarmProtectiveFactors["@value"] ||
            data.NoSelfHarmProtectiveFactors["@value"] === "false")
        ) {
          if (errs)
            errs.SelfHarmProtectiveFactors =
              "At least one Protective Factor is required.";
          else
            errs = {
              SelfHarmProtectiveFactors:
                "At least one Protective Factor is required.",
            };
        }

        if (
          (!data.Threatening["@value"] ||
            data.Threatening["@value"] === "false") &&
          (!data.SeekingMeans["@value"] ||
            data.SeekingMeans["@value"] === "false") &&
          (!data.Hopelessness["@value"] ||
            data.Hopelessness["@value"] === "false") &&
          (!data.IncreasedSubstanceUse["@value"] ||
            data.IncreasedSubstanceUse["@value"] === "false") &&
          (!data.DramaticMoodChanges["@value"] ||
            data.DramaticMoodChanges["@value"] === "false") &&
          (!data.GivingAwayPossessions["@value"] ||
            data.GivingAwayPossessions["@value"] === "false") &&
          (!data.SayingGoodbye["@value"] ||
            data.SayingGoodbye["@value"] === "false") &&
          (!data.SocialWithdrawal["@value"] ||
            data.SocialWithdrawal["@value"] === "false") &&
          (!data.RecklessBehavior["@value"] ||
            data.RecklessBehavior["@value"] === "false") &&
          (!data.Feelings["@value"] || data.Feelings["@value"] === "false") &&
          (!data.NoWarningSigns["@value"] ||
            data.NoWarningSigns["@value"] === "false")
        ) {
          if (errs)
            errs.WarningSigns =
              "At least one Current Warning Sign is required.";
          else
            errs = {
              WarningSigns: "At least one Current Warning Sign is required.",
            };
        }
      }

    if (
      data.TraumaSymptoms !== undefined &&
      data.TraumaSymptoms["@value"] === "true" &&
      (data.TraumaticExposure === undefined ||
        !data.TraumaticExposure["@value"] ||
        data.TraumaticExposure["@value"] === "false") &&
      (data.AvoidantTrauma === undefined ||
        !data.AvoidantTrauma["@value"] ||
        data.AvoidantTrauma["@value"] === "false") &&
      (data.HypervigilanceTrauma === undefined ||
        !data.HypervigilanceTrauma["@value"] ||
        data.HypervigilanceTrauma["@value"] === "false") &&
      (data.IntrusiveDreams === undefined ||
        !data.IntrusiveDreams["@value"] ||
        data.IntrusiveDreams["@value"] === "false") &&
      (data.NegativeThoughts === undefined ||
        !data.NegativeThoughts["@value"] ||
        data.NegativeThoughts["@value"] === "false") &&
      (data.CocentrationProblems === undefined ||
        !data.CocentrationProblems["@value"] ||
        data.CocentrationProblems["@value"] === "false") &&
      (data.RecklessBehaviorTrauma === undefined ||
        !data.RecklessBehaviorTrauma["@value"] ||
        data.RecklessBehaviorTrauma["@value"] === "false") &&
      (data.SleepDisturbancesTrauma === undefined ||
        !data.SleepDisturbancesTrauma["@value"] ||
        data.SleepDisturbancesTrauma["@value"] === "false") &&
      (data.OtherTrauma === undefined ||
        !data.OtherTrauma["@value"] ||
        data.OtherTrauma["@value"] === "false")
    ) {
      if (errs)
        errs.TraumaSymptoms = "At least one Trauma Symptom is required.";
      else
        errs = {
          TraumaSymptoms: "At least one Trauma Symptom is required.",
        };
    }

    if (
      data.AnxietyDisorder !== undefined &&
      data.AnxietyDisorder["@value"] === "true" &&
      (data.AnxietyDisorderExcessWorry === undefined ||
        !data.AnxietyDisorderExcessWorry["@value"] ||
        data.AnxietyDisorderExcessWorry["@value"] === "false") &&
      (data.AnxietyDisorderEasilyFatigued === undefined ||
        !data.AnxietyDisorderEasilyFatigued["@value"] ||
        data.AnxietyDisorderEasilyFatigued["@value"] === "false") &&
      (data.MuscleTension === undefined ||
        !data.MuscleTension["@value"] ||
        data.MuscleTension["@value"] === "false") &&
      (data.Overworried === undefined ||
        !data.Overworried["@value"] ||
        data.Overworried["@value"] === "false") &&
      (data.AnxietyDisorderDifficultyConcentrating === undefined ||
        !data.AnxietyDisorderDifficultyConcentrating["@value"] ||
        data.AnxietyDisorderDifficultyConcentrating["@value"] === "false") &&
      (data.SleepDisturbancesAnxiety === undefined ||
        !data.SleepDisturbancesAnxiety["@value"] ||
        data.SleepDisturbancesAnxiety["@value"] === "false") &&
      (data.RestlessAnxiety === undefined ||
        !data.RestlessAnxiety["@value"] ||
        data.RestlessAnxiety["@value"] === "false") &&
      (data.Irritability === undefined ||
        !data.Irritability["@value"] ||
        data.Irritability["@value"] === "false") &&
      (data.OtherAnxiety === undefined ||
        !data.OtherAnxiety["@value"] ||
        data.OtherAnxiety["@value"] === "false")
    ) {
      if (errs)
        errs.AnxietyDisorder = "At least one Anxiety Symptom is required.";
      else
        errs = {
          AnxietyDisorder: "At least one Anxiety Symptom is required.",
        };
    }

    if (
      data.DepressionSymptoms !== undefined &&
      data.DepressionSymptoms["@value"] === "true" &&
      (data.DepressedMood === undefined ||
        !data.DepressedMood["@value"] ||
        data.DepressedMood["@value"] === "false") &&
      (data.SleepChanges === undefined ||
        !data.SleepChanges["@value"] ||
        data.SleepChanges["@value"] === "false") &&
      (data.Guilt === undefined ||
        !data.Guilt["@value"] ||
        data.Guilt["@value"] === "false") &&
      (data.DiminishedInterest === undefined ||
        !data.DiminishedInterest["@value"] ||
        data.DiminishedInterest["@value"] === "false") &&
      (data.PurposelessMovement === undefined ||
        !data.PurposelessMovement["@value"] ||
        data.PurposelessMovement["@value"] === "false") &&
      (data.WeightChanges === undefined ||
        !data.WeightChanges["@value"] ||
        data.WeightChanges["@value"] === "false") &&
      (data.FatigueDepression === undefined ||
        !data.FatigueDepression["@value"] ||
        data.FatigueDepression["@value"] === "false") &&
      (data.ThoughtsOfDeath === undefined ||
        !data.ThoughtsOfDeath["@value"] ||
        data.ThoughtsOfDeath["@value"] === "false") &&
      (data.OtherDepression === undefined ||
        !data.OtherDepression["@value"] ||
        data.OtherDepression["@value"] === "false")
    ) {
      if (errs)
        errs.DepressionSymptoms =
          "At least one Depression Symptom is required.";
      else
        errs = {
          DepressionSymptoms: "At least one Depression Symptom is required.",
        };
    }

    if (
      data.AttentionHyperactivity !== undefined &&
      data.AttentionHyperactivity["@value"] === "true" &&
      (data.AttentionHyperactivityEasilyDistracted === undefined ||
        !data.AttentionHyperactivityEasilyDistracted["@value"] ||
        data.AttentionHyperactivityEasilyDistracted["@value"] === "false") &&
      (data.DifficultyFollowThrough === undefined ||
        !data.DifficultyFollowThrough["@value"] ||
        data.DifficultyFollowThrough["@value"] === "false") &&
      (data.Forgetful === undefined ||
        !data.Forgetful["@value"] ||
        data.Forgetful["@value"] === "false") &&
      (data.DifficultyListening === undefined ||
        !data.DifficultyListening["@value"] ||
        data.DifficultyListening["@value"] === "false") &&
      (data.DisorganizedAttention === undefined ||
        !data.DisorganizedAttention["@value"] ||
        data.DisorganizedAttention["@value"] === "false") &&
      (data.Fidgety === undefined ||
        !data.Fidgety["@value"] ||
        data.Fidgety["@value"] === "false") &&
      (data.LackOfDetails === undefined ||
        !data.LackOfDetails["@value"] ||
        data.LackOfDetails["@value"] === "false") &&
      (data.LosesThings === undefined ||
        !data.LosesThings["@value"] ||
        data.LosesThings["@value"] === "false") &&
      (data.InterruptsOthers === undefined ||
        !data.InterruptsOthers["@value"] ||
        data.InterruptsOthers["@value"] === "false") &&
      (data.UnableToSitStill === undefined ||
        !data.UnableToSitStill["@value"] ||
        data.UnableToSitStill["@value"] === "false") &&
      (data.ExcessiveTalking === undefined ||
        !data.ExcessiveTalking["@value"] ||
        data.ExcessiveTalking["@value"] === "false") &&
      (data.DifficultyWaiting === undefined ||
        !data.DifficultyWaiting["@value"] ||
        data.DifficultyWaiting["@value"] === "false") &&
      (data.OtherAttention === undefined ||
        !data.OtherAttention["@value"] ||
        data.OtherAttention["@value"] === "false")
    ) {
      if (errs)
        errs.AttentionHyperactivity =
          "At least one Attention and Hyperactivity Symptom is required.";
      else
        errs = {
          AttentionHyperactivity:
            "At least one Attention and Hyperactivity Symptom is required.",
        };
    }

    if (
      data.SU !== undefined &&
      data.SU["@value"] === "true" &&
      (data.PatternOfSubstanceUse === undefined ||
        !data.PatternOfSubstanceUse["@value"] ||
        data.PatternOfSubstanceUse["@value"] === "false") &&
      (data.DesiresCutDownUse === undefined ||
        !data.DesiresCutDownUse["@value"] ||
        data.DesiresCutDownUse["@value"] === "false") &&
      (data.TimeSpentObtaining === undefined ||
        !data.TimeSpentObtaining["@value"] ||
        data.TimeSpentObtaining["@value"] === "false") &&
      (data.Cravings === undefined ||
        !data.Cravings["@value"] ||
        data.Cravings["@value"] === "false") &&
      (data.FailureOfResponsibilities === undefined ||
        !data.FailureOfResponsibilities["@value"] ||
        data.FailureOfResponsibilities["@value"] === "false") &&
      (data.NegativeImpact === undefined ||
        !data.NegativeImpact["@value"] ||
        data.NegativeImpact["@value"] === "false") &&
      (data.AbandonsActivities === undefined ||
        !data.AbandonsActivities["@value"] ||
        data.AbandonsActivities["@value"] === "false") &&
      (data.Tolerance === undefined ||
        !data.Tolerance["@value"] ||
        data.Tolerance["@value"] === "false") &&
      (data.SUWithdrawal === undefined ||
        !data.SUWithdrawal["@value"] ||
        data.SUWithdrawal["@value"] === "false") &&
      (data.SUOther === undefined ||
        !data.SUOther["@value"] ||
        data.SUOther["@value"] === "false")
    ) {
      if (errs) errs.SU = "At least one Substance Use Symptom is required.";
      else
        errs = {
          SU: "At least one Substance Use Symptom is required.",
        };
    }

    if (
      data.DMDD !== undefined &&
      data.DMDD["@value"] === "true" &&
      (data.Temper === undefined ||
        !data.Temper["@value"] ||
        data.Temper["@value"] === "false") &&
      (data.OutsideOutbursts === undefined ||
        !data.OutsideOutbursts["@value"] ||
        data.OutsideOutbursts["@value"] === "false") &&
      (data.FrequentOutbursts === undefined ||
        !data.FrequentOutbursts["@value"] ||
        data.FrequentOutbursts["@value"] === "false") &&
      (data.DMDDIrritable === undefined ||
        !data.DMDDIrritable["@value"] ||
        data.DMDDIrritable["@value"] === "false") &&
      (data.OutburstsAroundOthers === undefined ||
        !data.OutburstsAroundOthers["@value"] ||
        data.OutburstsAroundOthers["@value"] === "false") &&
      (data.DMDDOther === undefined ||
        !data.DMDDOther["@value"] ||
        data.DMDDOther["@value"] === "false")
    ) {
      if (errs)
        errs.DMDD =
          "At least one Disruptive Mood Dysregulation Disorder Symptom is required.";
      else
        errs = {
          DMDD: "At least one Disruptive Mood Dysregulation Disorder Symptom is required.",
        };
    }

    if (
      data.ConductDisorder !== undefined &&
      data.ConductDisorder["@value"] === "true" &&
      (data.Aggression === undefined ||
        !data.Aggression["@value"] ||
        data.Aggression["@value"] === "false") &&
      (data.DestructionOfProperty === undefined ||
        !data.DestructionOfProperty["@value"] ||
        data.DestructionOfProperty["@value"] === "false") &&
      (data.Theft === undefined ||
        !data.Theft["@value"] ||
        data.Theft["@value"] === "false") &&
      (data.SeriousViolations === undefined ||
        !data.SeriousViolations["@value"] ||
        data.SeriousViolations["@value"] === "false") &&
      (data.OtherConductDisorder === undefined ||
        !data.OtherConductDisorder["@value"] ||
        data.OtherConductDisorder["@value"] === "false")
    ) {
      if (errs)
        errs.ConductDisorder =
          "At least one Conduct Disorder Symptom is required.";
      else
        errs = {
          ConductDisorder: "At least one Conduct Disorder Symptom is required.",
        };
    }

    if (
      data.ODD !== undefined &&
      data.ODD["@value"] === "true" &&
      (data.IrritableODD === undefined ||
        !data.IrritableODD["@value"] ||
        data.IrritableODD["@value"] === "false") &&
      (data.Argumentative === undefined ||
        !data.Argumentative["@value"] ||
        data.Argumentative["@value"] === "false") &&
      (data.Vindictive === undefined ||
        !data.Vindictive["@value"] ||
        data.Vindictive["@value"] === "false") &&
      (data.OtherODD === undefined ||
        !data.OtherODD["@value"] ||
        data.OtherODD["@value"] === "false")
    ) {
      if (errs)
        errs.ODD =
          "At least one Oppositional Defiance Disorder Symptom is required.";
      else
        errs = {
          ODD: "At least one Oppositional Defiance Disorder Symptom is required.",
        };
    }

    if (
      data.OCD !== undefined &&
      data.OCD["@value"] === "true" &&
      (data.OCDPresence === undefined ||
        !data.OCDPresence["@value"] ||
        data.OCDPresence["@value"] === "false") &&
      (data.Obsessions === undefined ||
        !data.Obsessions["@value"] ||
        data.Obsessions["@value"] === "false") &&
      (data.TimeConsuming === undefined ||
        !data.TimeConsuming["@value"] ||
        data.TimeConsuming["@value"] === "false") &&
      (data.Compulsions === undefined ||
        !data.Compulsions["@value"] ||
        data.Compulsions["@value"] === "false") &&
      (data.ReduceAnxiety === undefined ||
        !data.ReduceAnxiety["@value"] ||
        data.ReduceAnxiety["@value"] === "false") &&
      (data.SuppressUrges === undefined ||
        !data.SuppressUrges["@value"] ||
        data.SuppressUrges["@value"] === "false") &&
      (data.OCDOther === undefined ||
        !data.OCDOther["@value"] ||
        data.OCDOther["@value"] === "false")
    ) {
      if (errs)
        errs.OCD =
          "At least one Obsessive-Compulsive Disorder Symptom is required.";
      else
        errs = {
          OCD: "At least one Obsessive-Compulsive Disorder Symptom is required.",
        };
    }

    if (
      data.PanicDisorder !== undefined &&
      data.PanicDisorder["@value"] === "true" &&
      (data.PoundingHeart === undefined ||
        !data.PoundingHeart["@value"] ||
        data.PoundingHeart["@value"] === "false") &&
      (data.PanicDisorderSweating === undefined ||
        !data.PanicDisorderSweating["@value"] ||
        data.PanicDisorderSweating["@value"] === "false") &&
      (data.Trembling === undefined ||
        !data.Trembling["@value"] ||
        data.Trembling["@value"] === "false") &&
      (data.ShortnessOfBreath === undefined ||
        !data.ShortnessOfBreath["@value"] ||
        data.ShortnessOfBreath["@value"] === "false") &&
      (data.Choking === undefined ||
        !data.Choking["@value"] ||
        data.Choking["@value"] === "false") &&
      (data.ChestPain === undefined ||
        !data.ChestPain["@value"] ||
        data.ChestPain["@value"] === "false") &&
      (data.PanicDisorderNausea === undefined ||
        !data.PanicDisorderNausea["@value"] ||
        data.PanicDisorderNausea["@value"] === "false") &&
      (data.Dizzy === undefined ||
        !data.Dizzy["@value"] ||
        data.Dizzy["@value"] === "false") &&
      (data.Chills === undefined ||
        !data.Chills["@value"] ||
        data.Chills["@value"] === "false") &&
      (data.Derealization === undefined ||
        !data.Derealization["@value"] ||
        data.Derealization["@value"] === "false") &&
      (data.FearOfCrazy === undefined ||
        !data.FearOfCrazy["@value"] ||
        data.FearOfCrazy["@value"] === "false") &&
      (data.FearOfDying === undefined ||
        !data.FearOfDying["@value"] ||
        data.FearOfDying["@value"] === "false") &&
      (data.PanicDisorderOther === undefined ||
        !data.PanicDisorderOther["@value"] ||
        data.PanicDisorderOther["@value"] === "false")
    ) {
      if (errs)
        errs.PanicDisorder = "At least one Panic Disorder Symptom is required.";
      else
        errs = {
          PanicDisorder: "At least one Panic Disorder Symptom is required.",
        };
    }

    if (
      data.Phobias !== undefined &&
      data.Phobias["@value"] === "true" &&
      (data.PhobiasSpecificFear === undefined ||
        !data.PhobiasSpecificFear["@value"] ||
        data.PhobiasSpecificFear["@value"] === "false") &&
      (data.ImmediateFear === undefined ||
        !data.ImmediateFear["@value"] ||
        data.ImmediateFear["@value"] === "false") &&
      (data.PhobiasAvoidance === undefined ||
        !data.PhobiasAvoidance["@value"] ||
        data.PhobiasAvoidance["@value"] === "false") &&
      (data.FearExaggerated === undefined ||
        !data.FearExaggerated["@value"] ||
        data.FearExaggerated["@value"] === "false") &&
      (data.PhobiasOther === undefined ||
        !data.PhobiasOther["@value"] ||
        data.PhobiasOther["@value"] === "false")
    ) {
      if (errs) errs.Phobias = "At least one Phobia Symptom is required.";
      else
        errs = {
          Phobias: "At least one Phobia Symptom is required.",
        };
    }

    if (
      data.DI !== undefined &&
      data.DI["@value"] === "true" &&
      (data.DILowIQ === undefined ||
        !data.DILowIQ["@value"] ||
        data.DILowIQ["@value"] === "false") &&
      (data.DISensoryImpairments === undefined ||
        !data.DISensoryImpairments["@value"] ||
        data.DISensoryImpairments["@value"] === "false") &&
      (data.DISpeechDelays === undefined ||
        !data.DISpeechDelays["@value"] ||
        data.DISpeechDelays["@value"] === "false") &&
      (data.DIMotorCoordinationDelays === undefined ||
        !data.DIMotorCoordinationDelays["@value"] ||
        data.DIMotorCoordinationDelays["@value"] === "false") &&
      (data.DILearningDisorder === undefined ||
        !data.DILearningDisorder["@value"] ||
        data.DILearningDisorder["@value"] === "false") &&
      (data.DISpatialDelays === undefined ||
        !data.DISpatialDelays["@value"] ||
        data.DISpatialDelays["@value"] === "false") &&
      (data.DevelopmentalImpairmentsOther === undefined ||
        !data.DevelopmentalImpairmentsOther["@value"] ||
        data.DevelopmentalImpairmentsOther["@value"] === "false")
    ) {
      if (errs) errs.DI = "At least one Developmental Impairment is required.";
      else
        errs = {
          DI: "At least one Developmenta lImpairment is required.",
        };
    }

    if (
      (!data.SkillsTraining["@value"] ||
        data.SkillsTraining["@value"] === "false") &&
      (!data.CommunitySupport["@value"] ||
        data.CommunitySupport["@value"] === "false") &&
      (!data.MedicalDentalVision["@value"] ||
        data.MedicalDentalVision["@value"] === "false") &&
      (!data.SymptomManagementSkills["@value"] ||
        data.SymptomManagementSkills["@value"] === "false") &&
      (!data.Mentoring["@value"] || data.Mentoring["@value"] === "false") &&
      (!data.HealthyHabits["@value"] ||
        data.HealthyHabits["@value"] === "false") &&
      (!data.SocialSkills["@value"] ||
        data.SocialSkills["@value"] === "false") &&
      (!data.FamilySupport["@value"] ||
        data.FamilySupport["@value"] === "false") &&
      (!data.EducationSchool["@value"] ||
        data.EducationSchool["@value"] === "false") &&
      (!data.DecisionMaking["@value"] ||
        data.DecisionMaking["@value"] === "false") &&
      (!data.FosterFamilySupport["@value"] ||
        data.FosterFamilySupport["@value"] === "false") &&
      (!data.Vocational["@value"] || data.Vocational["@value"] === "false") &&
      (!data.FinancialSupport["@value"] ||
        data.FinancialSupport["@value"] === "false") &&
      (!data.Respite["@value"] || data.Respite["@value"] === "false") &&
      (!data.Occupational["@value"] ||
        data.Occupational["@value"] === "false") &&
      (!data.ProsocialActivities["@value"] ||
        data.ProsocialActivities["@value"] === "false") &&
      (!data.MedicationManagementNeed["@value"] ||
        data.MedicationManagementNeed["@value"] === "false") &&
      (!data.CaseManagement["@value"] ||
        data.CaseManagement["@value"] === "false")
    ) {
      if (errs) errs.AssessedNeeds = "At least one Assessed Need is required.";
      else
        errs = {
          AssessedNeeds: "At least one Assessed Need is required.",
        };
    }

    // if (
    //   (!data.MorePsychiatric["@value"] ||
    //     data.MorePsychiatric["@value"] === "false") &&
    //   (!data.MorePsychological["@value"] ||
    //     data.MorePsychological["@value"] === "false") &&
    //   (!data.MoreNeurological["@value"] ||
    //     data.MoreNeurological["@value"] === "false") &&
    //   (!data.MoreEducational["@value"] ||
    //     data.MoreEducational["@value"] === "false") &&
    //   (!data.MoreMedical["@value"] || data.MoreMedical["@value"] === "false") &&
    //   (!data.MoreDental["@value"] || data.MoreDental["@value"] === "false") &&
    //   (!data.MoreSubstanceUse["@value"] ||
    //     data.MoreSubstanceUse["@value"] === "false") &&
    //   (!data.MoreVocational["@value"] ||
    //     data.MoreVocational["@value"] === "false") &&
    //   (!data.MoreOccupational["@value"] ||
    //     data.MoreOccupational["@value"] === "false") &&
    //   (!data.MoreVisualAuditory["@value"] ||
    //     data.MoreVisualAuditory["@value"] === "false") &&
    //   (!data.MoreNutritional["@value"] ||
    //     data.MoreNutritional["@value"] === "false") &&
    //   (!data.MoreOther["@value"] || data.MoreOther["@value"] === "false")
    // ) {
    //   if (errs)
    //     errs.InformationSources =
    //       "At least one Further Evaluation Need is required.";
    //   else
    //     errs = {
    //       InformationSources: "At least one Further Evaluation Need is required.",
    //     };
    // }

    setErrors(errs);
    return errs;
  };

  const sectionsToNavigate = [
    { _id: "PresentingProblem", name: "Presenting Problem & Strengths" },
    { _id: "FamilyHistorySection", name: "Family Environment and History" },
    { _id: "Demographics", name: "Culture/Ethnicity/Faith" },
    { _id: "DevelopmentalHistory", name: "Developmental History" },
    { _id: "Education", name: "Education/Employment" },
    { _id: "Activities", name: "Basic Living Skills" },
    {
      _id: "Friendships",
      name: "Friendship/Social Peer Support/Relationships",
    },
    { _id: "LegalHistory", name: "Legal History" },
    { _id: "MedicalHistory", name: "Medical History/Medications" },
    {
      _id: "MentalHealthTreatmentHistory",
      name: "Mental Health Treatment History",
    },
    { _id: "SubstanceUseHistory", name: "Alcohol/Drug Use History" },
    { _id: "TraumaHistory", name: "Trauma History" },
    { _id: "MentalStatus", name: "Mental Status" },
    { _id: "Lethality", name: "Lethality" },
    { _id: "SymptomChecklist", name: "Symptom Checklist" },
    { _id: "ClinicalNarrative", name: "Clinical Narrative Summary" },
    { _id: "Diagnosis", name: "Diagnosis" },
    {
      _id: "TreatmentRecommendations",
      name: "Treatment Recommendations/Needs",
    },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={(e) => {
            preSignForm();
            toggleSignatureModal(e);
          }}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          sectionsToNavigate={sectionsToNavigate}
          beforePrintFunction={(callback) => {
            handleExpandStatusChange(true, callback);
          }}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="PreferredName"
                    label="Recipient's Preferred Name:"
                    error={errors.PreferredName}
                    value={data.PreferredName["@value"]}
                    onChange={handleChange}
                    identical={compare("PreferredName")}
                  />
                </div>
                <div className="col">
                  <FormSelect
                    name="Pronouns"
                    label="Recipient's Pronouns:"
                    error={errors.Pronouns}
                    value={data.Pronouns["@value"]}
                    options={pronounOptions}
                    onChange={handleChange}
                    identical={compare("Pronouns")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.Pronouns !== undefined &&
                  data.Pronouns["@value"] === "Prefer to self-describe" &&
                  data.PronounsDescription !== undefined ? (
                    <FormInput
                      name="PronounsDescription"
                      label="Please describe:"
                      error={errors.PronounsDescription}
                      value={data.PronounsDescription["@value"]}
                      onChange={handleChange}
                      identical={compare("PronounsDescription")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  ) : null}
                </div>
              </div>
              {!formDisabled ||
              (formDisabled &&
                data.SexAssignedAtBirth !== undefined &&
                data.GenderIdentity !== undefined) ? (
                <div className="row">
                  <div className="col">
                    <FormSelect
                      name="SexAssignedAtBirth"
                      label="Sex Assigned at Birth:"
                      error={errors.SexAssignedAtBirth}
                      value={
                        data.SexAssignedAtBirth !== undefined &&
                        data.SexAssignedAtBirth["@value"]
                      }
                      options={sexAtBirthOptions}
                      onChange={handleChange}
                      identical={compare("SexAssignedAtBirth")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SexAssignedAtBirth !== undefined &&
                      data.SexAssignedAtBirth["@value"] ===
                        "Sex not listed" && (
                        <FormInput
                          name="SexAssignedAtBirthDetails"
                          label="Please specify:"
                          error={errors.SexAssignedAtBirthDetails}
                          value={
                            data.SexAssignedAtBirthDetails !== undefined
                              ? data.SexAssignedAtBirthDetails["@value"]
                              : ""
                          }
                          onChange={handleChange}
                          identical={compare("SexAssignedAtBirthDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                  </div>
                  <div className="col">
                    <FormSelect
                      name="GenderIdentity"
                      label="Gender Identity:"
                      error={errors.GenderIdentity}
                      value={
                        data.GenderIdentity !== undefined &&
                        data.GenderIdentity["@value"]
                      }
                      options={genderIdentityOptions}
                      onChange={handleChange}
                      identical={compare("GenderIdentity")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.GenderIdentity !== undefined &&
                      data.GenderIdentity["@value"] ===
                        "Identity not listed" && (
                        <FormInput
                          name="GenderIdentityDescription"
                          label="Please specify:"
                          error={errors.GenderIdentityDescription}
                          value={
                            data.GenderIdentityDescription !== undefined
                              ? data.GenderIdentityDescription["@value"]
                              : ""
                          }
                          onChange={handleChange}
                          identical={compare("GenderIdentityDescription")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                  </div>
                </div>
              ) : null}
              <div className="row">
                <div className="col">
                  <FormInput
                    name="DateCompleted"
                    label="Date Completed:"
                    type="date"
                    error={errors.DateCompleted}
                    value={data.DateCompleted["@value"]}
                    onChange={handleChange}
                    identical={compare("DateCompleted")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormSelect
                    name="Division"
                    label="Division:"
                    error={errors.Division}
                    value={data.Division["@value"]}
                    options={divisionOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <FormRadioButton
                name="PSDType"
                label="Type of PSD:"
                error={errors.PSDType}
                value={data.PSDType["@value"]}
                options={psdTypeOptions}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.PSDType["@value"] === "Review/Update" ? (
                <FormInput
                  name="ReviewReason"
                  label="Reason:"
                  error={errors.ReviewReason}
                  value={getFieldValue(data.ReviewReason)}
                  onChange={handleChange}
                  identical={compare("ReviewReason")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <h5>Information Sources:</h5>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="ISRecipient"
                      label="Recipient"
                      checked={data.ISRecipient["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISParent"
                      label="Parent/Other"
                      checked={data.ISParent["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISFamily"
                      label="Family"
                      checked={data.ISFamily["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISGuardian"
                      label="Guardian"
                      checked={data.ISGuardian["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="ISCaregiver"
                      label="Non-parent Caregiver"
                      checked={data.ISCaregiver["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISPhysician"
                      label="Physician"
                      checked={data.ISPhysician["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISRecords"
                      label="Records"
                      checked={data.ISRecords["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISServiceProvider"
                      label="Service Provider"
                      checked={data.ISServiceProvider["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="ISSchool"
                      label="School"
                      checked={data.ISSchool["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISCourt"
                      label="Court"
                      checked={data.ISCourt["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISDHR"
                      label="DHR"
                      checked={data.ISDHR["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISOther"
                      label="Other"
                      checked={data.ISOther["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              {data.ISParent["@value"] === "true" ? (
                <FormInput
                  name="ISParentDetails"
                  label="Parent source:"
                  error={errors.ISParentDetails}
                  value={data.ISParentDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISParentDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISFamily["@value"] === "true" ? (
                <FormInput
                  name="ISFamilyDetails"
                  label="Family source:"
                  error={errors.ISFamilyDetails}
                  value={data.ISFamilyDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISFamilyDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISGuardian["@value"] === "true" ? (
                <FormInput
                  name="ISGuardianDetails"
                  label="Guardian source:"
                  error={errors.ISGuardianDetails}
                  value={data.ISGuardianDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISGuardianDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISCaregiver["@value"] === "true" ? (
                <FormInput
                  name="ISCaregiverDetails"
                  label="Non-parent Caregiver source:"
                  error={errors.ISCaregiverDetails}
                  value={data.ISCaregiverDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISCaregiverDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISPhysician["@value"] === "true" ? (
                <FormInput
                  name="ISPhysicianDetails"
                  label="Physician source:"
                  error={errors.ISPhysicianDetails}
                  value={data.ISPhysicianDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISPhysicianDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISRecords["@value"] === "true" ? (
                <FormInput
                  name="ISRecordsDetails"
                  label="Records source:"
                  error={errors.ISRecordsDetails}
                  value={data.ISRecordsDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISRecordsDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISServiceProvider["@value"] === "true" ? (
                <FormInput
                  name="ISServiceProviderDetails"
                  label="Service Provider source:"
                  error={errors.ISServiceProviderDetails}
                  value={data.ISServiceProviderDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISServiceProviderDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISSchool["@value"] === "true" ? (
                <FormInput
                  name="ISSchoolDetails"
                  label="School source:"
                  error={errors.ISSchoolDetails}
                  value={data.ISSchoolDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISSchoolDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISCourt["@value"] === "true" ? (
                <FormInput
                  name="ISCourtDetails"
                  label="Court source:"
                  error={errors.ISCourtDetails}
                  value={data.ISCourtDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISCourtDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISDHR["@value"] === "true" ? (
                <FormInput
                  name="ISDHRDetails"
                  label="DHR source:"
                  error={errors.ISDHRDetails}
                  value={data.ISDHRDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISDHRDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {data.ISOther["@value"] === "true" ? (
                <FormInput
                  name="ISOtherDetails"
                  label="Other source:"
                  error={errors.ISOtherDetails}
                  value={data.ISOtherDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISOtherDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {errors.InformationSources !== undefined ? (
                <div className="row">
                  <div className="col">
                    <div className="alert alert-danger">
                      {errors.InformationSources}
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="clearfix">
                <div className="float-start">
                  <button
                    className="btn btn-primary mb-2"
                    onClick={(e) => {
                      handleExpandStatusChange();
                    }}
                  >
                    {expandStatus === true ? "Collapse All" : "Expand All"}
                  </button>
                </div>
                <div className="float-end d-flex d-print-none">
                  <div className="me-2">Clinical Narrative Summary:</div>
                  <ToggleSwitch
                    values={["Show", "Hide"]}
                    selected={showNarrative}
                    onChange={setShowNarrative}
                  />
                </div>
              </div>
              <ExpandDiv
                id="PresentingProblem"
                expandStatus={expandStatus}
                className="mt-2"
                title="Presenting Problem &amp; Strengths"
              >
                <FormInput
                  name="ReferralReason"
                  label="Reason for Referral:"
                  error={errors.ReferralReason}
                  value={data.ReferralReason["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("ReferralReason")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="ReferralSource"
                  label="Referral Source:"
                  error={errors.ReferralSource}
                  value={data.ReferralSource["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("ReferralSource")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="PresentingProblem"
                  label="Presenting Problem:"
                  error={errors.PresentingProblem}
                  value={data.PresentingProblem["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("PresentingProblem")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="RecipientProblem"
                  label="Recipient Description of Problem (in their own words):"
                  error={errors.RecipientProblem}
                  value={data.RecipientProblem["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("RecipientProblem")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="CaregiverProblem"
                  label="Caregiver Description of Problem (in their own words):"
                  error={errors.CaregiverProblem}
                  value={data.CaregiverProblem["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("CaregiverProblem")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="RecipientStrengths"
                  label="Recipient Description of Strengths (in their words):"
                  error={errors.RecipientStrengths}
                  value={data.RecipientStrengths["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("RecipientStrengths")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="CaregiverStrengths"
                  label="Caregiver Description of Strengths (family perception):"
                  error={errors.CaregiverStrengths}
                  value={data.CaregiverStrengths["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("CaregiverStrengths")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormTextArea
                  name="RecipientAbilities"
                  label="Recipient abilities, skills, and interests (narrative):"
                  error={errors.RecipientAbilities}
                  value={data.RecipientAbilities["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("RecipientAbilities")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="FamilyHistorySection"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Environment and History"
              >
                <FormSelect
                  name="LivingSituation"
                  label="Living Situation:"
                  error={errors.LivingSituation}
                  value={data.LivingSituation["@value"]}
                  options={livingSituationOptions}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("LivingSituation")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <h5>Family Members/Supports for the Recipient:</h5>
                <FormCheckbox
                  name="RecipientSupportsUnknown"
                  label="Family Members Unknown"
                  divClassName="ms-3"
                  checked={
                    data.RecipientSupportsUnknown &&
                    data.RecipientSupportsUnknown["@value"] === "true"
                  }
                  commentsHandler={handleComments}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  disabled={formDisabled}
                />
                {data.RecipientSupportsUnknown["@value"] === "false" ? (
                  <FieldTable
                    name="RecipientSupportsDetails"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col py-2 border-end">Name</div>
                        <div className="col py-2 border-end">
                          Relationship to Recipient
                        </div>
                        <div className="col py-2">Quality of Relationship</div>
                      </div>
                      {data.RecipientSupportsDetails["@value"] &&
                        JSON.parse(data.RecipientSupportsDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col py-2 border-end d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "RecipientSupportsDetails"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "RecipientSupportsDetails"
                                        );
                                      }}
                                    ></i>
                                    <FormInput
                                      name="Name"
                                      value={item.Name}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "RecipientSupportsDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "RecipientSupportsDetails"
                                        );
                                      }}
                                    ></i>
                                    {item.Name}
                                  </>
                                )}
                              </div>
                              <div className="col py-2 border-end">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormSelect
                                    name="Relationship"
                                    value={item.Relationship}
                                    options={relationshipOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "RecipientSupportsDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Relationship
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormTextArea
                                    name="RelationshipQuality"
                                    value={item.RelationshipQuality}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "RecipientSupportsDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.RelationshipQuality
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled && (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("RecipientSupportsDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Family Member
                      </button>
                    )}
                    {errors.RecipientSupportsDetails && (
                      <div className="alert alert-danger">
                        {errors.RecipientSupportsDetails}
                      </div>
                    )}
                  </FieldTable>
                ) : null}
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="CaregiverCooperation"
                      label="Cooperation between caregivers on discipline and care:"
                      error={errors.CaregiverCooperation}
                      value={data.CaregiverCooperation["@value"]}
                      options={yesNoNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  {data.CaregiverCooperation["@value"] === "No" ? (
                    <div className="col">
                      <FormInput
                        name="CaregiverCooperationDetails"
                        label="Describe:"
                        error={errors.CaregiverCooperationDetails}
                        value={data.CaregiverCooperationDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("CaregiverCooperationDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="SupervisionDifficulties"
                      label="Difficulties with the supervision/monitoring of child:"
                      error={errors.SupervisionDifficulties}
                      value={data.SupervisionDifficulties["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  {data.SupervisionDifficulties["@value"] === "Yes" ? (
                    <div className="col">
                      <FormInput
                        name="SupervisionDifficultiesDetails"
                        label="Describe:"
                        error={errors.SupervisionDifficultiesDetails}
                        value={data.SupervisionDifficultiesDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SupervisionDifficultiesDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="DisciplinePractices"
                      label="Discipline practices:"
                      error={errors.DisciplinePractices}
                      value={data.DisciplinePractices["@value"]}
                      options={disciplinePracticesOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  {data.DisciplinePractices["@value"] === "Inconsistent" ||
                  data.DisciplinePractices["@value"] === "Ineffective" ? (
                    <div className="col">
                      <FormInput
                        name="DisciplinePracticesDetails"
                        label="Describe:"
                        error={errors.DisciplinePracticesDetails}
                        value={data.DisciplinePracticesDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("DisciplinePracticesDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                <FormInput
                  name="FamilyEnvironmentComments"
                  label="Comments:"
                  error={errors.FamilyEnvironmentComments}
                  value={data.FamilyEnvironmentComments["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("FamilyEnvironmentComments")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <h5>Family History</h5>
                <FormCheckbox
                  name="FamilyHistoryUnknown"
                  label="Family History Unknown"
                  divClassName="ms-3"
                  checked={
                    data.FamilyHistoryUnknown &&
                    data.FamilyHistoryUnknown["@value"] === "true"
                  }
                  commentsHandler={handleComments}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  disabled={formDisabled}
                />
                {data.FamilyHistoryUnknown["@value"] !== "true" && (
                  <>
                    <FieldTable
                      name="FamilyHistoryDetails"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            Family Member
                          </div>
                          <div className="col py-2 border-end">
                            Concern/Issue
                          </div>
                          <div className="col py-2">Explanation/Comments</div>
                        </div>
                        {data.FamilyHistoryDetails["@value"] &&
                          JSON.parse(data.FamilyHistoryDetails["@value"]).map(
                            (item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col py-2 border-end d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                      ></i>
                                      <FormSelect
                                        name="FamilyMember"
                                        options={familyMemberOptions}
                                        value={item.FamilyMember}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                      ></i>
                                      {item.FamilyMember}
                                    </>
                                  )}
                                </div>
                                <div className="col py-2 border-end">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <div className="container">
                                      <div className="row">
                                        <div className="col">
                                          <FormCheckbox
                                            name={`MentalHealth-${index}`}
                                            label="Mental Health"
                                            checked={
                                              item.MentalHealth === "true"
                                            }
                                            onChange={(e) => {
                                              e.currentTarget = {
                                                ...e.currentTarget,
                                                type: "checkbox",
                                                name: e.currentTarget.name.split(
                                                  "-"
                                                )[0],
                                              };
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </div>
                                        <div className="col">
                                          <FormCheckbox
                                            name={`Medical-${index}`}
                                            label="Medical"
                                            checked={item.Medical === "true"}
                                            onChange={(e) => {
                                              e.currentTarget = {
                                                ...e.currentTarget,
                                                type: "checkbox",
                                                name: e.currentTarget.name.split(
                                                  "-"
                                                )[0],
                                              };
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </div>
                                        <div className="col">
                                          <FormCheckbox
                                            name={`Disability-${index}`}
                                            label="Disability"
                                            checked={item.Disability === "true"}
                                            onChange={(e) => {
                                              e.currentTarget = {
                                                ...e.currentTarget,
                                                type: "checkbox",
                                                name: e.currentTarget.name.split(
                                                  "-"
                                                )[0],
                                              };
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </div>
                                      </div>
                                      <div className="row">
                                        <div className="col">
                                          <FormCheckbox
                                            name={`SubstanceAbuse-${index}`}
                                            label="Substance Abuse"
                                            checked={
                                              item.SubstanceAbuse === "true"
                                            }
                                            onChange={(e) => {
                                              e.currentTarget = {
                                                ...e.currentTarget,
                                                type: "checkbox",
                                                name: e.currentTarget.name.split(
                                                  "-"
                                                )[0],
                                              };
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </div>
                                        <div className="col">
                                          <FormCheckbox
                                            name={`Suicidal-${index}`}
                                            label="Suicidal"
                                            checked={item.Suicidal === "true"}
                                            onChange={(e) => {
                                              e.currentTarget = {
                                                ...e.currentTarget,
                                                type: "checkbox",
                                                name: e.currentTarget.name.split(
                                                  "-"
                                                )[0],
                                              };
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </div>
                                        <div className="col">
                                          <FormCheckbox
                                            name={`Legal-${index}`}
                                            label="Legal"
                                            checked={item.Legal === "true"}
                                            onChange={(e) => {
                                              e.currentTarget = {
                                                ...e.currentTarget,
                                                type: "checkbox",
                                                name: e.currentTarget.name.split(
                                                  "-"
                                                )[0],
                                              };
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </div>
                                      </div>
                                      <div className="row">
                                        <div className="col">
                                          <FormCheckbox
                                            name={`Trauma-${index}`}
                                            label="Trauma"
                                            checked={item.Trauma === "true"}
                                            onChange={(e) => {
                                              e.currentTarget = {
                                                ...e.currentTarget,
                                                type: "checkbox",
                                                name: e.currentTarget.name.split(
                                                  "-"
                                                )[0],
                                              };
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </div>
                                        <div className="col">
                                          <FormCheckbox
                                            name={`Perpetration-${index}`}
                                            label="Perpetration"
                                            checked={
                                              item.Perpetration === "true"
                                            }
                                            onChange={(e) => {
                                              e.currentTarget = {
                                                ...e.currentTarget,
                                                type: "checkbox",
                                                name: e.currentTarget.name.split(
                                                  "-"
                                                )[0],
                                              };
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </div>
                                        <div className="col">
                                          <FormCheckbox
                                            name={`DomesticViolence-${index}`}
                                            label="Domestic Violence"
                                            checked={
                                              item.DomesticViolence === "true"
                                            }
                                            onChange={(e) => {
                                              e.currentTarget = {
                                                ...e.currentTarget,
                                                type: "checkbox",
                                                name: e.currentTarget.name.split(
                                                  "-"
                                                )[0],
                                              };
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </div>
                                      </div>
                                      <div className="row">
                                        <div className="col">
                                          <FormCheckbox
                                            name={`CouplesConflict-${index}`}
                                            label="Marital/Couple's Conflict"
                                            checked={
                                              item.CouplesConflict === "true"
                                            }
                                            onChange={(e) => {
                                              e.currentTarget = {
                                                ...e.currentTarget,
                                                type: "checkbox",
                                                name: e.currentTarget.name.split(
                                                  "-"
                                                )[0],
                                              };
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </div>
                                        <div className="col">
                                          <FormCheckbox
                                            name={`Other-${index}`}
                                            label="Other"
                                            checked={item.Other === "true"}
                                            onChange={(e) => {
                                              e.currentTarget = {
                                                ...e.currentTarget,
                                                type: "checkbox",
                                                name: e.currentTarget.name.split(
                                                  "-"
                                                )[0],
                                              };
                                              handleRowChange(
                                                e,
                                                index,
                                                "FamilyHistoryDetails"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </div>
                                        <div className="col"></div>
                                      </div>
                                    </div>
                                  ) : (
                                    getFamilyMemberConcerns(item)
                                  )}
                                </div>
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="Comments"
                                      value={item.Comments}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "FamilyHistoryDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Comments
                                  )}
                                </div>
                              </div>
                            )
                          )}
                      </div>
                      {!formDisabled && (
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("FamilyHistoryDetails", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Family Member
                        </button>
                      )}
                      {errors.FamilyHistoryDetails && (
                        <div className="alert alert-danger">
                          {errors.FamilyHistoryDetails}
                        </div>
                      )}
                    </FieldTable>
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="Demographics"
                expandStatus={expandStatus}
                className="mt-2"
                title="Culture/Ethnicity/Faith"
              >
                <div className="container">
                  <div className="row">
                    <div className="col">Identified cultural group:</div>
                    <div className="col">
                      <FormInput
                        name="CulturalGroup"
                        error={errors.CulturalGroup}
                        value={data.CulturalGroup["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("CulturalGroup")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Special cultural traditions/celebrations/rituals:
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Traditions"
                        error={errors.Traditions}
                        value={data.Traditions["@value"]}
                        options={yesNoNoneOptions}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.Traditions["@value"] === "Yes" && (
                        <FormInput
                          name="TraditionsComments"
                          label="Describe:"
                          error={errors.TraditionsComments}
                          value={data.TraditionsComments["@value"]}
                          onChange={handleChange}
                          identical={compare("TraditionsComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">Languages spoken:</div>
                    <div className="col">
                      <FormInput
                        name="LanguagesSpoken"
                        error={errors.LanguagesSpoken}
                        value={data.LanguagesSpoken["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("LanguagesSpoken")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Negative experiences due to
                      customs/ethnicity/religion/race/SES:
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="NegativeCulturalExperience"
                        error={errors.NegativeCulturalExperience}
                        value={data.NegativeCulturalExperience["@value"]}
                        options={yesNoNoneOptions}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.NegativeCulturalExperience["@value"] === "Yes" && (
                        <FormInput
                          name="NegativeCulturalExperienceComments"
                          label="Comments"
                          error={errors.NegativeCulturalExperienceComments}
                          value={
                            data.NegativeCulturalExperienceComments["@value"]
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare(
                            "NegativeCulturalExperienceComments"
                          )}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Are there Important Cultural Factors (religion,
                      ethinicity, customs, races, SES) to consider in SAFY
                      Treatment?
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="ImportantFactors"
                        error={errors.ImportantFactors}
                        value={data.ImportantFactors["@value"]}
                        options={yesNoNoneOptions}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.ImportantFactors["@value"] === "Yes" && (
                        <FormInput
                          name="ImportantFactorsComments"
                          label="Comments"
                          error={errors.ImportantFactorsComments}
                          value={data.ImportantFactorsComments["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("ImportantFactorsComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Does faith play a role in this recipient's life:
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="FaithRole"
                        error={errors.FaithRole}
                        value={data.FaithRole["@value"]}
                        options={yesNoNoneOptions}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.FaithRole["@value"] === "Yes" && (
                        <FormInput
                          name="FaithRoleComments"
                          label="Describe the role of faith in recipient's life (include denomination):"
                          error={errors.FaithRoleComments}
                          value={data.FaithRoleComments["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("FaithRoleComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="DevelopmentalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Developmental History"
              >
                <h5>Mother's Pregnancy History:</h5>
                <FormCheckbox
                  name="PHNoProblems"
                  label="No problems reported"
                  divClassName="ms-3"
                  checked={data.PHNoProblems["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {(data.PHNoProblems["@value"] === undefined ||
                  data.PHNoProblems["@value"] === "false") && (
                  <>
                    <div className="container">
                      <div className="row">
                        <div className="col">Prenatal Care:</div>
                        <div className="col">
                          <FormRadioButton
                            name="PHPrenatalCare"
                            error={errors.PHPrenatalCare}
                            value={data.PHPrenatalCare["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.PHPrenatalCare["@value"] === "No" ? (
                            <FormTextArea
                              name="PHPrenatalCareComments"
                              error={errors.PHPrenatalCareComments}
                              value={data.PHPrenatalCareComments["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("PHPrenatalCareComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Exposure to alcohol, tobacco, or other drugs:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="PHExposure"
                            error={errors.PHExposure}
                            value={data.PHExposure["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.PHExposure["@value"] === "Yes" ? (
                            <FormTextArea
                              name="PHExposureComments"
                              error={errors.PHExposureComments}
                              value={data.PHExposureComments["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("PHExposureComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          History of DV or significant trauma during pregnancy:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="PHTrauma"
                            error={errors.PHTrauma}
                            value={data.PHTrauma["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.PHTrauma["@value"] === "Yes" ? (
                            <FormTextArea
                              name="PHTraumaComments"
                              error={errors.PHTraumaComments}
                              value={data.PHTraumaComments["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("PHTraumaComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Birth complications:</div>
                        <div className="col">
                          <FormRadioButton
                            name="PHComplications"
                            error={errors.PHComplications}
                            value={data.PHComplications["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.PHComplications["@value"] === "Yes" ? (
                            <FormTextArea
                              name="PHComplicationsComments"
                              error={errors.PHComplicationsComments}
                              value={data.PHComplicationsComments["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("PHComplicationsComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Medical concerns:</div>
                        <div className="col">
                          <FormRadioButton
                            name="PHConcerns"
                            error={errors.PHConcerns}
                            value={data.PHConcerns["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.PHConcerns["@value"] === "Yes" ? (
                            <FormTextArea
                              name="PHConcernsComments"
                              error={errors.PHConcernsComments}
                              value={data.PHConcernsComments["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("PHConcernsComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <b>Developmental</b>
                <br />
                <div className="container">
                  <div className="row">
                    <div className="col">
                      Developmental milestones met on time:
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="MilestonesMet"
                        error={errors.MilestonesMet}
                        value={data.MilestonesMet["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.MilestonesMet["@value"] === "No" ? (
                        <FormInput
                          name="MilestonesMetDetails"
                          label="Describe:"
                          error={errors.MilestonesMetDetails}
                          value={data.MilestonesMetDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("MilestonesMetDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Developmental delays or special needs:
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="SpecialNeeds"
                        error={errors.SpecialNeeds}
                        value={data.SpecialNeeds["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.SpecialNeeds["@value"] === "Yes" && (
                        <FormTextArea
                          name="SpecialNeedsDetails"
                          label="Describe:"
                          error={errors.SpecialNeedsDetails}
                          value={data.SpecialNeedsDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("SpecialNeedsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <b>Specialized Services</b> (Speech/Occupational/Physical
                      Therapy/Assistive Devices):
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="SpecializedServices"
                        error={errors.SpecializedServices}
                        value={data.SpecializedServices["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.SpecializedServices["@value"] === "Yes" && (
                        <FormInput
                          name="SpecializedServicesDetails"
                          label="Describe:"
                          error={errors.SpecializedServicesDetails}
                          value={data.SpecializedServicesDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("SpecializedServicesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="Education"
                expandStatus={expandStatus}
                className="mt-2"
                title="Education/Employment"
              >
                <FormRadioButton
                  name="EducationStatus"
                  error={errors.EducationStatus}
                  value={data.EducationStatus["@value"]}
                  options={educationOptions}
                  onChange={handleEducationStatusChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {/* {data.EducationStatus["@value"] === undefined ||
              data.EducationStatus["@value"] ===
                "Currently enrolled in school" ? ( */}
                {data.EducationStatus["@value"] !==
                "Recipient has not started school" ? (
                  <>
                    <h5>Education</h5>
                    <div className="container">
                      <div className="row">
                        <div className="col">School Name:</div>
                        <div className="col">
                          <FormInput
                            name="SchoolName"
                            error={errors.SchoolName}
                            value={data.SchoolName["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("SchoolName")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">Current Grade:</div>
                        <div className="col">
                          <FormInput
                            name="CurrentGrade"
                            error={errors.CurrentGrade}
                            value={data.CurrentGrade["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("CurrentGrade")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Regular Classroom:</div>
                        <div className="col">
                          <FormRadioButton
                            name="RegularClassroom"
                            error={errors.RegularClassroom}
                            value={data.RegularClassroom["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <>Active 504 Plan:</>
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="Active504Plan"
                            error={errors.Active504Plan}
                            value={data.Active504Plan["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormRadioButton
                            name="ActiveIEP"
                            label="Active IEP:"
                            error={errors.ActiveIEP}
                            value={data.ActiveIEP["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormTextArea
                            name="SchoolAttitude"
                            label="Recipient's attitude toward school:"
                            error={errors.SchoolAttitude}
                            value={data.SchoolAttitude["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("SchoolAttitude")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Employment</h5>
                    <FormRadioButton
                      name="CurrentlyEmployed"
                      label="Currently Employed:"
                      error={errors.CurrentlyEmployed}
                      value={data.CurrentlyEmployed["@value"]}
                      options={yesNoNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.CurrentlyEmployed["@value"] === "Yes" && (
                      <>
                        <FormInput
                          name="Employer"
                          label="Name of employer:"
                          error={errors.Employer}
                          value={data.Employer["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("Employer")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="JobTitle"
                          label="Job Title:"
                          error={errors.JobTitle}
                          value={data.JobTitle["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("JobTitle")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                    {data.CurrentlyEmployed["@value"] !== "N/A" && (
                      <FormInput
                        name="EmploymentInterests"
                        label="Employment Interests/Skills/Concerns:"
                        error={errors.EmploymentInterests}
                        value={data.EmploymentInterests["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("EmploymentInterests")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <FormRadioButton
                      name="MilitaryHistory"
                      label="Military History:"
                      error={errors.MilitaryHistory}
                      value={data.MilitaryHistory["@value"]}
                      options={yesNoNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="Activities"
                expandStatus={expandStatus}
                className="mt-2"
                title="Basic Living Skills"
                tooltip="This section is not asking about teaching basic living skills. This section is asking about restoring basic living skills that had been achieved, have since been lost, and now needs to be restored."
              >
                <FormCheckbox
                  name="ADLNA"
                  label="N/A"
                  divClassName="ms-3"
                  checked={data.ADLNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.ADLNA["@value"] !== "true" ? (
                  <>
                    <div className="container">
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mb-2">
                        <div className="col">
                          <b>Skill Areas</b>
                        </div>
                        <div className="col">
                          <b>Skill Achieved</b>
                        </div>
                        <div className="col">
                          <b>Restorative Services Needed</b>
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Personal Hygiene:</div>
                        <div className="col">
                          <FormRadioButton
                            name="Hygiene"
                            error={errors.Hygiene}
                            value={data.Hygiene["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="HygieneNeeded"
                            error={errors.HygieneNeeded}
                            value={data.HygieneNeeded["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Housekeeping:</div>
                        <div className="col">
                          <FormRadioButton
                            name="Housekeeping"
                            error={errors.Housekeeping}
                            value={data.Housekeeping["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="HousekeepingNeeded"
                            error={errors.HousekeepingNeeded}
                            value={data.HousekeepingNeeded["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Medication Management:</div>
                        <div className="col">
                          <FormRadioButton
                            name="MedicationManagement"
                            error={errors.MedicationManagement}
                            value={data.MedicationManagement["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="MedicationManagementNeeded"
                            error={errors.MedicationManagementNeeded}
                            value={data.MedicationManagementNeeded["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Use of Public Transportation:</div>
                        <div className="col">
                          <FormRadioButton
                            name="Transportation"
                            error={errors.Transportation}
                            value={data.Transportation["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="TransportationNeeded"
                            error={errors.TransportationNeeded}
                            value={data.TransportationNeeded["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Behavior Education:</div>
                        <div className="col">
                          <FormRadioButton
                            name="BehaviorEducation"
                            error={errors.BehaviorEducation}
                            value={data.BehaviorEducation["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="BehaviorEducationNeeded"
                            error={errors.BehaviorEducationNeeded}
                            value={data.BehaviorEducationNeeded["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Stress Management:</div>
                        <div className="col">
                          <FormRadioButton
                            name="StressManagement"
                            error={errors.StressManagement}
                            value={data.StressManagement["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="StressManagementNeeded"
                            error={errors.StressManagementNeeded}
                            value={data.StressManagementNeeded["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Healthy Lifestyle:</div>
                        <div className="col">
                          <FormRadioButton
                            name="HealthyLifestyle"
                            error={errors.HealthyLifestyle}
                            value={data.HealthyLifestyle["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="HealthyLifestyleNeeded"
                            error={errors.HealthyLifestyleNeeded}
                            value={data.HealthyLifestyleNeeded["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Laundry:</div>
                        <div className="col">
                          <FormRadioButton
                            name="Laundry"
                            error={errors.Laundry}
                            value={data.Laundry["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="LaundryNeeded"
                            error={errors.LaundryNeeded}
                            value={data.LaundryNeeded["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Meal Preparation:</div>
                        <div className="col">
                          <FormRadioButton
                            name="MealPrep"
                            error={errors.MealPrep}
                            value={data.MealPrep["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="MealPrepNeeded"
                            error={errors.MealPrepNeeded}
                            value={data.MealPrepNeeded["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Money management:</div>
                        <div className="col">
                          <FormRadioButton
                            name="MoneyManagement"
                            error={errors.MoneyManagement}
                            value={data.MoneyManagement["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="MoneyManagementNeeded"
                            error={errors.MoneyManagementNeeded}
                            value={data.MoneyManagementNeeded["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Shopping:</div>
                        <div className="col">
                          <FormRadioButton
                            name="Shopping"
                            error={errors.Shopping}
                            value={data.Shopping["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="ShoppingNeeded"
                            error={errors.ShoppingNeeded}
                            value={data.ShoppingNeeded["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormTextArea
                            name="BasicSkillsComments"
                            label="Comments:"
                            error={errors.BasicSkillsComments}
                            value={data.BasicSkillsComments["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("BasicSkillsComments")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="Friendships"
                expandStatus={expandStatus}
                className="mt-2"
                title="Friendship/ Social Peer Support/ Relationships"
              >
                <FormCheckbox
                  name="RelationshipsNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.RelationshipsNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.RelationshipsNA["@value"] !== "true" ? (
                  <>
                    <div className="container">
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom">
                        <div className="col">
                          <b>Skill</b>
                        </div>
                        <div className="col">
                          <b>Functioning Level</b>
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">Cooperative play skills:</div>
                        <div className="col">
                          <FormRadioButton
                            name="CoopSkills"
                            error={errors.CoopSkills}
                            value={data.CoopSkills["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.CoopSkills["@value"] === "Problematic" ? (
                            <FormInput
                              name="CoopSkillsDetails"
                              label="Describe:"
                              error={errors.CoopSkillsDetails}
                              value={data.CoopSkillsDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("CoopSkillsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">
                          Communication and interaction skills:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="CommunicationSkills"
                            error={errors.CommunicationSkills}
                            value={data.CommunicationSkills["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.CommunicationSkills["@value"] ===
                          "Problematic" ? (
                            <FormInput
                              name="CommunicationSkillsDetails"
                              label="Describe:"
                              error={errors.CommunicationSkillsDetails}
                              value={data.CommunicationSkillsDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("CommunicationSkillsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">
                          Emotional understanding and self-regulation skills:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="EmotionalUnderstanding"
                            error={errors.EmotionalUnderstanding}
                            value={data.EmotionalUnderstanding["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.EmotionalUnderstanding["@value"] ===
                          "Problematic" ? (
                            <FormInput
                              name="EmotionalUnderstandingDetails"
                              label="Describe:"
                              error={errors.EmotionalUnderstandingDetails}
                              value={
                                data.EmotionalUnderstandingDetails["@value"]
                              }
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare(
                                "EmotionalUnderstandingDetails"
                              )}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">
                          Social problem-solving skills:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="ProblemSolving"
                            error={errors.ProblemSolving}
                            value={data.ProblemSolving["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.ProblemSolving["@value"] === "Problematic" ? (
                            <FormInput
                              name="ProblemSolvingDetails"
                              label="Describe:"
                              error={errors.ProblemSolvingDetails}
                              value={data.ProblemSolvingDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("ProblemSolvingDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">Conflict resolution skills:</div>
                        <div className="col">
                          <FormRadioButton
                            name="ConflictResolution"
                            error={errors.ConflictResolution}
                            value={data.ConflictResolution["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.ConflictResolution["@value"] ===
                          "Problematic" ? (
                            <FormInput
                              name="ConflictResolutionDetails"
                              label="Describe:"
                              error={errors.ConflictResolutionDetails}
                              value={data.ConflictResolutionDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("ConflictResolutionDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">Decision making skills:</div>
                        <div className="col">
                          <FormRadioButton
                            name="DecisionMakingSkills"
                            error={errors.DecisionMakingSkills}
                            value={data.DecisionMakingSkills["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.DecisionMakingSkills["@value"] ===
                          "Problematic" ? (
                            <FormInput
                              name="DecisionMakingSkillsDetails"
                              label="Describe:"
                              error={errors.DecisionMakingSkillsDetails}
                              value={data.DecisionMakingSkillsDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("DecisionMakingSkillsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <FormRadioButton
                      name="Bullied"
                      label="History of being bullied:"
                      error={errors.Bullied}
                      value={data.Bullied["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.Bullied["@value"] === "Yes" ? (
                      <FormInput
                        name="BulliedDetails"
                        label="Describe:"
                        error={errors.BulliedDetails}
                        value={data.BulliedDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("BulliedDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="Bully"
                      label="History of bullying others:"
                      error={errors.Bully}
                      value={data.Bully["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.Bully["@value"] === "Yes" ? (
                      <FormInput
                        name="BullyDetails"
                        label="Describe:"
                        error={errors.BullyDetails}
                        value={data.BullyDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("BullyDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="AcceptedByPeers"
                      label="Accepted by peer group:"
                      error={errors.AcceptedByPeers}
                      value={data.AcceptedByPeers["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.AcceptedByPeers["@value"] === "No" && (
                      <FormInput
                        name="AcceptedByPeersDetails"
                        label="Comments:"
                        error={errors.AcceptedByPeersDetails}
                        value={data.AcceptedByPeersDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("AcceptedByPeersDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <FormRadioButton
                      name="NegativeAssociations"
                      label="Negative peer group associations:"
                      error={errors.NegativeAssociations}
                      value={data.NegativeAssociations["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.NegativeAssociations["@value"] === "Yes" && (
                      <FormInput
                        name="NegativeAssociationsDetails"
                        label="Comments:"
                        error={errors.NegativeAssociationsDetails}
                        value={data.NegativeAssociationsDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("NegativeAssociationsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="LegalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Legal History"
              >
                <FormCheckbox
                  name="LegalNA"
                  label="N/A"
                  divClassName="ms-3"
                  checked={data.LegalNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.LegalNA["@value"] !== "true" ? (
                  <>
                    <FormRadioButton
                      name="LegalInvolvement"
                      label="Current involvement with the Legal System:"
                      error={errors.LegalInvolvement}
                      value={data.LegalInvolvement["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.LegalInvolvement["@value"] === "Yes" && (
                      <FormTextArea
                        name="LegalInvolvementDetails"
                        label="Describe:"
                        error={errors.LegalInvolvementDetails}
                        value={data.LegalInvolvementDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("LegalInvolvementDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <FormRadioButton
                      name="DHRInvolvement"
                      label="Current Department of Human Resources (DHR) Involvement with Family:"
                      error={errors.DHRInvolvement}
                      value={data.DHRInvolvement["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.DHRInvolvement["@value"] === "Yes" && (
                      <FormTextArea
                        name="DHRInvolvementDetails"
                        label="Describe:"
                        error={errors.DHRInvolvementDetails}
                        value={data.DHRInvolvementDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("DHRInvolvementDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <div className="row">
                      <div className="col">
                        <FormRadioButton
                          name="DHRCaseworker"
                          label="DHR Caseworker:"
                          error={errors.DHRCaseworker}
                          value={data.DHRCaseworker["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      {data.DHRCaseworker["@value"] === "Yes" && (
                        <>
                          <div className="col clearfix">
                            <div className="float-start">Name:</div>
                            <div className="float-start ms-2">
                              <FormInput
                                name="DHRCaseworkerName"
                                error={errors.DHRCaseworkerName}
                                value={data.DHRCaseworkerName["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("DHRCaseworkerName")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="row">
                      <div className="col">
                        <FormRadioButton
                          name="ProbationOfficer"
                          label="Probation/Parole Officer:"
                          error={errors.ProbationOfficer}
                          value={data.ProbationOfficer["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      {data.ProbationOfficer["@value"] === "Yes" && (
                        <>
                          <div className="col clearfix">
                            <div className="float-start">Name:</div>
                            <div className="float-start ms-2">
                              <FormInput
                                name="ProbationOfficerName"
                                error={errors.ProbationOfficerName}
                                value={data.ProbationOfficerName["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("ProbationOfficerName")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="row">
                      <div className="col">
                        <FormRadioButton
                          name="GALCASA"
                          label="Guardian ad Litem (GAL) or Court Appointed Special Advocate (CASA):"
                          error={errors.GALCASA}
                          value={data.GALCASA["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      {data.GALCASA["@value"] === "Yes" && (
                        <>
                          <div className="col clearfix">
                            <div className="float-start">Name:</div>
                            <div className="float-start ms-2">
                              <FormInput
                                name="GALCASAName"
                                error={errors.GALCASAName}
                                value={data.GALCASAName["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("GALCASAName")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <FormInput
                      name="OtherLegalDetails"
                      label="Other Court/Custody Related Issues:"
                      error={errors.OtherLegalDetails}
                      value={data.OtherLegalDetails["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("OtherLegalDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="MedicalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Medical History/Medications"
              >
                <b>Medical</b>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <FormInput
                        name="PCP"
                        label="Primary Care Physician:"
                        error={errors.PCP}
                        value={data.PCP["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("PCP")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="PCPPhone"
                        label="Phone:"
                        error={errors.PCPPhone}
                        value={data.PCPPhone["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("PCPPhone")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormInput
                        name="LastMedicalExamDate"
                        label="Date of last medical exam:"
                        error={errors.LastMedicalExamDate}
                        value={data.LastMedicalExamDate["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("LastMedicalExamDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="LastDentalExamDate"
                        label="Date of last dental exam:"
                        error={errors.LastDentalExamDate}
                        value={data.LastDentalExamDate["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("LastDentalExamDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Allergies"
                        label="Allergies"
                        divClassName="ms-3"
                        checked={data.Allergies["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Allergies["@value"] === "true" ? (
                        <FormInput
                          name="AllergiesDetails"
                          label="List:"
                          error={errors.AllergiesDetails}
                          value={data.AllergiesDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("AllergiesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="MedicalConditions"
                        label="Medical conditions/concerns"
                        divClassName="ms-3"
                        checked={data.MedicalConditions["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.MedicalConditions["@value"] === "true" ? (
                        <FormInput
                          name="MedicalConditionsDetails"
                          label="List:"
                          error={errors.MedicalConditionsDetails}
                          value={data.MedicalConditionsDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("MedicalConditionsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="NutritionalConcerns"
                        label="Nutritional concerns"
                        divClassName="ms-3"
                        checked={data.NutritionalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.NutritionalConcerns["@value"] === "true" ? (
                        <FormInput
                          name="NutritionalConcernsDetails"
                          label="List:"
                          error={errors.NutritionalConcernsDetails}
                          value={data.NutritionalConcernsDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("NutritionalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="DentalConcerns"
                        label="Dental concerns"
                        divClassName="ms-3"
                        checked={data.DentalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.DentalConcerns["@value"] === "true" ? (
                        <FormInput
                          name="DentalConcernsDetails"
                          label="List:"
                          error={errors.DentalConcernsDetails}
                          value={data.DentalConcernsDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("DentalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                <FormRadioButton
                  name="Undiagnosed"
                  className="mt-2"
                  label="Recipient has symptoms that suggest an undiagnosed medical illness that may be causing or contributing to mental health symptoms:"
                  error={errors.Undiagnosed}
                  value={data.Undiagnosed["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.Undiagnosed["@value"] === "Yes" ? (
                  <FormInput
                    name="UndiagnosedDetails"
                    label="List:"
                    error={errors.UndiagnosedDetails}
                    value={data.UndiagnosedDetails["@value"]}
                    onChange={(e) => {
                      handleChange(e);
                      buildClinicalNarrativeSummary();
                    }}
                    identical={compare("UndiagnosedDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
                <br />
                <b>Current Medications</b>
                <FormCheckbox
                  name="NoMedications"
                  label="Not currently on medications"
                  divClassName="ms-3"
                  checked={data.NoMedications["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoMedications["@value"] !== "true" ? (
                  <FieldTable
                    name="CurrentMedicationDetails"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col py-2 border-end">Medication</div>
                        <div className="col py-2 border-end">Dosage/Use</div>
                        <div className="col py-2">Purpose</div>
                      </div>
                      {data.CurrentMedicationDetails["@value"] &&
                        JSON.parse(data.CurrentMedicationDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col py-2 border-end d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    <FormInput
                                      name="Name"
                                      value={item.Name}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                    ></i>
                                    {item.Name}
                                  </>
                                )}
                              </div>
                              <div className="col py-2 border-end">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Dosage"
                                    value={item.Dosage}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "CurrentMedicationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Dosage
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Purpose"
                                    value={item.Purpose}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "CurrentMedicationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Purpose
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled && (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("CurrentMedicationDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Medication
                      </button>
                    )}
                    {errors.CurrentMedicationDetails && (
                      <div className="alert alert-danger">
                        {errors.CurrentMedicationDetails}
                      </div>
                    )}
                  </FieldTable>
                ) : null}
                <FormRadioButton
                  name="PsychotropicMeds"
                  label="Past psychotropic medications:"
                  error={errors.PsychotropicMeds}
                  value={data.PsychotropicMeds["@value"]}
                  options={yesNoNoneNAOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.PsychotropicMeds["@value"] === "Yes" ? (
                  <FormInput
                    name="PsychotropicMedsDetails"
                    label="Describe:"
                    error={errors.PsychotropicMedsDetails}
                    value={data.PsychotropicMedsDetails["@value"]}
                    onChange={(e) => {
                      handleChange(e);
                      buildClinicalNarrativeSummary();
                    }}
                    identical={compare("PsychotropicMedsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
                <b>
                  Sexual History to Include Pertinent Sexual Issues/Concerns
                </b>
                <FormCheckbox
                  name="SexualHistoryNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.SexualHistoryNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.SexualHistoryNA["@value"] !== "true" ? (
                  <>
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          Gender identity or sexual orientation concerns:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="GenderIdentityConcerns"
                            error={errors.GenderIdentityConcerns}
                            value={data.GenderIdentityConcerns["@value"]}
                            options={yesNoNoneNAOptions}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("GenderIdentityConcerns")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.GenderIdentityConcerns["@value"] === "Yes" ? (
                            <FormInput
                              name="GenderIdentityConcernsDetails"
                              label="Describe:"
                              error={errors.GenderIdentityConcernsDetails}
                              value={
                                data.GenderIdentityConcernsDetails["@value"]
                              }
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare(
                                "GenderIdentityConcernsDetails"
                              )}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Education on healthy sexual behavior/relationships:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SexualEducation"
                            error={errors.SexualEducation}
                            value={data.SexualEducation["@value"]}
                            options={yesNoNoneNAOptions}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("SexualEducation")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Sexually active:</div>
                        <div className="col">
                          <FormRadioButton
                            name="SexuallyActive"
                            error={errors.SexuallyActive}
                            value={data.SexuallyActive["@value"]}
                            options={yesNoNAOptions}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      {data.SexuallyActive["@value"] === "Yes" && (
                        <div className="ms-3">
                          <div className="row">
                            <div className="col-3">
                              Caregiver aware of sexual history:
                            </div>
                            <div className="col">
                              <FormRadioButton
                                name="CaregiverAware"
                                error={errors.CaregiverAware}
                                value={data.CaregiverAware["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-3">Use of protection:</div>
                            <div className="col">
                              <FormRadioButton
                                name="UseProtection"
                                error={errors.UseProtection}
                                value={data.UseProtection["@value"]}
                                options={useOfProtectionOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-3">
                              Awareness of sexual risks:
                            </div>
                            <div className="col">
                              <FormRadioButton
                                name="AwareOfRisks"
                                error={errors.AwareOfRisks}
                                value={data.AwareOfRisks["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-3">Pregnancy:</div>
                            <div className="col">
                              <FormRadioButton
                                name="Pregnancy"
                                error={errors.Pregnancy}
                                value={data.Pregnancy["@value"]}
                                options={pregnancyOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-3">Children:</div>
                            <div className="col">
                              <FormRadioButton
                                name="Children"
                                error={errors.Children}
                                value={data.Children["@value"]}
                                options={yesNoNAOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-3">Comments:</div>
                            <div className="col">
                              <FormInput
                                name="SexuallyActiveComments"
                                error={errors.SexuallyActiveComments}
                                value={data.SexuallyActiveComments["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("SexuallyActiveComments")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="row">
                        <div className="col">Victim of sexual abuse:</div>
                        <div className="col">
                          <FormRadioButton
                            name="SexualAbuseVictim"
                            error={errors.SexualAbuseVictim}
                            value={data.SexualAbuseVictim["@value"]}
                            options={yesNoNoneOptions}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Age inappropriate sexual behavior:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SexualBehavior"
                            error={errors.SexualBehavior}
                            value={data.SexualBehavior["@value"]}
                            options={yesNoNoneOptions}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.SexualBehavior["@value"] === "Yes" && (
                            <FormInput
                              name="SexualBehaviorDetails"
                              label="Comments:"
                              error={errors.SexualBehaviorDetails}
                              value={data.SexualBehaviorDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("SexualBehaviorDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Sexually acting out/reactive behavior:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SexuallyActingOut"
                            error={errors.SexuallyActingOut}
                            value={data.SexuallyActingOut["@value"]}
                            options={yesNoNoneOptions}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.SexuallyActingOut["@value"] === "Yes" ? (
                            <FormTextArea
                              name="SexuallyActingOutComments"
                              label="Comments:"
                              error={errors.SexuallyActingOutComments}
                              value={data.SexuallyActingOutComments["@value"]}
                              onChange={handleChange}
                              identical={compare("SexuallyActingOutComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Sexually abusive/offending behavior:
                        </div>
                        <div className="col">
                          <FormRadioButton
                            name="SexuallyAbusive"
                            error={errors.SexuallyAbusive}
                            value={data.SexuallyAbusive["@value"]}
                            options={yesNoNoneOptions}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          {data.SexuallyAbusive["@value"] === "Yes" ? (
                            <FormTextArea
                              name="SexuallyAbusiveComments"
                              label="Comments:"
                              error={errors.SexuallyAbusiveComments}
                              value={data.SexuallyAbusiveComments["@value"]}
                              onChange={handleChange}
                              identical={compare("SexuallyAbusiveComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="MentalHealthTreatmentHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Mental Health Treatment History"
              >
                <FormCheckbox
                  name="MentalHealthNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.MentalHealthNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.MentalHealthNA["@value"] !== "true" ? (
                  <>
                    <FormRadioButton
                      name="PastMentalHealthTreatment"
                      label="Past mental health counseling/treatment:"
                      error={errors.PastMentalHealthTreatment}
                      value={data.PastMentalHealthTreatment["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="PastMentalHealthPlacement"
                      label="Past placement in a group home/residential treatment facility/psychiatric hospital/unit:"
                      error={errors.PastMentalHealthPlacement}
                      value={data.PastMentalHealthPlacement["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormInput
                      name="PastMentalHealthDiagnosis"
                      label="Previous mental health diagnosis:"
                      error={errors.PastMentalHealthDiagnosis}
                      value={data.PastMentalHealthDiagnosis["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("PastMentalHealthDiagnosis")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.PastMentalHealthTreatment["@value"] === "Yes" ? (
                      <FieldTable
                        name="MentalHealthHistory"
                        commentsHandler={handleComments}
                      >
                        <br />
                        <div className="container border-top border-start border-end mb-2">
                          <div className="row border-bottom">
                            <div className="col-3 py-2 border-end">
                              Agency/Facility
                            </div>
                            <div className="col-2 py-2 border-end">Dates</div>
                            <div className="col-4 py-2 border-end">Reason</div>
                            <div className="col-3 py-2">Outcome</div>
                          </div>
                          {data.MentalHealthHistory["@value"] &&
                            JSON.parse(data.MentalHealthHistory["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col-3 py-2 border-end d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "MentalHealthHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "MentalHealthHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <FormInput
                                          name="Agency"
                                          value={item.Agency}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "MentalHealthHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "MentalHealthHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        {item.Agency}
                                      </>
                                    )}
                                  </div>
                                  <div className="col-2 py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Dates"
                                        value={item.Dates}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "MentalHealthHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Dates
                                    )}
                                  </div>
                                  <div className="col-4 py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Reason"
                                        value={item.Reason}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "MentalHealthHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Reason
                                    )}
                                  </div>
                                  <div className="col-3 py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <FormSelect
                                          name="Outcome"
                                          value={item.Outcome}
                                          options={outcomeOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "MentalHealthHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        {item.Outcome === "Other" ? (
                                          <FormInput
                                            name="OutcomeDetails"
                                            value={item.OutcomeDetails}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "MentalHealthHistory"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        ) : null}
                                      </>
                                    ) : item.OutcomeDetails !== undefined &&
                                      item.OutcomeDetails !== "" ? (
                                      item.OutcomeDetails
                                    ) : (
                                      item.Outcome
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                        </div>
                        {!formDisabled && (
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("MentalHealthHistory", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add Agency/Facility
                          </button>
                        )}
                        {errors.MentalHealthHistory && (
                          <div className="alert alert-danger">
                            {errors.MentalHealthHistory}
                          </div>
                        )}
                      </FieldTable>
                    ) : null}
                    <FormTextArea
                      name="RecipientPerspectiveOfTreatment"
                      label="Recipient's perspective of treatment/counseling:"
                      error={errors.RecipientPerspectiveOfTreatment}
                      value={data.RecipientPerspectiveOfTreatment["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("RecipientPerspectiveOfTreatment")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormTextArea
                      name="CaregiverPerspectiveOfTreatment"
                      label="Caregiver's perspective of treatment/counseling:"
                      error={errors.CaregiverPerspectiveOfTreatment}
                      value={data.CaregiverPerspectiveOfTreatment["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("CaregiverPerspectiveOfTreatment")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="SubstanceUseHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Alcohol/Drug Use History"
              >
                <FormCheckbox
                  name="SubstanceUseNA"
                  label="No current or past use/experimentation/abuse of alcohol and/or drugs"
                  divClassName="ms-3"
                  checked={data.SubstanceUseNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleSubstanceUseNAChange}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="DeniesSubstanceUse"
                  label="Recipient denies current or past use/experimentation/abuse of alcohol and/or drugs"
                  divClassName="ms-3"
                  checked={data.DeniesSubstanceUse["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="CurrentSubstanceUse"
                  label="Recipient reports current use/experimentation/abuse of alcohol and/or drugs"
                  divClassName="ms-3"
                  checked={data.CurrentSubstanceUse["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="PastSubstanceUse"
                  label="Recipient reports past use/experimentation/abuse of alcohol and/or drugs"
                  divClassName="ms-3"
                  checked={data.PastSubstanceUse["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="SuspectedSubstanceUse"
                  label="Caregiver suspects or has concerns about potential use by Recipient"
                  divClassName="ms-3"
                  checked={data.SuspectedSubstanceUse["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="SubstanceUseEvidence"
                  label="Caregiver has found evidence of substance related paraphernalia belonging to Recipient"
                  divClassName="ms-3"
                  checked={data.SubstanceUseEvidence["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="SubstanceUseByPeers"
                  label="Recipient associates with peers who use alcohol and/or drugs"
                  divClassName="ms-3"
                  checked={data.SubstanceUseByPeers["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.SubstanceUseNA["@value"] !== "true" &&
                data.DeniesSubstanceUse["@value"] !== "true" ? (
                  <>
                    Current drug or alcohol use:
                    <FieldTable
                      name="SubstanceUseHistory"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            Substance Type
                          </div>
                          <div className="col py-2 border-end">Status</div>
                          <div className="col py-2 border-end">
                            Pattern of Use
                          </div>
                          <div className="col py-2 border-end">First Use</div>
                          <div className="col py-2 border-end">Last Use</div>
                          <div className="col py-2 border-end">Frequency</div>
                          <div className="col py-2 border-end">Amount</div>
                          <div className="col py-2">Method</div>
                        </div>
                        {data.SubstanceUseHistory["@value"] &&
                          JSON.parse(data.SubstanceUseHistory["@value"]).map(
                            (item, index) => (
                              <>
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col py-2 border-end d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <FormInput
                                          name="SubstanceType"
                                          value={item.SubstanceType}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        {item.SubstanceType}
                                      </>
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Status"
                                        value={item.Status}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Status
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Pattern"
                                        value={item.Pattern}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Pattern
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="FirstUse"
                                        value={item.FirstUse}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.FirstUse
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="LastUse"
                                        value={item.LastUse}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.LastUse
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Frequency"
                                        value={item.Frequency}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Frequency
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Amount"
                                        value={item.Amount}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Amount
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Method"
                                        value={item.Method}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Method
                                    )}
                                  </div>
                                </div>
                              </>
                            )
                          )}
                      </div>
                      {!formDisabled && (
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("SubstanceUseHistory", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Substance
                        </button>
                      )}
                      {errors.SubstanceUseHistory && (
                        <div className="alert alert-danger">
                          {errors.SubstanceUseHistory}
                        </div>
                      )}
                    </FieldTable>
                    <FormRadioButton
                      name="SubstanceUseTreatment"
                      label="Medical treatment/hospitalization due to use:"
                      error={errors.SubstanceUseTreatment}
                      value={data.SubstanceUseTreatment["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SubstanceUseTreatment["@value"] === "Yes" ? (
                      <FormInput
                        name="SubstanceUseTreatmentDetails"
                        label="Describe:"
                        error={errors.SubstanceUseTreatmentDetails}
                        value={data.SubstanceUseTreatmentDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="SubstanceUseWithdrawal"
                      label="Withdrawal:"
                      error={errors.SubstanceUseWithdrawal}
                      value={data.SubstanceUseWithdrawal["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SubstanceUseWithdrawal["@value"] === "Yes" ? (
                      <FormInput
                        name="SubstanceUseWithdrawalDetails"
                        label="Describe:"
                        error={errors.SubstanceUseWithdrawalDetails}
                        value={data.SubstanceUseWithdrawalDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="SubstanceUseDetox"
                      label="Presenting detox issues:"
                      error={errors.SubstanceUseDetox}
                      value={data.SubstanceUseDetox["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SubstanceUseDetox["@value"] === "Yes" ? (
                      <FormInput
                        name="SubstanceUseDetoxDetails"
                        label="Describe:"
                        error={errors.SubstanceUseDetoxDetails}
                        value={data.SubstanceUseDetoxDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="SubstanceUseOverdose"
                      label="Overdose history:"
                      error={errors.SubstanceUseOverdose}
                      value={data.SubstanceUseOverdose["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SubstanceUseOverdose["@value"] === "Yes" ? (
                      <FormInput
                        name="SubstanceUseOverdoseDetails"
                        label="Describe:"
                        error={errors.SubstanceUseOverdoseDetails}
                        value={data.SubstanceUseOverdoseDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="SubstanceUseLegal"
                      label="Legal charges/involvement due to use:"
                      error={errors.SubstanceUseLegal}
                      value={data.SubstanceUseLegal["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SubstanceUseLegal["@value"] === "Yes" ? (
                      <FormInput
                        name="SubstanceUseLegalDetails"
                        label="Describe:"
                        error={errors.SubstanceUseLegalDetails}
                        value={data.SubstanceUseLegalDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="SubstanceUseFamilyProblems"
                      label="Use has caused problems with family or friends:"
                      error={errors.SubstanceUseFamilyProblems}
                      value={data.SubstanceUseFamilyProblems["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SubstanceUseFamilyProblems["@value"] === "Yes" ? (
                      <FormInput
                        name="SubstanceUseFamilyProblemsDetails"
                        label="Describe:"
                        error={errors.SubstanceUseFamilyProblemsDetails}
                        value={data.SubstanceUseFamilyProblemsDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="SubstanceUseSchoolProblems"
                      label="Use has caused problems with school or work:"
                      error={errors.SubstanceUseSchoolProblems}
                      value={data.SubstanceUseSchoolProblems["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SubstanceUseSchoolProblems["@value"] === "Yes" ? (
                      <FormInput
                        name="SubstanceUseSchoolProblemsDetails"
                        label="Describe:"
                        error={errors.SubstanceUseSchoolProblemsDetails}
                        value={data.SubstanceUseSchoolProblemsDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormInput
                      name="RecipientPerception"
                      label="Recipient's perception of substance use:"
                      error={errors.RecipientPerception}
                      value={data.RecipientPerception["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("RecipientPerception")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormInput
                      name="CaregiverPerception"
                      label="Caregiver's perception of Recipient's substance use:"
                      error={errors.CaregiverPerception}
                      value={data.CaregiverPerception["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("CaregiverPerception")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <br />
                    <b>Alcohol/Drug Treatment History</b>
                    <FormCheckbox
                      name="NoPastTreatment"
                      label="No past treatment"
                      divClassName="ms-3"
                      checked={data.NoPastTreatment["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    {data.NoPastTreatment["@value"] !== "true" ? (
                      <FieldTable
                        name="SubstanceUseTreatmentHistory"
                        commentsHandler={handleComments}
                      >
                        <br />
                        <div className="container border-top border-start border-end mb-2">
                          <div className="row border-bottom">
                            <div className="col-3 py-2 border-end">
                              Agency/Facility
                            </div>
                            <div className="col-3 py-2 border-end">Dates</div>
                            <div className="col-6 py-2">
                              Summary of Services/Treatment
                            </div>
                          </div>
                          {data.SubstanceUseTreatmentHistory["@value"] &&
                            JSON.parse(
                              data.SubstanceUseTreatmentHistory["@value"]
                            ).map((item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col-3 py-2 border-end d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "SubstanceUseTreatmentHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "SubstanceUseTreatmentHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      <FormInput
                                        name="Agency"
                                        value={item.Agency}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseTreatmentHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "SubstanceUseTreatmentHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      {item.Agency}
                                    </>
                                  )}
                                </div>
                                <div className="col-3 py-2 border-end">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="Dates"
                                      value={item.Dates}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SubstanceUseTreatmentHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Dates
                                  )}
                                </div>
                                <div className="col-6 py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormTextArea
                                      name="Summary"
                                      value={item.Summary}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SubstanceUseTreatmentHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Summary
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                        {!formDisabled && (
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("SubstanceUseTreatmentHistory", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add Agency/Facility
                          </button>
                        )}
                        {errors.SubstanceUseTreatmentHistory && (
                          <div className="alert alert-danger">
                            {errors.SubstanceUseTreatmentHistory}
                          </div>
                        )}
                      </FieldTable>
                    ) : null}
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="TraumaHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Trauma History"
              >
                <FormCheckbox
                  name="TraumaNA"
                  label="No history of abuse or trauma"
                  divClassName="ms-3"
                  checked={data.TraumaNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.TraumaNA["@value"] !== "true" ? (
                  <>
                    <FieldTable
                      name="TraumaHistory"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col-3 py-2 border-end">Trauma</div>
                          <div className="col-3 py-2 border-end">Status</div>
                          <div className="col-6 py-2">Comments</div>
                        </div>
                        {data.TraumaHistory["@value"] &&
                          JSON.parse(data.TraumaHistory["@value"]).map(
                            (item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col-3 py-2 border-end d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(index, "TraumaHistory");
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "TraumaHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      <FormSelect
                                        name="Trauma"
                                        value={item.Trauma}
                                        options={traumaOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "TraumaHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                      {item.Trauma === "Other" ? (
                                        <FormInput
                                          name="TraumaDetails"
                                          value={item.TraumaDetails}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "TraumaHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : null}
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(index, "TraumaHistory");
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      {item.TraumaDetails !== undefined &&
                                      item.TraumaDetails !== ""
                                        ? item.TraumaDetails
                                        : item.Trauma}
                                    </>
                                  )}
                                </div>
                                <div className="col-3 py-2 border-end">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormSelect
                                      name="Status"
                                      value={item.Status}
                                      options={statusOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "TraumaHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Status
                                  )}
                                </div>
                                <div className="col-6 py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormTextArea
                                      name="Comments"
                                      value={item.Comments}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "TraumaHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Comments
                                  )}
                                </div>
                              </div>
                            )
                          )}
                      </div>
                      {!formDisabled && (
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("TraumaHistory", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Trauma
                        </button>
                      )}
                      {errors.TraumaHistory && (
                        <div className="alert alert-danger">
                          {errors.TraumaHistory}
                        </div>
                      )}
                    </FieldTable>
                    <br />
                    <b>Trauma Symptoms/Responses:</b>
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="DistressingMemories"
                            label="Distressing memories"
                            divClassName="ms-3"
                            checked={
                              data.DistressingMemories["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="BlockOut"
                            label="Blocking out events"
                            divClassName="ms-3"
                            checked={data.BlockOut["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Hypervigilance"
                            label="Hypervigilance"
                            divClassName="ms-3"
                            checked={data.Hypervigilance["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="PhysiologicalResponses"
                            label="Physiological responses"
                            divClassName="ms-3"
                            checked={
                              data.PhysiologicalResponses["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="LossOfMemories"
                            label="Loss of memories"
                            divClassName="ms-3"
                            checked={data.LossOfMemories["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Hyperarousal"
                            label="Hyperarousal"
                            divClassName="ms-3"
                            checked={data.Hyperarousal["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="SleepDisturbances"
                            label="Sleep disturbances"
                            divClassName="ms-3"
                            checked={
                              data.SleepDisturbances["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="DevelopmentalRegression"
                            label="Developmental regression"
                            divClassName="ms-3"
                            checked={
                              data.DevelopmentalRegression["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="Nightmares"
                            label="Frightening dreams"
                            divClassName="ms-3"
                            checked={data.Nightmares["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Avoidant"
                            label="Avoidant behaviors"
                            divClassName="ms-3"
                            checked={data.Avoidant["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="DifficultyConcentratingSymptom"
                            label="Difficulty concentrating"
                            divClassName="ms-3"
                            checked={
                              data.DifficultyConcentratingSymptom["@value"] ===
                              "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="UnhealthyBoundaries"
                            label="Unhealthy boundaries"
                            divClassName="ms-3"
                            checked={
                              data.UnhealthyBoundaries["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="Flashbacks"
                            label="Flashbacks"
                            divClassName="ms-3"
                            checked={data.Flashbacks["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Detachment"
                            label="Detachment"
                            divClassName="ms-3"
                            checked={data.Detachment["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="EmotionalDysregulation"
                            label="Emotional dysregulation"
                            divClassName="ms-3"
                            checked={
                              data.EmotionalDysregulation["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="RepetitivePlayOfEvents"
                            label="Repetitive play of events"
                            divClassName="ms-3"
                            checked={
                              data.RepetitivePlayOfEvents["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="PsychologicalDistress"
                            label="Psychological distress"
                            divClassName="ms-3"
                            checked={
                              data.PsychologicalDistress["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="DiminishedInterests"
                            label="Diminished interests"
                            divClassName="ms-3"
                            checked={
                              data.DiminishedInterests["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Dissocation"
                            label="Dissocation"
                            divClassName="ms-3"
                            checked={data.Dissocation["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="NegativeCognitions"
                            label="Negative cognitions"
                            divClassName="ms-3"
                            checked={
                              data.NegativeCognitions["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-3">
                          <FormCheckbox
                            name="SocialWithdrawalSymptom"
                            label="Social withdrawal"
                            divClassName="ms-3"
                            checked={
                              data.SocialWithdrawalSymptom["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col-3">
                          <FormCheckbox
                            name="NoSymptoms"
                            label="None reported"
                            divClassName="ms-3"
                            checked={
                              data.NoSymptoms &&
                              data.NoSymptoms["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    {errors.TraumaSymptomsResponse !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.TraumaSymptomsResponse}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <br />
                    <b>Protective Factors:</b>
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="SocialSupports"
                            label="Social supports"
                            divClassName="ms-3"
                            checked={data.SocialSupports["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="SenseOfMastery"
                            label="Sense of mastery"
                            divClassName="ms-3"
                            checked={data.SenseOfMastery["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="CaregiverStability"
                            label="Caregiver stability"
                            divClassName="ms-3"
                            checked={
                              data.CaregiverStability["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="StrongSpiritualBeliefs"
                            label="Strong spiritual beliefs"
                            divClassName="ms-3"
                            checked={
                              data.StrongSpiritualBeliefs["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="InterpersonalSkills"
                            label="Interpersonal skills"
                            divClassName="ms-3"
                            checked={
                              data.InterpersonalSkills["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="PositiveOutlook"
                            label="Positive outlook"
                            divClassName="ms-3"
                            checked={data.PositiveOutlook["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="PlacementStability"
                            label="Placement stability"
                            divClassName="ms-3"
                            checked={
                              data.PlacementStability["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Pets"
                            label="Pets"
                            divClassName="ms-3"
                            checked={data.Pets["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="ValidatingExperiences"
                            label="Validating experiences"
                            divClassName="ms-3"
                            checked={
                              data.ValidatingExperiences["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Resources"
                            label="Resources"
                            divClassName="ms-3"
                            checked={data.Resources["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="TiesToExtendedFamily"
                            label="Ties to extended family"
                            divClassName="ms-3"
                            checked={
                              data.TiesToExtendedFamily["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="Adaptability"
                            label="Adaptability"
                            divClassName="ms-3"
                            checked={data.Adaptability["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="SecureAttachments"
                            label="Secure attachments"
                            divClassName="ms-3"
                            checked={
                              data.SecureAttachments["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="StrongCulturalIdentity"
                            label="Strong cultural identity"
                            divClassName="ms-3"
                            checked={
                              data.StrongCulturalIdentity["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <FormCheckbox
                            name="NoTraumaProtectiveFactors"
                            label="None reported"
                            divClassName="ms-3"
                            checked={
                              data.NoTraumaProtectiveFactors &&
                              data.NoTraumaProtectiveFactors["@value"] ===
                                "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col"></div>
                      </div>
                    </div>
                    {errors.TraumaProtectiveFactors !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.TraumaProtectiveFactors}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <FormInput
                      name="CopingStrategies"
                      label="Coping strategies:"
                      error={errors.CopingStrategies}
                      value={data.CopingStrategies["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("CopingStrategies")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="RecipientAware"
                      label="Recipient is aware of traumatic events/reports:"
                      error={errors.RecipientAware}
                      value={data.RecipientAware["@value"]}
                      options={traumaAwarenessOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormInput
                      name="RecipientTraumaPerspective"
                      label="Recipient's perspective of trauma:"
                      error={errors.RecipientTraumaPerspective}
                      value={data.RecipientTraumaPerspective["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("RecipientTraumaPerspective")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormInput
                      name="CaregiverTraumaPerspective"
                      label="Caregiver's perspective of trauma:"
                      error={errors.CaregiverTraumaPerspective}
                      value={data.CaregiverTraumaPerspective["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("CaregiverTraumaPerspective")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="MentalStatus"
                expandStatus={expandStatus}
                className="mt-2"
                title="Mental Status"
              >
                <div className="container">
                  <div className="row border-bottom">
                    <div className="col-3">Appearance</div>
                    <div className="col-9 d-flex flex-wrap">
                      <FormCheckbox
                        name="AppropriateAppearance"
                        label="Appropriate"
                        divClassName="ms-3"
                        checked={
                          data.AppropriateAppearance["@value"] === "true"
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Neat"
                        label="Neat"
                        divClassName="ms-3"
                        checked={data.Neat["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Disheveled"
                        label="Disheveled"
                        divClassName="ms-3"
                        checked={data.Disheveled["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="BizarreAppearance"
                        label="Bizarre"
                        divClassName="ms-3"
                        checked={data.BizarreAppearance["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="OtherAppearance"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherAppearance["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherAppearance["@value"] === "true" ? (
                        <FormInput
                          name="OtherAppearanceDetails"
                          label="Comments:"
                          error={errors.OtherAppearanceDetails}
                          value={data.OtherAppearanceDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherAppearanceDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSAppearance !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSAppearance}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Behavior</div>
                    <div className="col-9 d-flex flex-wrap">
                      <FormCheckbox
                        name="Cooperative"
                        label="Cooperative"
                        divClassName="ms-3"
                        checked={data.Cooperative["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Guarded"
                        label="Guarded"
                        divClassName="ms-3"
                        checked={data.Guarded["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Hyperactive"
                        label="Hyperactive"
                        divClassName="ms-3"
                        checked={data.Hyperactive["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Agitated"
                        label="Agitated"
                        divClassName="ms-3"
                        checked={data.Agitated["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Paranoid"
                        label="Paranoid"
                        divClassName="ms-3"
                        checked={data.Paranoid["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Tics"
                        label="Tics"
                        divClassName="ms-3"
                        checked={data.Tics["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Impulsive"
                        label="Impulsive"
                        divClassName="ms-3"
                        checked={data.Impulsive["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Aggressive"
                        label="Aggressive"
                        divClassName="ms-3"
                        checked={data.Aggressive["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Withdrawn"
                        label="Withdrawn"
                        divClassName="ms-3"
                        checked={data.Withdrawn["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="BizarreBehavior"
                        label="Bizarre"
                        divClassName="ms-3"
                        checked={data.BizarreBehavior["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Rigidity"
                        label="Rigidity"
                        divClassName="ms-3"
                        checked={data.Rigidity["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="OtherBehavior"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherBehavior["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherBehavior["@value"] === "true" ? (
                        <FormInput
                          name="OtherBehaviorDetails"
                          label="Comments:"
                          error={errors.OtherBehaviorDetails}
                          value={data.OtherBehaviorDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherBehaviorDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSBehavior !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSBehavior}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Speech</div>
                    <div className="col-9 d-flex flex-wrap">
                      <FormCheckbox
                        name="Normal"
                        label="Normal"
                        divClassName="ms-3"
                        checked={data.Normal["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Slurred"
                        label="Slurred"
                        divClassName="ms-3"
                        checked={data.Slurred["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Pressured"
                        label="Pressured"
                        divClassName="ms-3"
                        checked={data.Pressured["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Tangential"
                        label="Tangential"
                        divClassName="ms-3"
                        checked={data.Tangential["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Incoherent"
                        label="Incoherent"
                        divClassName="ms-3"
                        checked={data.Incoherent["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Stutters"
                        label="Stutters"
                        divClassName="ms-3"
                        checked={data.Stutters["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="OtherSpeech"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherSpeech["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherSpeech["@value"] === "true" ? (
                        <FormInput
                          name="OtherSpeechDetails"
                          label="Comments:"
                          error={errors.OtherSpeechDetails}
                          value={data.OtherSpeechDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherSpeechDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSSpeech !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSSpeech}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Mood</div>
                    <div className="col-9 d-flex flex-wrap">
                      <FormCheckbox
                        name="Euthymic"
                        label="Euthymic"
                        divClassName="ms-3"
                        checked={data.Euthymic["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Anxious"
                        label="Anxious"
                        divClassName="ms-3"
                        checked={data.Anxious["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Angry"
                        label="Angry"
                        divClassName="ms-3"
                        checked={data.Angry["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Depressed"
                        label="Depressed"
                        divClassName="ms-3"
                        checked={data.Depressed["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Euphoric"
                        label="Euphoric"
                        divClassName="ms-3"
                        checked={data.Euphoric["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Irritable"
                        label="Irritable"
                        divClassName="ms-3"
                        checked={data.Irritable["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Fearful"
                        label="Fearful"
                        divClassName="ms-3"
                        checked={data.Fearful["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="OtherMood"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherMood["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherMood["@value"] === "true" ? (
                        <FormInput
                          name="OtherMoodDetails"
                          label="Comments:"
                          error={errors.OtherMoodDetails}
                          value={data.OtherMoodDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherMoodDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSMood !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSMood}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Affect</div>
                    <div className="col-9 d-flex flex-wrap">
                      <FormCheckbox
                        name="FullRange"
                        label="Full range"
                        divClassName="ms-3"
                        checked={data.FullRange["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Restricted"
                        label="Restricted"
                        divClassName="ms-3"
                        checked={data.Restricted["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Blunted"
                        label="Blunted"
                        divClassName="ms-3"
                        checked={data.Blunted["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Flat"
                        label="Flat"
                        divClassName="ms-3"
                        checked={data.Flat["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Inappropriate"
                        label="Inappropriate"
                        divClassName="ms-3"
                        checked={data.Inappropriate["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="OtherAffect"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherAffect["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherAffect["@value"] === "true" ? (
                        <FormInput
                          name="OtherAffectDetails"
                          label="Comments:"
                          error={errors.OtherAffectDetails}
                          value={data.OtherAffectDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherAffectDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSAffect !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSAffect}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Thought Process/Perception</div>
                    <div className="col-9 d-flex flex-wrap">
                      <FormCheckbox
                        name="Logical"
                        label="Logical"
                        divClassName="ms-3"
                        checked={data.Logical["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Preoccupations"
                        label="Preoccupations"
                        divClassName="ms-3"
                        checked={data.Preoccupations["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Delusions"
                        label="Delusions"
                        divClassName="ms-3"
                        checked={data.Delusions["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Hallucinations"
                        label="Hallucinations"
                        divClassName="ms-3"
                        checked={data.Hallucinations["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Racing"
                        label="Racing"
                        divClassName="ms-3"
                        checked={data.Racing["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Disorganized"
                        label="Disorganized"
                        divClassName="ms-3"
                        checked={data.Disorganized["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Concrete"
                        label="Concrete"
                        divClassName="ms-3"
                        checked={data.Concrete["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="SuicidalIdeation"
                        label="Suicidal Ideation"
                        divClassName="ms-3"
                        checked={data.SuicidalIdeation["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="HomicidalIdeation"
                        label="Homicidal Ideation"
                        divClassName="ms-3"
                        checked={data.HomicidalIdeation["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="OtherPerception"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherPerception["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherPerception["@value"] === "true" ? (
                        <FormInput
                          name="OtherPerceptionDetails"
                          label="Comments:"
                          error={errors.OtherPerceptionDetails}
                          value={data.OtherPerceptionDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherPerceptionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSPerception !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSPerception}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">
                      Cognition/Intellectual Functioning
                    </div>
                    <div className="col-9 d-flex flex-wrap">
                      <FormCheckbox
                        name="NoImpairment"
                        label="No Impairment"
                        divClassName="ms-3"
                        checked={data.NoImpairment["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="ShortAttentionSpan"
                        label="Short Attention Span"
                        divClassName="ms-3"
                        checked={data.ShortAttentionSpan["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="DifficultyConcentrating"
                        label="Difficulty Concentrating"
                        divClassName="ms-3"
                        checked={
                          data.DifficultyConcentrating["@value"] === "true"
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Distractibility"
                        label="Distractibility"
                        divClassName="ms-3"
                        checked={data.Distractibility["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="MemoryImpairment"
                        label="Memory Impairment"
                        divClassName="ms-3"
                        checked={data.MemoryImpairment["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="LowIntelligence"
                        label="Low Intelligence"
                        divClassName="ms-3"
                        checked={data.LowIntelligence["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="OtherCognition"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherCognition["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherCognition["@value"] === "true" ? (
                        <FormInput
                          name="OtherCognitionDetails"
                          label="Comments:"
                          error={errors.OtherCognitionDetails}
                          value={data.OtherCognitionDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherCognitionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSCognition !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSCognition}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Insight/Judgment</div>
                    <div className="col-9 d-flex flex-wrap">
                      <FormCheckbox
                        name="AppropriateInsight"
                        label="Appropriate"
                        divClassName="ms-3"
                        checked={data.AppropriateInsight["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Fair"
                        label="Fair"
                        divClassName="ms-3"
                        checked={data.Fair["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="Impaired"
                        label="Impaired"
                        divClassName="ms-3"
                        checked={data.Impaired["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <FormCheckbox
                        name="OtherInsight"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherInsight["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherInsight["@value"] === "true" ? (
                        <FormInput
                          name="OtherInsightDetails"
                          label="Comments:"
                          error={errors.OtherInsightDetails}
                          value={data.OtherInsightDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherInsightDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSInsight !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSInsight}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2">
                    <div className="col-3">Comments/Observations</div>
                    <div className="col-9">
                      <FormTextArea
                        name="MentalStatusComments"
                        error={errors.MentalStatusComments}
                        value={data.MentalStatusComments["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("MentalStatusComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="Lethality"
                expandStatus={expandStatus}
                className="mt-2"
                title="Lethality"
              >
                <FormCheckbox
                  name="LethalityNA"
                  label="N/A"
                  divClassName="ms-3"
                  checked={data.LethalityNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.LethalityNA["@value"] !== "true" ? (
                  <>
                    <b>Suicide/Harm to Self</b>
                    <FormRadioButton
                      name="SelfAbusiveBehavior"
                      label="Self-injurious or self-abusive behavior:"
                      error={errors.SelfAbusiveBehavior}
                      value={data.SelfAbusiveBehavior["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SelfAbusiveBehavior["@value"] !== "None/Never" ? (
                      <FormInput
                        name="SelfAbusiveBehaviorDetails"
                        label="Describe:"
                        error={errors.SelfAbusiveBehaviorDetails}
                        value={data.SelfAbusiveBehaviorDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SelfAbusiveBehaviorDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="SuicidalThreats"
                      label="Suicidal threats/gestures:"
                      error={errors.SuicidalThreats}
                      value={data.SuicidalThreats["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SuicidalThreats["@value"] !== "None/Never" ? (
                      <FormInput
                        name="SuicidalThreatsDetails"
                        label="Describe:"
                        error={errors.SuicidalThreatsDetails}
                        value={data.SuicidalThreatsDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SuicidalThreatsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="SuicideAttempt"
                      label="Suicide attempt:"
                      error={errors.SuicideAttempt}
                      value={data.SuicideAttempt["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SuicideAttempt["@value"] !== "None/Never" ? (
                      <FormInput
                        name="SuicideAttemptDetails"
                        label="Describe:"
                        error={errors.SuicideAttemptDetails}
                        value={data.SuicideAttemptDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SuicideAttemptDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    {data.SuicidalThreats["@value"] !== "None/Never" ||
                    data.SuicideAttempt["@value"] !== "None/Never" ? (
                      <>
                        <i>
                          Based on the responses to these questions, please
                          complete the lifetime{" "}
                          <Link
                            to={`/client/${clientid}?tab=cpass`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            C-SSRS Assessment
                          </Link>
                          .
                        </i>
                        <br />
                      </>
                    ) : null}
                    {(data.SuicidalThreats["@value"] !== "None/Never" &&
                      data.SuicidalThreats["@value"] !==
                        "More Than 6 Months Ago") ||
                    (data.SuicideAttempt["@value"] !== "None/Never" &&
                      data.SuicideAttempt["@value"] !==
                        "More Than 6 Months Ago") ? (
                      <>
                        <i>
                          Based on the responses to these questions, please
                          complete a{" "}
                          <Link
                            to={`/client/${clientid}?tab=forms`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Safety Plan
                          </Link>
                          .
                        </i>
                        <br />
                      </>
                    ) : null}
                    <FormRadioButton
                      name="CaregiverConcernsOfSelfHarm"
                      label="Parent/Caregiver concerns of suicide/self-harm:"
                      error={errors.CaregiverConcernsOfSelfHarm}
                      value={data.CaregiverConcernsOfSelfHarm["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.CaregiverConcernsOfSelfHarm["@value"] === "Yes" ? (
                      <FormInput
                        name="CaregiverConcernsOfSelfHarmDetails"
                        label="Describe:"
                        error={errors.CaregiverConcernsOfSelfHarmDetails}
                        value={
                          data.CaregiverConcernsOfSelfHarmDetails["@value"]
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare(
                          "CaregiverConcernsOfSelfHarmDetails"
                        )}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="OtherConcernsOfSelfHarm"
                      label="Other expressed concerns of suicide/self-harm:"
                      error={errors.OtherConcernsOfSelfHarm}
                      value={data.OtherConcernsOfSelfHarm["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.OtherConcernsOfSelfHarm["@value"] === "Yes" ? (
                      <FormInput
                        name="OtherConcernsOfSelfHarmDetails"
                        label="Describe:"
                        error={errors.OtherConcernsOfSelfHarmDetails}
                        value={data.OtherConcernsOfSelfHarmDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OtherConcernsOfSelfHarmDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    {data.SelfAbusiveBehavior["@value"] !== "None/Never" ||
                    data.SuicidalThreats["@value"] !== "None/Never" ||
                    data.SuicideAttempt["@value"] !== "None/Never" ? (
                      <>
                        <b>Risk Factors</b>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="PriorAttempt"
                                label="Prior suicide attempt(s)"
                                divClassName="ms-3"
                                checked={data.PriorAttempt["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="FamilyHistory"
                                label="Family history of suicide"
                                divClassName="ms-3"
                                checked={
                                  data.FamilyHistory["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="AccessToMeans"
                                label="Access to lethal means"
                                divClassName="ms-3"
                                checked={
                                  data.AccessToMeans["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Exposure"
                                label="Exposure to another person's suicide"
                                divClassName="ms-3"
                                checked={data.Exposure["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="HealthCondition"
                                label="Serious or chronic health condition and/or pain"
                                divClassName="ms-3"
                                checked={
                                  data.HealthCondition["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="ElevatingBeliefs"
                                label="Cultural or religious beliefs elevating suicide"
                                divClassName="ms-3"
                                checked={
                                  data.ElevatingBeliefs["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="MentalHealthDisorder"
                                label="Mental health disorder"
                                divClassName="ms-3"
                                checked={
                                  data.MentalHealthDisorder["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="OtherRiskFactors"
                                label="Other"
                                divClassName="ms-3"
                                checked={
                                  data.OtherRiskFactors["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="NoRiskFactors"
                                label="None reported"
                                divClassName="ms-3"
                                checked={
                                  data.NoRiskFactors["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.OtherRiskFactors["@value"] === "true" ? (
                                <FormInput
                                  name="OtherRiskFactorsDetails"
                                  label="Describe risk factors:"
                                  error={errors.OtherRiskFactorsDetails}
                                  value={
                                    data.OtherRiskFactorsDetails !== undefined
                                      ? data.OtherRiskFactorsDetails["@value"]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare("OtherRiskFactorsDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                          {errors.RiskFactors !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.RiskFactors}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                        <br />
                      </>
                    ) : null}
                    <b>Protective Factors</b>
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="ClinicalCare"
                            label="Effective clinical care for mental, physical, and substance abuse disorders"
                            divClassName="ms-3"
                            checked={data.ClinicalCare["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="Connected"
                            label="Connectedness to individuals, family, community, and social institutions"
                            divClassName="ms-3"
                            checked={data.Connected["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="Skills"
                            label="Skills in problem solving, conflict resolution, and nonviolent ways of handling disputes"
                            divClassName="ms-3"
                            checked={data.Skills["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="CaregiverContacts"
                            label="Contacts with caregivers"
                            divClassName="ms-3"
                            checked={
                              data.CaregiverContacts["@value"] === "true"
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="DiscouragingBeliefs"
                            label="Cultural and religious beliefs that discourage suicide"
                            divClassName="ms-3"
                            checked={
                              data.DiscouragingBeliefs["@value"] === "true"
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormCheckbox
                            name="NoSelfHarmProtectiveFactors"
                            label="None reported"
                            divClassName="ms-3"
                            checked={
                              data.NoSelfHarmProtectiveFactors["@value"] ===
                              "true"
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      {errors.SelfHarmProtectiveFactors !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.SelfHarmProtectiveFactors}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <br />
                    {data.SelfAbusiveBehavior["@value"] !== "None/Never" ||
                    data.SuicidalThreats["@value"] !== "None/Never" ||
                    data.SuicideAttempt["@value"] !== "None/Never" ? (
                      <>
                        <b>Current Warning Signs</b>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="Threatening"
                                label="Threatening to hurt or kill oneself"
                                divClassName="ms-3"
                                checked={data.Threatening["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="SayingGoodbye"
                                label="Visiting or calling people to say goodbye"
                                divClassName="ms-3"
                                checked={
                                  data.SayingGoodbye["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="SeekingMeans"
                                label="Seeking a means to kill oneself"
                                divClassName="ms-3"
                                checked={data.SeekingMeans["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="SocialWithdrawal"
                                label="Social withdrawal/isolation"
                                divClassName="ms-3"
                                checked={
                                  data.SocialWithdrawal["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="Hopelessness"
                                label="Hopelessness"
                                divClassName="ms-3"
                                checked={data.Hopelessness["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="RecklessBehavior"
                                label="Reckless behavior/increase in risky activities"
                                divClassName="ms-3"
                                checked={
                                  data.RecklessBehavior["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="IncreasedSubstanceUse"
                                label="Increasing alcohol or drug use"
                                divClassName="ms-3"
                                checked={
                                  data.IncreasedSubstanceUse["@value"] ===
                                  "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Feelings"
                                label="Feelings of alienation, loneliness, guilt, shame, or inadequacy"
                                divClassName="ms-3"
                                checked={data.Feelings["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="DramaticMoodChanges"
                                label="Dramatic mood changes"
                                divClassName="ms-3"
                                checked={
                                  data.DramaticMoodChanges["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="GivingAwayPossessions"
                                label="Giving away prized possessions"
                                divClassName="ms-3"
                                checked={
                                  data.GivingAwayPossessions["@value"] ===
                                  "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="NoWarningSigns"
                                label="None reported"
                                divClassName="ms-3"
                                checked={
                                  data.NoWarningSigns &&
                                  data.NoWarningSigns["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          {errors.WarningSigns !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.WarningSigns}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </>
                    ) : null}
                    <FormRadioButton
                      name="SelfHarmSafetyPlanStatus"
                      label="Safety plan status:"
                      error={errors.SelfHarmSafetyPlanStatus}
                      value={data.SelfHarmSafetyPlanStatus["@value"]}
                      options={safetyPlanStatusOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <b>Current Risk of Harm to Self:</b>
                    <FormSelect
                      name="CurrentSelfHarmRisk"
                      error={errors.CurrentSelfHarmRisk}
                      value={data.CurrentSelfHarmRisk["@value"]}
                      options={riskOptions}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormTextArea
                      name="CurrentSelfHarmRiskComments"
                      label="Comments/Plan:"
                      error={errors.CurrentSelfHarmRiskComments}
                      value={data.CurrentSelfHarmRiskComments["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("CurrentSelfHarmRiskComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <br />
                    <b>Harm to Others/Endangerment</b>
                    <FormRadioButton
                      name="ThreatsToHarmOthers"
                      label="Threats to intentionally harm/endanger others:"
                      error={errors.ThreatsToHarmOthers}
                      value={data.ThreatsToHarmOthers["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ThreatsToHarmOthers["@value"] !== "None/Never" && (
                      <FormInput
                        name="ThreatsToHarmOthersDetails"
                        label="Describe:"
                        error={errors.ThreatsToHarmOthersDetails}
                        value={data.ThreatsToHarmOthersDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ThreatsToHarmOthersDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <FormRadioButton
                      name="ThoughtsOfHarm"
                      label="Thoughts of intentionally harming/endangering others:"
                      error={errors.ThoughtsOfHarm}
                      value={data.ThoughtsOfHarm["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ThoughtsOfHarm["@value"] !== "None/Never" ? (
                      <FormInput
                        name="ThoughtsOfHarmDetails"
                        label="Describe:"
                        error={errors.ThoughtsOfHarmDetails}
                        value={data.ThoughtsOfHarmDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ThoughtsOfHarmDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <FormRadioButton
                      name="ActsOfHarm"
                      label="Acts of intentionally harm/endangering others:"
                      error={errors.ActsOfHarm}
                      value={data.ActsOfHarm["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ActsOfHarm["@value"] !== "None/Never" && (
                      <FormInput
                        name="ActsOfHarmDetails"
                        label="Describe:"
                        error={errors.ActsOfHarmDetails}
                        value={data.ActsOfHarmDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ActsOfHarmDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <FormRadioButton
                      name="ActsOfHarmAnimals"
                      label="Acts of intentionally harm/endangering animals:"
                      error={errors.ActsOfHarmAnimals}
                      value={data.ActsOfHarmAnimals["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ActsOfHarmAnimals["@value"] !== "None/Never" && (
                      <FormInput
                        name="ActsOfHarmAnimalsDetails"
                        label="Describe:"
                        error={errors.ActsOfHarmAnimalsDetails}
                        value={data.ActsOfHarmAnimalsDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ActsOfHarmAnimalsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <FormRadioButton
                      name="ActsInvolvingWeapons"
                      label="Acts involving weapons:"
                      error={errors.ActsInvolvingWeapons}
                      value={data.ActsInvolvingWeapons["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ActsInvolvingWeapons["@value"] !== "None/Never" && (
                      <FormInput
                        name="ActsInvolvingWeaponsDetails"
                        label="Describe:"
                        error={errors.ActsInvolvingWeaponsDetails}
                        value={data.ActsInvolvingWeaponsDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ActsInvolvingWeaponsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    {data.ThreatsToHarmOthers["@value"] !== "None/Never" ||
                    data.ThoughtsOfHarm["@value"] !== "None/Never" ||
                    data.ActsOfHarm["@value"] !== "None/Never" ||
                    data.ActsOfHarmAnimals["@value"] !== "None/Never" ||
                    data.ActsInvolvingWeapons["@value"] !== "None/Never" ? (
                      <>
                        Based on the responses to these questions, please
                        complete a{" "}
                        <Link
                          to={`/client/${clientid}?tab=forms`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Safety Plan
                        </Link>
                        .
                      </>
                    ) : null}
                    <FormRadioButton
                      name="CaregiverConcernOfOtherHarm"
                      label="Parent/Caregiver concerns of harm/endangering others:"
                      error={errors.CaregiverConcernOfOtherHarm}
                      value={data.CaregiverConcernOfOtherHarm["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.CaregiverConcernOfOtherHarm["@value"] !== "Yes" && (
                      <FormInput
                        name="CaregiverConcernOfOtherHarmDetails"
                        label="Describe:"
                        error={errors.CaregiverConcernOfOtherHarmDetails}
                        value={
                          data.CaregiverConcernOfOtherHarmDetails["@value"]
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare(
                          "CaregiverConcernOfOtherHarmDetails"
                        )}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <FormRadioButton
                      name="OtherConcernsOfOtherHarm"
                      label="Other expressed concerns of harm/endangering others:"
                      error={errors.OtherConcernsOfOtherHarm}
                      value={data.OtherConcernsOfOtherHarm["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.OtherConcernsOfOtherHarm["@value"] !== "Yes" && (
                      <FormInput
                        name="OtherConcernsOfOtherHarmDetails"
                        label="Describe:"
                        error={errors.OtherConcernsOfOtherHarmDetails}
                        value={data.OtherConcernsOfOtherHarmDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OtherConcernsOfOtherHarmDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <FormRadioButton
                      name="OtherHarmSafetyPlanStatus"
                      label="Safety plan status:"
                      error={errors.OtherHarmSafetyPlanStatus}
                      value={data.OtherHarmSafetyPlanStatus["@value"]}
                      options={safetyPlanStatusOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <b>Current Risk of Harm to Others:</b>
                    <FormSelect
                      name="CurrentOtherHarmRisk"
                      error={errors.CurrentOtherHarmRisk}
                      value={data.CurrentOtherHarmRisk["@value"]}
                      options={riskOptions}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FormTextArea
                      name="CurrentOtherHarmRiskDetails"
                      label="Comments/Plan:"
                      error={errors.CurrentOtherHarmRiskDetails}
                      value={data.CurrentOtherHarmRiskDetails["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("CurrentOtherHarmRiskDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="SymptomChecklist"
                expandStatus={expandStatus}
                className="mt-2"
                title="Symptom Checklist"
              >
                Mark if symptoms of diagnostic category are present. Describe
                symptom <b>frequency, intensity and duration</b> if present to
                assist in formulating diagnosis.
                <br />
                <div className="border rounded mb-2 px-2 pt-2 mt-2">
                  <FormCheckbox
                    name="TraumaSymptoms"
                    label="Symptoms of Trauma and Other Stressor-Related Disorders"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.TraumaSymptoms !== undefined &&
                      data.TraumaSymptoms["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handleTraumaSymptomsChange}
                    disabled={formDisabled}
                  />
                  {data.TraumaSymptoms !== undefined &&
                    data.TraumaSymptoms["@value"] === "true" && (
                      <>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="TraumaticExposure"
                                label="Exposure to a traumatic event"
                                divClassName="ms-3"
                                checked={
                                  data.TraumaticExposure !== undefined &&
                                  data.TraumaticExposure["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="AvoidantTrauma"
                                label="Persistent avoidant behavior"
                                divClassName="ms-3"
                                checked={
                                  data.AvoidantTrauma !== undefined &&
                                  data.AvoidantTrauma["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="HypervigilanceTrauma"
                                label="Hypervigilance"
                                divClassName="ms-3"
                                checked={
                                  data.HypervigilanceTrauma !== undefined &&
                                  data.HypervigilanceTrauma["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="IntrusiveDreams"
                                label="Intrusive dreams/memories"
                                divClassName="ms-3"
                                checked={
                                  data.IntrusiveDreams !== undefined &&
                                  data.IntrusiveDreams["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="NegativeThoughts"
                                label="Negative thoughts and mood"
                                divClassName="ms-3"
                                checked={
                                  data.NegativeThoughts !== undefined &&
                                  data.NegativeThoughts["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="CocentrationProblems"
                                label="Problems with concentration"
                                divClassName="ms-3"
                                checked={
                                  data.CocentrationProblems !== undefined &&
                                  data.CocentrationProblems["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="RecklessBehaviorTrauma"
                                label="Irritable or reckless behavior"
                                divClassName="ms-3"
                                checked={
                                  data.RecklessBehaviorTrauma !== undefined &&
                                  data.RecklessBehaviorTrauma["@value"] ===
                                    "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="SleepDisturbancesTrauma"
                                label="Sleep disturbances"
                                divClassName="ms-3"
                                checked={
                                  data.SleepDisturbancesTrauma !== undefined &&
                                  data.SleepDisturbancesTrauma["@value"] ===
                                    "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="OtherTrauma"
                                label="Other:"
                                divClassName="ms-3"
                                checked={
                                  data.OtherTrauma !== undefined &&
                                  data.OtherTrauma["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                              {data.OtherTrauma !== undefined &&
                              data.OtherTrauma["@value"] === "true" ? (
                                <FormInput
                                  name="OtherTraumaDetails"
                                  error={errors.OtherTraumaDetails}
                                  value={
                                    data.OtherTraumaDetails !== undefined
                                      ? data.OtherTraumaDetails["@value"]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare("OtherTraumaDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {errors.TraumaSymptoms !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.TraumaSymptoms}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <FormInput
                          name="TraumaSymptomsFrequency"
                          label="Frequency:"
                          error={errors.TraumaSymptomsFrequency}
                          value={
                            data.TraumaSymptomsFrequency !== undefined
                              ? data.TraumaSymptomsFrequency["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("TraumaSymptomsFrequency")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="TraumaSymptomsIntensity"
                          label="Intensity:"
                          error={errors.TraumaSymptomsIntensity}
                          value={
                            data.TraumaSymptomsIntensity !== undefined
                              ? data.TraumaSymptomsIntensity["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("TraumaSymptomsIntensity")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="TraumaSymptomsDuration"
                          label="Duration:"
                          error={errors.TraumaSymptomsDuration}
                          value={
                            data.TraumaSymptomsDuration !== undefined
                              ? data.TraumaSymptomsDuration["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("TraumaSymptomsDuration")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="TraumaSymptomsOnset"
                          label="Onset:"
                          error={errors.TraumaSymptomsOnset}
                          value={
                            data.TraumaSymptomsOnset !== undefined
                              ? data.TraumaSymptomsOnset["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("TraumaSymptomsOnset")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        Diagnoses to Consider: Post-traumatic Stress Disorder
                        (PTSD), Acute Stress Disorder, Adjustment Disorder,
                        Other Specified Trauma-and Stressor-Related Disorder
                      </>
                    )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="AnxietyDisorder"
                    label="Symptoms of Anxiety"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.AnxietyDisorder !== undefined &&
                      data.AnxietyDisorder["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handleAnxietyDisorderChange}
                    disabled={formDisabled}
                  />
                  {data.AnxietyDisorder !== undefined &&
                    data.AnxietyDisorder["@value"] === "true" && (
                      <>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="AnxietyDisorderExcessWorry"
                                label="Excess worry"
                                divClassName="ms-3"
                                checked={
                                  data.AnxietyDisorderExcessWorry !==
                                    undefined &&
                                  data.AnxietyDisorderExcessWorry["@value"] ===
                                    "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="AnxietyDisorderEasilyFatigued"
                                label="Easily fatigued"
                                divClassName="ms-3"
                                checked={
                                  data.AnxietyDisorderEasilyFatigued !==
                                    undefined &&
                                  data.AnxietyDisorderEasilyFatigued[
                                    "@value"
                                  ] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="MuscleTension"
                                label="Muscle tension"
                                divClassName="ms-3"
                                checked={
                                  data.MuscleTension !== undefined &&
                                  data.MuscleTension["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="Overworried"
                                label="Difficulty controlling worry"
                                divClassName="ms-3"
                                checked={
                                  data.Overworried !== undefined &&
                                  data.Overworried["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="AnxietyDisorderDifficultyConcentrating"
                                label="Difficulty concentrating"
                                divClassName="ms-3"
                                checked={
                                  data.AnxietyDisorderDifficultyConcentrating !==
                                    undefined &&
                                  data.AnxietyDisorderDifficultyConcentrating[
                                    "@value"
                                  ] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="SleepDisturbancesAnxiety"
                                label="Sleep difficulties"
                                divClassName="ms-3"
                                checked={
                                  data.SleepDisturbancesAnxiety !== undefined &&
                                  data.SleepDisturbancesAnxiety["@value"] ===
                                    "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="RestlessAnxiety"
                                label="Restless"
                                divClassName="ms-3"
                                checked={
                                  data.RestlessAnxiety !== undefined &&
                                  data.RestlessAnxiety["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Irritability"
                                label="Irritability"
                                divClassName="ms-3"
                                checked={
                                  data.Irritability !== undefined &&
                                  data.Irritability["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="OtherAnxiety"
                                label="Other:"
                                divClassName="ms-3"
                                checked={
                                  data.OtherAnxiety !== undefined &&
                                  data.OtherAnxiety["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                              {data.OtherAnxiety !== undefined &&
                              data.OtherAnxiety["@value"] === "true" ? (
                                <FormInput
                                  name="OtherAnxietyDetails"
                                  error={errors.OtherAnxietyDetails}
                                  value={
                                    data.OtherAnxietyDetails !== undefined
                                      ? data.OtherAnxietyDetails["@value"]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare("OtherAnxietyDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {errors.AnxietyDisorder !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.AnxietyDisorder}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <FormInput
                          name="AnxietyDisorderFrequency"
                          label="Frequency:"
                          error={errors.AnxietyDisorderFrequency}
                          value={
                            data.AnxietyDisorderFrequency !== undefined
                              ? data.AnxietyDisorderFrequency["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("AnxietyDisorderFrequency")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="AnxietyDisorderIntensity"
                          label="Intensity:"
                          error={errors.AnxietyDisorderIntensity}
                          value={
                            data.AnxietyDisorderIntensity !== undefined
                              ? data.AnxietyDisorderIntensity["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("AnxietyDisorderIntensity")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="AnxietyDisorderDuration"
                          label="Duration:"
                          error={errors.AnxietyDisorderDuration}
                          value={
                            data.AnxietyDisorderDuration !== undefined
                              ? data.AnxietyDisorderDuration["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("AnxietyDisorderDuration")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        Diagnosis to consider: Generalized Anxiety Disorder
                      </>
                    )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="DepressionSymptoms"
                    label="Symptoms of Depression"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.DepressionSymptoms !== undefined &&
                      data.DepressionSymptoms["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handleDepressionChange}
                    disabled={formDisabled}
                  />
                  {data.DepressionSymptoms !== undefined &&
                    data.DepressionSymptoms["@value"] === "true" && (
                      <>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="DepressedMood"
                                label="Depressed mood most of the day, nearly every day"
                                divClassName="ms-3"
                                checked={
                                  data.DepressedMood !== undefined &&
                                  data.DepressedMood["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="SleepChanges"
                                label="Insomnia or hypersomnia"
                                divClassName="ms-3"
                                checked={
                                  data.SleepChanges !== undefined &&
                                  data.SleepChanges["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Guilt"
                                label="Feelings of worthlessness/guilt"
                                divClassName="ms-3"
                                checked={
                                  data.Guilt !== undefined &&
                                  data.Guilt["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="DiminishedInterest"
                                label="Diminished interest or pleasure in most daily activities"
                                divClassName="ms-3"
                                checked={
                                  data.DiminishedInterest !== undefined &&
                                  data.DiminishedInterest["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="PurposelessMovement"
                                label="Purposeless movement"
                                divClassName="ms-3"
                                checked={
                                  data.PurposelessMovement !== undefined &&
                                  data.PurposelessMovement["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="DifficultyConcentratingDepression"
                                label="Inability to think/concentrate"
                                divClassName="ms-3"
                                checked={
                                  data.DifficultyConcentratingDepression !==
                                    undefined &&
                                  data.DifficultyConcentratingDepression[
                                    "@value"
                                  ] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="WeightChanges"
                                label="Significant weight loss or weight gain"
                                divClassName="ms-3"
                                checked={
                                  data.WeightChanges !== undefined &&
                                  data.WeightChanges["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="FatigueDepression"
                                label="Fatigue"
                                divClassName="ms-3"
                                checked={
                                  data.FatigueDepression !== undefined &&
                                  data.FatigueDepression["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="ThoughtsOfDeath"
                                label="Recurrent thoughts of death"
                                divClassName="ms-3"
                                checked={
                                  data.ThoughtsOfDeath !== undefined &&
                                  data.ThoughtsOfDeath["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-4">
                              <FormCheckbox
                                name="OtherDepression"
                                label="Other:"
                                divClassName="ms-3"
                                checked={
                                  data.OtherDepression !== undefined &&
                                  data.OtherDepression["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                              {data.OtherDepression !== undefined &&
                              data.OtherDepression["@value"] === "true" ? (
                                <FormInput
                                  name="OtherDepressionDetails"
                                  error={errors.OtherDepressionDetails}
                                  value={
                                    data.OtherDepressionDetails !== undefined
                                      ? data.OtherDepressionDetails["@value"]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare("OtherDepressionDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {errors.DepressionSymptoms !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.DepressionSymptoms}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <FormInput
                          name="DepressionFrequency"
                          label="Frequency:"
                          error={errors.DepressionFrequency}
                          value={
                            data.DepressionFrequency !== undefined
                              ? data.DepressionFrequency["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("DepressionFrequency")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="DepressionIntensity"
                          label="Intensity:"
                          error={errors.DepressionIntensity}
                          value={
                            data.DepressionIntensity !== undefined
                              ? data.DepressionIntensity["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("DepressionIntensity")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="DepressionDuration"
                          label="Duration:"
                          error={errors.DepressionDuration}
                          value={
                            data.DepressionDuration !== undefined
                              ? data.DepressionDuration["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("DepressionDuration")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        Diagnosis to consider: Major Depressive Disorder, Other
                        Specified Depressive Disorder, Unspecified Depressive
                        Disorder
                      </>
                    )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="AttentionHyperactivity"
                    label="Symptoms of Attention and Hyperactivity"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.AttentionHyperactivity !== undefined &&
                      data.AttentionHyperactivity["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handleAttentionHyperactivityChange}
                    disabled={formDisabled}
                  />
                  {data.AttentionHyperactivity !== undefined &&
                    data.AttentionHyperactivity["@value"] === "true" && (
                      <>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="AttentionHyperactivityEasilyDistracted"
                                label="Easily distracted"
                                divClassName="ms-3"
                                checked={
                                  data.AttentionHyperactivityEasilyDistracted !==
                                    undefined &&
                                  data.AttentionHyperactivityEasilyDistracted[
                                    "@value"
                                  ] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="DifficultyFollowThrough"
                                label="Difficulty following-through"
                                divClassName="ms-3"
                                checked={
                                  data.DifficultyFollowThrough !== undefined &&
                                  data.DifficultyFollowThrough["@value"] ===
                                    "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Forgetful"
                                label="Forgetful"
                                divClassName="ms-3"
                                checked={
                                  data.Forgetful !== undefined &&
                                  data.Forgetful["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="DifficultyListening"
                                label="Difficulty listening"
                                divClassName="ms-3"
                                checked={
                                  data.DifficultyListening !== undefined &&
                                  data.DifficultyListening["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="DisorganizedAttention"
                                label="Disorganized in tasks and activities"
                                divClassName="ms-3"
                                checked={
                                  data.DisorganizedAttention !== undefined &&
                                  data.DisorganizedAttention["@value"] ===
                                    "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Fidgety"
                                label="Fidgety"
                                divClassName="ms-3"
                                checked={
                                  data.Fidgety !== undefined &&
                                  data.Fidgety["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="LackOfDetails"
                                label="Lack of attention to detail"
                                divClassName="ms-3"
                                checked={
                                  data.LackOfDetails !== undefined &&
                                  data.LackOfDetails["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="LosesThings"
                                label="Loses things"
                                divClassName="ms-3"
                                checked={
                                  data.LosesThings !== undefined &&
                                  data.LosesThings["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="InterruptsOthers"
                                label="Interrupts others"
                                divClassName="ms-3"
                                checked={
                                  data.InterruptsOthers !== undefined &&
                                  data.InterruptsOthers["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="UnableToSitStill"
                                label="Unable to sit still"
                                divClassName="ms-3"
                                checked={
                                  data.UnableToSitStill !== undefined &&
                                  data.UnableToSitStill["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="ExcessiveTalking"
                                label="Excessive talking"
                                divClassName="ms-3"
                                checked={
                                  data.ExcessiveTalking !== undefined &&
                                  data.ExcessiveTalking["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="DifficultyWaiting"
                                label="Difficulty waiting for their turn"
                                divClassName="ms-3"
                                checked={
                                  data.DifficultyWaiting !== undefined &&
                                  data.DifficultyWaiting["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-4">
                              <FormCheckbox
                                name="OtherAttention"
                                label="Other:"
                                divClassName="ms-3"
                                checked={
                                  data.OtherAttention !== undefined &&
                                  data.OtherAttention["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                              {data.OtherAttention !== undefined &&
                              data.OtherAttention["@value"] === "true" ? (
                                <FormInput
                                  name="OtherAttentionDetails"
                                  error={errors.OtherAttentionDetails}
                                  value={
                                    data.OtherAttentionDetails !== undefined
                                      ? data.OtherAttentionDetails["@value"]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare("OtherAttentionDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {errors.AttentionHyperactivity !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.AttentionHyperactivity}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <FormInput
                          name="AttentionHyperactivityFrequency"
                          label="Frequency:"
                          error={errors.AttentionHyperactivityFrequency}
                          value={
                            data.AttentionHyperactivityFrequency !== undefined
                              ? data.AttentionHyperactivityFrequency["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("AttentionHyperactivityFrequency")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="AttentionHyperactivityIntensity"
                          label="Intensity:"
                          error={errors.AttentionHyperactivityIntensity}
                          value={
                            data.AttentionHyperactivityIntensity !== undefined
                              ? data.AttentionHyperactivityIntensity["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("AttentionHyperactivityIntensity")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="AttentionHyperactivityDuration"
                          label="Duration:"
                          error={errors.AttentionHyperactivityDuration}
                          value={
                            data.AttentionHyperactivityDuration !== undefined
                              ? data.AttentionHyperactivityDuration["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("AttentionHyperactivityDuration")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        Diagnosis to consider: Attention-Deficit/Hyperactivity
                        Disorder
                      </>
                    )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="SU"
                    label="Symptoms of Substance Use Disorders"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.SU !== undefined && data.SU["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handleSubstanceUseSymptomsChange}
                    disabled={formDisabled}
                  />
                  {data.SU !== undefined && data.SU["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="PatternOfSubstanceUse"
                              label="Takes substances in large amounts and/or over long periods of time"
                              divClassName="ms-3"
                              checked={
                                data.PatternOfSubstanceUse !== undefined &&
                                data.PatternOfSubstanceUse["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DesiresCutDownUse"
                              label="Persistent desire to cut down on substance use"
                              divClassName="ms-3"
                              checked={
                                data.DesiresCutDownUse !== undefined &&
                                data.DesiresCutDownUse["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="TimeSpentObtaining"
                              label="Spends a significant amount of time obtaining substance(s)"
                              divClassName="ms-3"
                              checked={
                                data.TimeSpentObtaining !== undefined &&
                                data.TimeSpentObtaining["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="Cravings"
                              label="Cravings for substances"
                              divClassName="ms-3"
                              checked={
                                data.Cravings !== undefined &&
                                data.Cravings["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="FailureOfResponsibilities"
                              label="Failure to fulfill major roles at work, school, or home"
                              divClassName="ms-3"
                              checked={
                                data.FailureOfResponsibilities !== undefined &&
                                data.FailureOfResponsibilities["@value"] ===
                                  "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="NegativeImpact"
                              label="Use consistently negatively impacts relationships"
                              divClassName="ms-3"
                              checked={
                                data.NegativeImpact !== undefined &&
                                data.NegativeImpact["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="AbandonsActivities"
                              label="Giving up/reducing previously enjoyed activities"
                              divClassName="ms-3"
                              checked={
                                data.AbandonsActivities !== undefined &&
                                data.AbandonsActivities["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Tolerance"
                              label="Tolerance"
                              divClassName="ms-3"
                              checked={
                                data.Tolerance !== undefined &&
                                data.Tolerance["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="SUWithdrawal"
                              label="Withdrawal"
                              divClassName="ms-3"
                              checked={
                                data.SUWithdrawal !== undefined &&
                                data.SUWithdrawal["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="SUOther"
                              label="Other:"
                              divClassName="ms-3"
                              checked={
                                data.SUOther !== undefined &&
                                data.SUOther["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                            {data.SUOther !== undefined &&
                            data.SUOther["@value"] === "true" ? (
                              <FormInput
                                name="SUOtherDetails"
                                error={errors.SUOtherDetails}
                                value={
                                  data.SUOtherDetails !== undefined
                                    ? data.SUOtherDetails["@value"]
                                    : ""
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("SUOtherDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.SU !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.SU}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="SubstanceUseFrequency"
                        label="Frequency:"
                        error={errors.SubstanceUseFrequency}
                        value={
                          data.SubstanceUseFrequency !== undefined
                            ? data.SubstanceUseFrequency["@value"]
                            : ""
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SubstanceUseFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="SubstanceUseIntensity"
                        label="Intensity:"
                        error={errors.SubstanceUseIntensity}
                        value={
                          data.SubstanceUseIntensity !== undefined
                            ? data.SubstanceUseIntensity["@value"]
                            : ""
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SubstanceUseIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="SubstanceUseDuration"
                        label="Duration:"
                        error={errors.SubstanceUseDuration}
                        value={
                          data.SubstanceUseDuration !== undefined
                            ? data.SubstanceUseDuration["@value"]
                            : ""
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SubstanceUseDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="DMDD"
                    label="Symptoms of Disruptive Mood Dysregulation Disorder"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.DMDD !== undefined && data.DMDD["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handleDMDDChange}
                    disabled={formDisabled}
                  />
                  {data.DMDD !== undefined &&
                    data.DMDD["@value"] === "true" && (
                      <>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="Temper"
                                label="Severe and recurrent temper outbursts (verbally and/or behaviorally)"
                                divClassName="ms-3"
                                checked={
                                  data.Temper !== undefined &&
                                  data.Temper["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="OutsideOutbursts"
                                label="Outbursts are outside of developmental level"
                                divClassName="ms-3"
                                checked={
                                  data.OutsideOutbursts !== undefined &&
                                  data.OutsideOutbursts["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="FrequentOutbursts"
                                label="Outbursts occur three or more times per week"
                                divClassName="ms-3"
                                checked={
                                  data.FrequentOutbursts !== undefined &&
                                  data.FrequentOutbursts["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="DMDDIrritable"
                                label="Irritable/angry mood most of the day"
                                divClassName="ms-3"
                                checked={
                                  data.DMDDIrritable !== undefined &&
                                  data.DMDDIrritable["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="OutburstsAroundOthers"
                                label="Symptoms occur in home, school and/or with peers"
                                divClassName="ms-3"
                                checked={
                                  data.OutburstsAroundOthers !== undefined &&
                                  data.OutburstsAroundOthers["@value"] ===
                                    "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="DMDDOther"
                                label="Other:"
                                divClassName="ms-3"
                                checked={
                                  data.DMDDOther !== undefined &&
                                  data.DMDDOther["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                              {data.DMDDOther !== undefined &&
                              data.DMDDOther["@value"] === "true" ? (
                                <FormInput
                                  name="DMDDOtherDetails"
                                  error={errors.DMDDOtherDetails}
                                  value={
                                    data.DMDDOtherDetails !== undefined
                                      ? data.DMDDOtherDetails["@value"]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare("DMDDOtherDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {errors.DMDD !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.DMDD}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <FormInput
                          name="DMDDFrequency"
                          label="Frequency:"
                          error={errors.DMDDFrequency}
                          value={
                            data.DMDDFrequency !== undefined
                              ? data.DMDDFrequency["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("DMDDFrequency")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="DMDDIntensity"
                          label="Intensity:"
                          error={errors.DMDDIntensity}
                          value={
                            data.DMDDIntensity !== undefined
                              ? data.DMDDIntensity["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("DMDDIntensity")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="DMDDDuration"
                          label="Duration:"
                          error={errors.DMDDDuration}
                          value={
                            data.DMDDDuration !== undefined
                              ? data.DMDDDuration["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("DMDDDuration")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="ConductDisorder"
                    label="Symptoms of Conduct Disorder"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.ConductDisorder !== undefined &&
                      data.ConductDisorder["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handleConductDisorderChange}
                    disabled={formDisabled}
                  />
                  {data.ConductDisorder !== undefined &&
                    data.ConductDisorder["@value"] === "true" && (
                      <>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="Aggression"
                                label="Aggression to people and/or animals"
                                divClassName="ms-3"
                                checked={
                                  data.Aggression !== undefined &&
                                  data.Aggression["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="DestructionOfProperty"
                                label="Destruction of Property"
                                divClassName="ms-3"
                                checked={
                                  data.DestructionOfProperty !== undefined &&
                                  data.DestructionOfProperty["@value"] ===
                                    "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Theft"
                                label="Deceitfulness or theft"
                                divClassName="ms-3"
                                checked={
                                  data.Theft !== undefined &&
                                  data.Theft["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-4">
                              <FormCheckbox
                                name="SeriousViolations"
                                label="Serious violations of rules"
                                divClassName="ms-3"
                                checked={
                                  data.SeriousViolations !== undefined &&
                                  data.SeriousViolations["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col-4">
                              <FormCheckbox
                                name="OtherConductDisorder"
                                label="Other:"
                                divClassName="ms-3"
                                checked={
                                  data.OtherConductDisorder !== undefined &&
                                  data.OtherConductDisorder["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                              {data.OtherConductDisorder !== undefined &&
                              data.OtherConductDisorder["@value"] === "true" ? (
                                <FormInput
                                  name="OtherConductDisorderDetails"
                                  error={errors.OtherConductDisorderDetails}
                                  value={
                                    data.OtherConductDisorderDetails !==
                                    undefined
                                      ? data.OtherConductDisorderDetails[
                                          "@value"
                                        ]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare(
                                    "OtherConductDisorderDetails"
                                  )}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {errors.ConductDisorder !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.ConductDisorder}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <FormInput
                          name="ConductDisorderFrequency"
                          label="Frequency:"
                          error={errors.ConductDisorderFrequency}
                          value={
                            data.ConductDisorderFrequency !== undefined
                              ? data.ConductDisorderFrequency["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("ConductDisorderFrequency")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="ConductDisorderIntensity"
                          label="Intensity:"
                          error={errors.ConductDisorderIntensity}
                          value={
                            data.ConductDisorderIntensity !== undefined
                              ? data.ConductDisorderIntensity["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("ConductDisorderIntensity")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="ConductDisorderDuration"
                          label="Duration:"
                          error={errors.ConductDisorderDuration}
                          value={
                            data.ConductDisorderDuration !== undefined
                              ? data.ConductDisorderDuration["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("ConductDisorderDuration")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="ODD"
                    label="Symptoms of Oppositional Defiance Disorder (ODD)"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.ODD !== undefined && data.ODD["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handleODDChange}
                    disabled={formDisabled}
                  />
                  {data.ODD !== undefined && data.ODD["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="IrritableODD"
                              label="Angry/irritable mood"
                              divClassName="ms-3"
                              checked={
                                data.IrritableODD !== undefined &&
                                data.IrritableODD["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Argumentative"
                              label="Argumentative/defiant"
                              divClassName="ms-3"
                              checked={
                                data.Argumentative !== undefined &&
                                data.Argumentative["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Vindictive"
                              label="Vindictive"
                              divClassName="ms-3"
                              checked={
                                data.Vindictive !== undefined &&
                                data.Vindictive["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="OtherODD"
                              label="Other:"
                              divClassName="ms-3"
                              checked={
                                data.OtherODD !== undefined &&
                                data.OtherODD["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                            {data.OtherODD !== undefined &&
                            data.OtherODD["@value"] === "true" ? (
                              <FormInput
                                name="OtherODDDetails"
                                error={errors.OtherODDDetails}
                                value={
                                  data.OtherODDDetails !== undefined
                                    ? data.OtherODDDetails["@value"]
                                    : ""
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("OtherODDDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.ODD !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.ODD}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="ODDFrequency"
                        label="Frequency:"
                        error={errors.ODDFrequency}
                        value={
                          data.ODDFrequency !== undefined
                            ? data.ODDFrequency["@value"]
                            : ""
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ODDFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="ODDIntensity"
                        label="Intensity:"
                        error={errors.ODDIntensity}
                        value={
                          data.ODDIntensity !== undefined
                            ? data.ODDIntensity["@value"]
                            : ""
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ODDIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="ODDDuration"
                        label="Duration:"
                        error={errors.ODDDuration}
                        value={
                          data.ODDDuration !== undefined
                            ? data.ODDDuration["@value"]
                            : ""
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ODDDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="OCD"
                    label="Symptoms of Obsessive-Compulsive Disorder (OCD)"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.OCD !== undefined && data.OCD["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handleOCDChange}
                    disabled={formDisabled}
                  />
                  {data.OCD !== undefined && data.OCD["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="OCDPresence"
                              label="Presence of obsessions, compulsions, or both"
                              divClassName="ms-3"
                              checked={
                                data.OCDPresence !== undefined &&
                                data.OCDPresence["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="Obsessions"
                              label="Obsessions (recurrent and persistent thoughts, urges, or images)"
                              divClassName="ms-3"
                              checked={
                                data.Obsessions !== undefined &&
                                data.Obsessions["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="TimeConsuming"
                              label="Obsessions or compulsions are time consuming and cause distress/impairment"
                              divClassName="ms-3"
                              checked={
                                data.TimeConsuming !== undefined &&
                                data.TimeConsuming["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="Compulsions"
                              label="Compulsions/repetitive behaviors (hand washing, ordering, checking,) or mental acts (praying, counting, repeating words silently)"
                              divClassName="ms-3"
                              checked={
                                data.Compulsions !== undefined &&
                                data.Compulsions["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="ReduceAnxiety"
                              label="Behaviors and mental acts done to prevent or reduce anxiety or distress"
                              divClassName="ms-3"
                              checked={
                                data.ReduceAnxiety !== undefined &&
                                data.ReduceAnxiety["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="SuppressUrges"
                              label="Attempts to ignore or suppress thoughts, urges or images with other thought or action (compulsion)"
                              divClassName="ms-3"
                              checked={
                                data.SuppressUrges !== undefined &&
                                data.SuppressUrges["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="OCDOther"
                              label="Other:"
                              divClassName="ms-3"
                              checked={
                                data.OCDOther !== undefined &&
                                data.OCDOther["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.OCDOther !== undefined &&
                            data.OCDOther["@value"] === "true" ? (
                              <FormInput
                                name="OCDOtherDetails"
                                error={errors.OCDOtherDetails}
                                value={
                                  data.OCDOtherDetails !== undefined
                                    ? data.OCDOtherDetails["@value"]
                                    : ""
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("OCDOtherDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.OCD !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.OCD}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <FormInput
                        name="OCDFrequency"
                        label="Frequency:"
                        error={errors.OCDFrequency}
                        value={
                          data.OCDFrequency !== undefined
                            ? data.OCDFrequency["@value"]
                            : ""
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OCDFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="OCDIntensity"
                        label="Intensity:"
                        error={errors.OCDIntensity}
                        value={
                          data.OCDIntensity !== undefined
                            ? data.OCDIntensity["@value"]
                            : ""
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OCDIntensity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="OCDDuration"
                        label="Duration:"
                        error={errors.OCDDuration}
                        value={
                          data.OCDDuration !== undefined
                            ? data.OCDDuration["@value"]
                            : ""
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("OCDDuration")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="PanicDisorder"
                    label="Symptoms of Panic Disorder"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.PanicDisorder !== undefined &&
                      data.PanicDisorder["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handlePanicDisorderChange}
                    disabled={formDisabled}
                  />
                  {data.PanicDisorder !== undefined &&
                    data.PanicDisorder["@value"] === "true" && (
                      <>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="PoundingHeart"
                                label="Palpitations, pounding heart, accelerated heart rate"
                                divClassName="ms-3"
                                checked={
                                  data.PoundingHeart !== undefined &&
                                  data.PoundingHeart["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="PanicDisorderSweating"
                                label="Sweating"
                                divClassName="ms-3"
                                checked={
                                  data.PanicDisorderSweating !== undefined &&
                                  data.PanicDisorderSweating["@value"] ===
                                    "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Trembling"
                                label="Trembling/shaking"
                                divClassName="ms-3"
                                checked={
                                  data.Trembling !== undefined &&
                                  data.Trembling["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="ShortnessOfBreath"
                                label="Shortness of breath"
                                divClassName="ms-3"
                                checked={
                                  data.ShortnessOfBreath !== undefined &&
                                  data.ShortnessOfBreath["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Choking"
                                label="Feelings of choking"
                                divClassName="ms-3"
                                checked={
                                  data.Choking !== undefined &&
                                  data.Choking["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="ChestPain"
                                label="Chest pain/discomfort"
                                divClassName="ms-3"
                                checked={
                                  data.ChestPain !== undefined &&
                                  data.ChestPain["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="PanicDisorderNausea"
                                label="Nausea/abdominal discomfort"
                                divClassName="ms-3"
                                checked={
                                  data.PanicDisorderNausea !== undefined &&
                                  data.PanicDisorderNausea["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Dizzy"
                                label="Dizzy/faint"
                                divClassName="ms-3"
                                checked={
                                  data.Dizzy !== undefined &&
                                  data.Dizzy["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="Chills"
                                label="Chills"
                                divClassName="ms-3"
                                checked={
                                  data.Chills !== undefined &&
                                  data.Chills["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="Derealization"
                                label="Derealization or depersonalization"
                                divClassName="ms-3"
                                checked={
                                  data.Derealization !== undefined &&
                                  data.Derealization["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="FearOfCrazy"
                                label="Fear of losing control/going crazy"
                                divClassName="ms-3"
                                checked={
                                  data.FearOfCrazy !== undefined &&
                                  data.FearOfCrazy["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="FearOfDying"
                                label="Fear of dying"
                                divClassName="ms-3"
                                checked={
                                  data.FearOfDying !== undefined &&
                                  data.FearOfDying["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-4">
                              <FormCheckbox
                                name="PanicDisorderOther"
                                label="Other:"
                                divClassName="ms-3"
                                checked={
                                  data.PanicDisorderOther !== undefined &&
                                  data.PanicDisorderOther["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                              {data.PanicDisorderOther !== undefined &&
                              data.PanicDisorderOther["@value"] === "true" ? (
                                <FormInput
                                  name="PanicDisorderOtherDetails"
                                  error={errors.PanicDisorderOtherDetails}
                                  value={
                                    data.PanicDisorderOtherDetails !== undefined
                                      ? data.PanicDisorderOtherDetails["@value"]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare(
                                    "PanicDisorderOtherDetails"
                                  )}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {errors.PanicDisorder !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.PanicDisorder}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <FormInput
                          name="PanicDisorderFrequency"
                          label="Frequency:"
                          error={errors.PanicDisorderFrequency}
                          value={
                            data.PanicDisorderFrequency !== undefined
                              ? data.PanicDisorderFrequency["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("PanicDisorderFrequency")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="PanicDisorderIntensity"
                          label="Intensity:"
                          error={errors.PanicDisorderIntensity}
                          value={
                            data.PanicDisorderIntensity !== undefined
                              ? data.PanicDisorderIntensity["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("PanicDisorderIntensity")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="PanicDisorderDuration"
                          label="Duration:"
                          error={errors.PanicDisorderDuration}
                          value={
                            data.PanicDisorderDuration !== undefined
                              ? data.PanicDisorderDuration["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("PanicDisorderDuration")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="Phobias"
                    label="Symptoms of Phobias"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.Phobias !== undefined &&
                      data.Phobias["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handlePhobiasChange}
                    disabled={formDisabled}
                  />
                  {data.Phobias !== undefined &&
                    data.Phobias["@value"] === "true" && (
                      <>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <FormCheckbox
                                name="PhobiasSpecificFear"
                                label="Marked fear/anxiety about a specific object or situation"
                                divClassName="ms-3"
                                checked={
                                  data.PhobiasSpecificFear !== undefined &&
                                  data.PhobiasSpecificFear["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="ImmediateFear"
                                label="Object or situation provokes immediate fear or anxiety"
                                divClassName="ms-3"
                                checked={
                                  data.ImmediateFear !== undefined &&
                                  data.ImmediateFear["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <FormCheckbox
                                name="PhobiasAvoidance"
                                label="Avoids objective/situation"
                                divClassName="ms-3"
                                checked={
                                  data.PhobiasAvoidance !== undefined &&
                                  data.PhobiasAvoidance["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={handleChange}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-4">
                              <FormCheckbox
                                name="FearExaggerated"
                                label="Fear and anxiety is out of proportion"
                                divClassName="ms-3"
                                checked={
                                  data.FearExaggerated !== undefined &&
                                  data.FearExaggerated["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col-4">
                              <FormCheckbox
                                name="PhobiasOther"
                                label="Other:"
                                divClassName="ms-3"
                                checked={
                                  data.PhobiasOther !== undefined &&
                                  data.PhobiasOther["@value"] === "true"
                                }
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                              {data.PhobiasOther !== undefined &&
                              data.PhobiasOther["@value"] === "true" ? (
                                <FormInput
                                  name="PhobiasOtherDetails"
                                  error={errors.PhobiasOtherDetails}
                                  value={
                                    data.PhobiasOtherDetails !== undefined
                                      ? data.PhobiasOtherDetails["@value"]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare("PhobiasOtherDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {errors.Phobias !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.Phobias}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <FormInput
                          name="PhobiasFrequency"
                          label="Frequency:"
                          error={errors.PhobiasFrequency}
                          value={
                            data.PhobiasFrequency !== undefined
                              ? data.PhobiasFrequency["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("PhobiasFrequency")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="PhobiasIntensity"
                          label="Intensity:"
                          error={errors.PhobiasIntensity}
                          value={
                            data.PhobiasIntensity !== undefined
                              ? data.PhobiasIntensity["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("PhobiasIntensity")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="PhobiasDuration"
                          label="Duration:"
                          error={errors.PhobiasDuration}
                          value={
                            data.PhobiasDuration !== undefined
                              ? data.PhobiasDuration["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("PhobiasDuration")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        Diagnosis to consider: Specific Phobia, Social Anxiety
                        Disorder (Social Phobia), Agoraphobia
                      </>
                    )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="DI"
                    label="Developmental Impairments"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.DI !== undefined && data.DI["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handleDIChange}
                    disabled={formDisabled}
                  />
                  {data.DI !== undefined && data.DI["@value"] === "true" && (
                    <>
                      <div className="container">
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="DILowIQ"
                              label="Low intelligence/IQ"
                              divClassName="ms-3"
                              checked={
                                data.DILowIQ !== undefined &&
                                data.DILowIQ["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DISensoryImpairments"
                              label="Sensory impairments"
                              divClassName="ms-3"
                              checked={
                                data.DISensoryImpairments !== undefined &&
                                data.DISensoryImpairments["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DISpeechDelays"
                              label="Language/speech delays"
                              divClassName="ms-3"
                              checked={
                                data.DISpeechDelays !== undefined &&
                                data.DISpeechDelays["@value"] === "true"
                              }
                              commentsHandler={handleComments}
                              onChange={handleChange}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col">
                            <FormCheckbox
                              name="DIMotorCoordinationDelays"
                              label="Motor coordination delays"
                              divClassName="ms-3"
                              checked={
                                data.DIMotorCoordinationDelays !== undefined &&
                                data.DIMotorCoordinationDelays["@value"] ===
                                  "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DILearningDisorder"
                              label="Specific learning disorder"
                              divClassName="ms-3"
                              checked={
                                data.DILearningDisorder !== undefined &&
                                data.DILearningDisorder["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="col">
                            <FormCheckbox
                              name="DISpatialDelays"
                              label="Visio-spatial skill delays"
                              divClassName="ms-3"
                              checked={
                                data.DISpatialDelays !== undefined &&
                                data.DISpatialDelays["@value"] === "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-4">
                            <FormCheckbox
                              name="DevelopmentalImpairmentsOther"
                              label="Other:"
                              divClassName="ms-3"
                              checked={
                                data.DevelopmentalImpairmentsOther !==
                                  undefined &&
                                data.DevelopmentalImpairmentsOther["@value"] ===
                                  "true"
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                            {data.DevelopmentalImpairmentsOther["@value"] ===
                            "true" ? (
                              <FormInput
                                name="DevelopmentalImpairmentsOtherDetails"
                                error={
                                  errors.DevelopmentalImpairmentsOtherDetails
                                }
                                value={
                                  data.DevelopmentalImpairmentsOtherDetails !==
                                    undefined &&
                                  data.DevelopmentalImpairmentsOtherDetails[
                                    "@value"
                                  ]
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare(
                                  "DevelopmentalImpairmentsOtherDetails"
                                )}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {errors.DI !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.DI}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {(data.DILowIQ !== undefined &&
                        data.DILowIQ["@value"] === "true") ||
                      (data.DISensoryImpairments !== undefined &&
                        data.DISensoryImpairments["@value"] === "true") ||
                      (data.DISpeechDelays !== undefined &&
                        data.DISpeechDelays["@value"] === "true") ||
                      (data.DIMotorCoordinationDelays !== undefined &&
                        data.DIMotorCoordinationDelays["@value"] === "true") ||
                      (data.DILearningDisorder !== undefined &&
                        data.DILearningDisorder["@value"] === "true") ||
                      (data.DISpatialDelays !== undefined &&
                        data.DISpatialDelays["@value"] === "true") ? (
                        <FormInput
                          name="DevelopmentalImpairmentsDetails"
                          label="Please describe:"
                          error={errors.DevelopmentalImpairmentsDetails}
                          value={
                            data.DevelopmentalImpairmentsDetails !== undefined
                              ? data.DevelopmentalImpairmentsDetails["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("DevelopmentalImpairmentsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </>
                  )}
                </div>
                <div className="border rounded mb-2 px-2 pt-2">
                  <FormCheckbox
                    name="OtherSymptoms"
                    label="Other Symptoms"
                    divClassName="ms-3 fw-bold"
                    checked={
                      data.OtherSymptoms !== undefined &&
                      data.OtherSymptoms["@value"] === "true"
                    }
                    commentsHandler={handleComments}
                    onChange={handleOtherSymptomsChange}
                    disabled={formDisabled}
                  />
                  {data.OtherSymptoms !== undefined &&
                    data.OtherSymptoms["@value"] === "true" && (
                      <>
                        <FormInput
                          name="OtherSymptomsDetails"
                          label="Please describe:"
                          error={errors.OtherSymptomsDetails}
                          value={
                            data.OtherSymptomsDetails !== undefined
                              ? data.OtherSymptomsDetails["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherSymptomsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="OtherSymptomsFrequency"
                          label="Frequency:"
                          error={errors.OtherSymptomsFrequency}
                          value={
                            data.OtherSymptomsFrequency !== undefined
                              ? data.OtherSymptomsFrequency["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherSymptomsFrequency")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="OtherSymptomsIntensity"
                          label="Intensity:"
                          error={errors.OtherSymptomsIntensity}
                          value={
                            data.OtherSymptomsIntensity !== undefined
                              ? data.OtherSymptomsIntensity["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherSymptomsIntensity")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <FormInput
                          name="OtherSymptomsDuration"
                          label="Duration:"
                          error={errors.OtherSymptomsDuration}
                          value={
                            data.OtherSymptomsDuration !== undefined
                              ? data.OtherSymptomsDuration["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherSymptomsDuration")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="ClinicalNarrative"
                expandStatus={expandStatus}
                className="mt-2"
                title="Clinical Narrative Summary"
              >
                Include etiology of presenting problem and maintenance of
                problem; mental health history; alcohol/drug history; severity
                of problem; where problem occurs (functioning at home, at work,
                in community); onset of problem (acute vs. chronic); recipient
                motivation for treatment; whether problem is known to be
                responsive to treatment.
                <FormTextArea
                  name="ClinicalNarrativeSummary"
                  error={errors.ClinicalNarrativeSummary}
                  value={data.ClinicalNarrativeSummary["@value"]}
                  onChange={handleChange}
                  identical={compare("ClinicalNarrativeSummary")}
                  commentsHandler={handleComments}
                  disabled
                />
                <FormTextArea
                  name="ClinicalNarrativeSummaryComments"
                  label="Clinical Narrative Additional Comments"
                  error={errors.ClinicalNarrativeSummaryComments}
                  value={data.ClinicalNarrativeSummaryComments["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("ClinicalNarrativeSummaryComments")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  rows="5"
                  placeholder="Include information on who the ongoing clinician will be, the date of their care planning session, and any other comments necessary for the clinical narrative summary..."
                />
              </ExpandDiv>
              <ExpandDiv
                id="Diagnosis"
                expandStatus={expandStatus}
                className="mt-2"
                title="Diagnosis"
              >
                <FieldTable
                  name="DiagnosisDetails"
                  commentsHandler={handleComments}
                >
                  <br />
                  <div className="container border-top border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col-3 py-2 border-end">ICD-10 Code</div>
                      <div className="col py-2">Diagnosis</div>
                    </div>
                    {data.DiagnosisDetails["@value"] &&
                      JSON.parse(data.DiagnosisDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col-3 py-2 border-end d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(index, "DiagnosisDetails");
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "DiagnosisDetails"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  <FormInput
                                    name="Code"
                                    value={item.Code}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "DiagnosisDetails");
                                    }}
                                  ></i>
                                  {item.Code}
                                </>
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormTextArea
                                  name="Diagnosis"
                                  value={item.Diagnosis}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "DiagnosisDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Diagnosis
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled && (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("DiagnosisDetails", {
                          IsEditable: true,
                        });
                      }}
                    >
                      Add Diagnosis
                    </button>
                  )}
                  {errors.DiagnosisDetails && (
                    <div className="alert alert-danger">
                      {errors.DiagnosisDetails}
                    </div>
                  )}
                </FieldTable>
              </ExpandDiv>
              <ExpandDiv
                id="TreatmentRecommendations"
                expandStatus={expandStatus}
                className="mt-2"
                title="Treatment Recommendations/Needs"
              >
                <b>Assessed Needs</b>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="SkillsTraining"
                        label="Specific Skills Training"
                        divClassName="ms-3"
                        checked={data.SkillsTraining["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="CommunitySupport"
                        label="Community/Environmental Support"
                        divClassName="ms-3"
                        checked={data.CommunitySupport["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="MedicalDentalVision"
                        label="Medical/Dental/Vision"
                        divClassName="ms-3"
                        checked={data.MedicalDentalVision["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="SymptomManagementSkills"
                        label="Symptom Management Skills"
                        divClassName="ms-3"
                        checked={
                          data.SymptomManagementSkills["@value"] === "true"
                        }
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="Mentoring"
                        label="Mentoring/Coaching"
                        divClassName="ms-3"
                        checked={data.Mentoring["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="HealthyHabits"
                        label="Nutrition/Healthy Habits"
                        divClassName="ms-3"
                        checked={data.HealthyHabits["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="SocialSkills"
                        label="Development of Social/Interpersonal Skills"
                        divClassName="ms-3"
                        checked={data.SocialSkills["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="FamilySupport"
                        label="Family Support"
                        divClassName="ms-3"
                        checked={data.FamilySupport["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="EducationSchool"
                        label="Education/School"
                        divClassName="ms-3"
                        checked={data.EducationSchool["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="DecisionMaking"
                        label="Decision-Making"
                        divClassName="ms-3"
                        checked={data.DecisionMaking["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="FosterFamilySupport"
                        label="Foster Family Support"
                        divClassName="ms-3"
                        checked={data.FosterFamilySupport["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="Vocational"
                        label="Vocational"
                        divClassName="ms-3"
                        checked={data.Vocational["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="FinancialSupport"
                        label="Financial Support"
                        divClassName="ms-3"
                        checked={data.FinancialSupport["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="Respite"
                        label="Respite"
                        divClassName="ms-3"
                        checked={data.Respite["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="Occupational"
                        label="Occupational"
                        divClassName="ms-3"
                        checked={data.Occupational["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="ProsocialActivities"
                        label="Prosocial Activities"
                        divClassName="ms-3"
                        checked={data.ProsocialActivities["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="MedicationManagementNeed"
                        label="Medication Management"
                        divClassName="ms-3"
                        checked={
                          data.MedicationManagementNeed["@value"] === "true"
                        }
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="CaseManagement"
                        label="Case Management"
                        divClassName="ms-3"
                        checked={data.CaseManagement["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                {errors.AssessedNeeds !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.AssessedNeeds}
                      </div>
                    </div>
                  </div>
                ) : null}
                <br />
                <b>Treatment Recommendations</b>
                <FieldTable
                  name="TreatmentRecommendations"
                  commentsHandler={handleComments}
                >
                  <div className="container border-top border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col py-2 border-end">Recommendation</div>
                      <div className="col py-2 border-end">Status</div>
                      <div className="col py-2">Reason</div>
                    </div>
                    {data.TreatmentRecommendations["@value"] &&
                      JSON.parse(data.TreatmentRecommendations["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col py-2 border-end d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  <FormSelect
                                    name="Recommendation"
                                    value={item.Recommendation}
                                    options={treatmentRecommendationOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  {item.Recommendation}
                                </>
                              )}
                            </div>
                            <div className="col py-2 border-end">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <div className="container">
                                  <div className="row">
                                    <div className="col">
                                      <FormCheckbox
                                        name={`Deferred-${index}`}
                                        label="SAFY will be providing"
                                        checked={item.Deferred === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "TreatmentRecommendations"
                                          );
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <FormCheckbox
                                        name={`Referred-${index}`}
                                        label="SAFY will be referring to another agency/individual"
                                        checked={item.Referred === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "TreatmentRecommendations"
                                          );
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <FormCheckbox
                                        name={`Declined-${index}`}
                                        label="Declined by Recipient"
                                        checked={item.Declined === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "TreatmentRecommendations"
                                          );
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                getTreatmentRecommendationStatus(item)
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormInput
                                  name="Reason"
                                  value={item.Reason}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "TreatmentRecommendations"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Reason
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled && (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("TreatmentRecommendations", {
                          IsEditable: true,
                        });
                      }}
                    >
                      Add Treatment Recommendation
                    </button>
                  )}
                  {errors.TreatmentRecommendations && (
                    <div className="alert alert-danger">
                      {errors.TreatmentRecommendations}
                    </div>
                  )}
                </FieldTable>
                <br />
                <b>Further Evaluation Needs</b>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="MorePsychiatric"
                        label="Psychiatric"
                        divClassName="ms-3"
                        checked={data.MorePsychiatric["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="MorePsychological"
                        label="Psychological"
                        divClassName="ms-3"
                        checked={data.MorePsychological["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="MoreNeurological"
                        label="Neurological"
                        divClassName="ms-3"
                        checked={data.MoreNeurological["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="MoreEducational"
                        label="Educational"
                        divClassName="ms-3"
                        checked={data.MoreEducational["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="MoreMedical"
                        label="Medical"
                        divClassName="ms-3"
                        checked={data.MoreMedical["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="MoreDental"
                        label="Dental"
                        divClassName="ms-3"
                        checked={data.MoreDental["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="MoreSubstanceUse"
                        label="Alcohol/Drug"
                        divClassName="ms-3"
                        checked={data.MoreSubstanceUse["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="MoreVocational"
                        label="Vocational"
                        divClassName="ms-3"
                        checked={data.MoreVocational["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="MoreOccupational"
                        label="Occupational"
                        divClassName="ms-3"
                        checked={data.MoreOccupational["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="MoreVisualAuditory"
                        label="Visual/Auditory"
                        divClassName="ms-3"
                        checked={data.MoreVisualAuditory["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="MoreNutritional"
                        label="Nutritional"
                        divClassName="ms-3"
                        checked={data.MoreNutritional["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormCheckbox
                        name="MoreOther"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.MoreOther["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-9"></div>
                    <div className="col">
                      {data.MoreOther["@value"] === "true" ? (
                        <FormInput
                          name="MoreOtherDetails"
                          error={errors.MoreOtherDetails}
                          value={data.MoreOtherDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("MoreOtherDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                {errors.EvaluationNeeds !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.EvaluationNeeds}
                      </div>
                    </div>
                  </div>
                ) : null}
                <br />
                <FormRadioButton
                  name="DiagnosisEducation"
                  label="Education regarding diagnosis provided to Recipient/Family:"
                  error={errors.DiagnosisEducation}
                  value={data.DiagnosisEducation["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormRadioButton
                  name="RecommendationEducation"
                  label="Education regarding recommendations provided to Recipient/Family:"
                  error={errors.RecommendationEducation}
                  value={data.RecommendationEducation["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormRadioButton
                  name="RecipientAgrees"
                  label="Recipient in agreement with recommendations:"
                  error={errors.RecipientAgrees}
                  value={data.RecipientAgrees["@value"]}
                  options={yesNoNAAgeOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormRadioButton
                  name="FamilyAgrees"
                  label="Family or Guardian in agreement with recommendations:"
                  error={errors.FamilyAgrees}
                  value={data.FamilyAgrees["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              {showNarrative === "Show" ? (
                <div className="clinicalNarrative bg-white border p-2">
                  <label>
                    <b>Clinical Narrative Summary:</b>
                  </label>
                  <textarea
                    value={data.ClinicalNarrativeSummary["@value"]}
                    rows="5"
                    disabled
                  />
                </div>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default ALPSD;
